import React, { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Accordion, Dropdown, Form, InputGroup, Button, Modal } from 'react-bootstrap';
import Select, { components } from 'react-select';
import { Plus, Square, CheckSquare, ThumbsUp, X, Trash2, Search, Check, MoreVertical, Pause, Play, Clock, Zap, Award, BadgeCheck, ChevronRight, ChevronLeft, Calendar, Users, Pencil, ListFilter, MessageSquare, Send, Maximize2, Folder, Tag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { addCardComment, create_new_task, create_task_category, delete_task, delete_task_category, ensure_card, formatoDuracion, get_all_members, get_current_cards, list_task_categories, listCardComments, listKrs, listStrategyTeamTasks, load_task_list, reassignTaskAssignee, task_log, update_task, update_task_category, update_task_category_link, update_task_due_date, updateCardDescription, updateCardKr, updateCardStatus } from '../../../utils/talent_services';
import Card_link_2task from './modal/Card_link_2task';
import TacticDueDateRangePicker from './modal/TacticDueDateRangePicker';
import Modal_proyectos from '../../menus/Modal';
import UserAvatar from '../dashboard/UserAvatar';
import Statistics from './Statistics';

function resolveNewTaskLinkForGroup(groupKey, groups, strategyCanvasMeta) {
    const oppGroupId = getOpportunityGroupId(groupKey);
    if (groupKey === 'Sin estrategia') {
        return { linkType: 'none', displayLabel: 'Sin estrategia', trelloCardId: null, opportunityId: null };
    }
    const list = groups[groupKey];
    if (oppGroupId != null) {
        return {
            linkType: 'opportunity',
            displayLabel: 'Oportunidades',
            trelloCardId: null,
            opportunityId: String(oppGroupId),
        };
    }
    if (list && list.length > 0) {
        const withCard = list.filter((x) => {
            const cid = x.trello_card_id;
            return cid != null && String(cid).trim() !== '';
        });
        if (withCard.length > 0) {
            const tid = withCard[0].trello_card_id;
            return { linkType: 'trello', displayLabel: groupKey, trelloCardId: tid, opportunityId: null };
        }
    }
    const canvas = strategyCanvasMeta && strategyCanvasMeta[groupKey];
    if (canvas && canvas.trello_card_id != null && String(canvas.trello_card_id).trim() !== '') {
        const tid = String(canvas.trello_card_id).trim();
        if (tid !== '99999999') {
            return { linkType: 'trello', displayLabel: groupKey, trelloCardId: tid, opportunityId: null };
        }
    }
    return { linkType: 'none', displayLabel: groupKey, trelloCardId: null, opportunityId: null };
}

/** trello_card_id real de la estrategia (excl. placeholder 99999999). */
function getTrelloCardIdForStrategyGroup(groupKey, groups, pinnedNewTaskCtx, strategyCanvasMeta) {
    if (groupKey === 'Sin estrategia' || getOpportunityGroupId(groupKey) != null) return null;
    const list = groups[groupKey] || [];
    const row = list.find((x) => {
        const cid = x.trello_card_id;
        return cid != null && String(cid).trim() !== '' && cid !== '99999999' && cid !== 99999999;
    });
    if (row) return String(row.trello_card_id).trim();
    const canvas = strategyCanvasMeta && strategyCanvasMeta[groupKey];
    if (canvas && canvas.trello_card_id != null && String(canvas.trello_card_id).trim() !== '') {
        const cid = String(canvas.trello_card_id).trim();
        if (cid !== '99999999') return cid;
    }
    if (
        pinnedNewTaskCtx &&
        pinnedNewTaskCtx.strategyGroupKey === groupKey &&
        pinnedNewTaskCtx.linkType === 'trello' &&
        pinnedNewTaskCtx.trelloCardId != null &&
        String(pinnedNewTaskCtx.trelloCardId).trim() !== ''
    ) {
        const tid = String(pinnedNewTaskCtx.trelloCardId).trim();
        if (tid !== '99999999') return tid;
    }
    return null;
}

/** PK talent_db.cards para actualizar estado sin depender del string trello_card_id. */
function getCardDbIdForStrategyGroup(groupKey, groups, pinnedNewTaskCtx, strategyCanvasMeta) {
    if (groupKey === 'Sin estrategia' || getOpportunityGroupId(groupKey) != null) return null;
    const list = groups[groupKey] || [];
    const row = list.find((x) => x.card_id != null && x.card_id !== '' && Number(x.card_id) > 0);
    if (row) return Number(row.card_id);
    const canvas = strategyCanvasMeta && strategyCanvasMeta[groupKey];
    const fromCanvas = canvas && canvas.card_db_id != null && Number(canvas.card_db_id) > 0 ? Number(canvas.card_db_id) : null;
    if (fromCanvas) return fromCanvas;
    return null;
}

/** Tarea vinculada a fila talent_db.cards (card_id) o, en datos heredados, solo trello_card_id. */
function taskHasLinkedStrategyCard(task) {
    if (!task) return false;
    const cid = task.card_id;
    if (cid != null && cid !== '' && Number(cid) > 0) return true;
    const tid = task.trello_card_id;
    return tid != null && String(tid).trim() !== '' && tid !== '99999999' && tid !== 99999999;
}

/** Clave de agrupación por estrategia / oportunidad (misma lógica en filtro y mosaico). */
function getTaskStrategyGroupKey(task, strategyCanvasMeta = null) {
    const hasOpp = task.opportunity_id != null && task.opportunity_id !== '' && Number(task.opportunity_id) > 0;
    const hasCard = taskHasLinkedStrategyCard(task);
    if (hasCard) {
        const tid =
            task.trello_card_id != null && String(task.trello_card_id).trim() !== ''
                ? String(task.trello_card_id).trim()
                : '';
        const cid =
            task.card_id != null && task.card_id !== '' && Number(task.card_id) > 0
                ? Number(task.card_id)
                : null;
        if (strategyCanvasMeta && typeof strategyCanvasMeta === 'object') {
            for (const [gk, meta] of Object.entries(strategyCanvasMeta)) {
                if (!meta || typeof meta !== 'object') continue;
                const metaTid =
                    meta.trello_card_id != null && String(meta.trello_card_id).trim() !== ''
                        ? String(meta.trello_card_id).trim()
                        : '';
                const metaCid =
                    meta.card_db_id != null && Number(meta.card_db_id) > 0 ? Number(meta.card_db_id) : null;
                if (tid !== '' && metaTid !== '' && tid === metaTid) return String(gk).trim();
                if (cid != null && metaCid != null && cid === metaCid) return String(gk).trim();
            }
        }
        return task.strategy || task.trello_card_id || 'Sin nombre';
    }
    if (hasOpp) return buildOpportunityGroupKey(task.opportunity_id);
    return 'Sin estrategia';
}

function getOwnTasksForStrategyGroup(groupKey, groups, strategyCanvasMeta) {
    const direct = groups[groupKey] || [];
    if (direct.length > 0) return direct;
    const canvas = strategyCanvasMeta && strategyCanvasMeta[groupKey];
    if (!canvas || typeof canvas !== 'object') return direct;
    const tid =
        canvas.trello_card_id != null && String(canvas.trello_card_id).trim() !== ''
            ? String(canvas.trello_card_id).trim()
            : '';
    const cid =
        canvas.card_db_id != null && Number(canvas.card_db_id) > 0 ? Number(canvas.card_db_id) : null;
    if (tid === '' && cid == null) return direct;
    const matched = [];
    for (const list of Object.values(groups)) {
        if (!Array.isArray(list)) continue;
        for (const t of list) {
            const tTid =
                t.trello_card_id != null && String(t.trello_card_id).trim() !== ''
                    ? String(t.trello_card_id).trim()
                    : '';
            const tCid =
                t.card_id != null && t.card_id !== '' && Number(t.card_id) > 0 ? Number(t.card_id) : null;
            if (cid != null && tCid != null && tCid === cid) {
                matched.push(t);
            } else if (tid !== '' && tTid !== '' && tTid === tid) {
                matched.push(t);
            }
        }
    }
    return matched.length > 0 ? matched : direct;
}

const PRIVATE_SECTION_TITLE = 'Privadas (solo tu puedes verlas)';

function isPrivateStrategyGroup(groupKey, groups, strategyCanvasMeta) {
    if (groupKey == null || groupKey === 'Sin estrategia' || getOpportunityGroupId(groupKey) != null) {
        return false;
    }
    const canvas = strategyCanvasMeta && strategyCanvasMeta[groupKey];
    if (canvas?.private === true) return true;
    const list = groups[groupKey] || [];
    return list.some(
        (t) =>
            t.private === true ||
            t.private === 1 ||
            Number(t.card_private) === 1
    );
}

function isCreatorOwnedCanvas(meta, viewerUsrId) {
    if (meta == null || typeof meta !== 'object') return false;
    const viewerId = viewerUsrId != null && viewerUsrId !== '' ? Number(viewerUsrId) : null;
    if (viewerId == null || !Number.isFinite(viewerId)) return false;
    return meta.creator_usr_id != null && Number(meta.creator_usr_id) === viewerId;
}

function isCreatorOwnedPrivateCanvas(meta, viewerUsrId) {
    return isCreatorOwnedCanvas(meta, viewerUsrId) && meta.private === true;
}

/** talent_db.cards.status → badge en pie de estrategia (todo clickeable → popup). */
function getStrategyGroupCardStatusBadge(groupKey, groups, pinnedNewTaskCtx, strategyCanvasMeta) {
    const tid = getTrelloCardIdForStrategyGroup(groupKey, groups, pinnedNewTaskCtx, strategyCanvasMeta);
    if (!tid) return null;
    const list = groups[groupKey] || [];
    const withCard = list.find((x) => String(x.trello_card_id ?? '').trim() === tid);
    let s;
    if (withCard) {
        s = Number(withCard.card_status);
    } else {
        const canvas = strategyCanvasMeta && strategyCanvasMeta[groupKey];
        if (!canvas) return null;
        s = Number(canvas.card_status);
    }
    const pickTitle =
        'Cambiar estado de la tarjeta (Ninguno, Alineación, Ejecución, Terminado o Archivado)';
    if (s === 1) {
        return { label: 'Alineación', title: pickTitle, variant: 'alineacion' };
    }
    if (s === 2) {
        return { label: 'Ejecución', title: pickTitle, variant: 'ejecucion' };
    }
    if (s === 3) {
        return { label: 'Terminado', title: pickTitle, variant: 'terminado' };
    }
    if (s === 4) {
        return { label: 'Archivado', title: pickTitle, variant: 'archivado' };
    }
    if (s === 0 || !Number.isFinite(s)) {
        return { label: 'estado', title: pickTitle, variant: 'unset' };
    }
    return { label: 'estado', title: pickTitle, variant: 'unset' };
}

/** 0 ninguno, 1–4 estados de talent_db.cards; null si no hay tarjeta (p. ej. Sin estrategia / Oportunidades). */
function getStrategyGroupCardStatusCode(groupKey, groups, pinnedNewTaskCtx, strategyCanvasMeta) {
    const tid = getTrelloCardIdForStrategyGroup(groupKey, groups, pinnedNewTaskCtx, strategyCanvasMeta);
    if (!tid) return null;
    const list = groups[groupKey] || [];
    const withCard = list.find((x) => String(x.trello_card_id ?? '').trim() === tid);
    let s;
    if (withCard) {
        s = Number(withCard.card_status);
    } else {
        const canvas = strategyCanvasMeta && strategyCanvasMeta[groupKey];
        if (!canvas) return null;
        s = Number(canvas.card_status);
    }
    if (!Number.isFinite(s) || s < 0 || s > 4) return 0;
    return s;
}

const CARD_STATUS_FILTER_OPTIONS = [
    { value: 0, label: 'Sin definir' },
    { value: 1, label: 'Alineación' },
    { value: 2, label: 'Ejecución' },
    { value: 3, label: 'Terminado' },
    { value: 4, label: 'Archivado' },
];

const DUE_DATE_FILTER_MODES = new Set(['none', 'range', 'no_due_date']);

const ALLOWED_CARD_STATUS_FILTER_VALUES = new Set(CARD_STATUS_FILTER_OPTIONS.map((o) => o.value));

function tacticFollowupPrefsStorageKey(usrId) {
    const id = usrId != null && usrId !== '' ? String(usrId) : 'anon';
    return `tacticfollowup_ui_v1_${id}`;
}

function readTacticFollowupPrefs(usrId) {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(tacticFollowupPrefsStorageKey(usrId));
        if (!raw) return null;
        const p = JSON.parse(raw);
        return p && typeof p === 'object' ? p : null;
    } catch {
        return null;
    }
}

function parseStoredCardStatusFilter(raw) {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((x) => Number(x))
        .filter((x) => !Number.isNaN(x) && ALLOWED_CARD_STATUS_FILTER_VALUES.has(x))
        .sort((a, b) => a - b);
}

function parseStoredTeamAssigneeFilter(raw) {
    if (!Array.isArray(raw)) return [];
    const nums = raw.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0);
    return [...new Set(nums)].sort((a, b) => a - b);
}

function resolveInitialTeamAssigneeFilter(prefs, usrId) {
    if (prefs && Object.prototype.hasOwnProperty.call(prefs, 'teamAssigneeFilterIds')) {
        return parseStoredTeamAssigneeFilter(prefs.teamAssigneeFilterIds);
    }
    if (prefs && prefs.onlyPersonalStrategies === false) {
        return [];
    }
    const uid = usrId != null ? Number(usrId) : NaN;
    return Number.isFinite(uid) && uid > 0 ? [uid] : [];
}

function parseStoredDueDateFilterMode(raw) {
    const v = raw == null ? 'none' : String(raw);
    return DUE_DATE_FILTER_MODES.has(v) ? v : 'none';
}

function toLocalMidnight(dateLike) {
    const d = dateLike instanceof Date ? new Date(dateLike) : new Date(dateLike);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
}

function parseStoredDueDateFilterRange(raw) {
    if (!raw || typeof raw !== 'object') {
        return { startDate: null, endDate: null, key: 'selection' };
    }
    const startDate = raw.startDate ? toLocalMidnight(raw.startDate) : null;
    const endDate = raw.endDate ? toLocalMidnight(raw.endDate) : null;
    return {
        startDate: startDate && !Number.isNaN(startDate.getTime()) ? startDate : null,
        endDate: endDate && !Number.isNaN(endDate.getTime()) ? endDate : null,
        key: 'selection',
    };
}

function parseDueDateOnlyString(raw) {
    if (raw == null || raw === '') return null;
    const s = String(raw).trim();
    if (
        s === '' ||
        s === 'null' ||
        s === 'undefined' ||
        /^0000-00-00/i.test(s) ||
        /^0000-/i.test(s)
    ) {
        return null;
    }
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const da = Number(m[3]);
    if (!Number.isFinite(y) || y < 1 || !Number.isFinite(mo) || !Number.isFinite(da)) return null;
    const d = new Date(y, mo - 1, da);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
}

function hasTaskDueDate(task) {
    return parseDueDateOnlyString(task?.due_date) != null;
}

/** null = todos los grupos KR abiertos; array = solo esos sectionId abiertos ([] = todo cerrado). */
function parseKrAccordionOpenIdsFromPrefs(p) {
    if (!p || !Object.prototype.hasOwnProperty.call(p, 'krAccordionOpenIds')) return null;
    const raw = p.krAccordionOpenIds;
    if (raw === null) return null;
    if (!Array.isArray(raw)) return null;
    return raw.filter((x) => typeof x === 'string' && x.length > 0);
}

/** Convierte fila de strategy_task_previews (load_task_list) al shape del mosaico. */
function previewRowToMosaicTask(row) {
    if (!row || row.id == null) return null;
    const opp = row.opportunity_id;
    const hasOpp = opp != null && opp !== '' && Number(opp) > 0;
    const cidRaw = row.card_id;
    const cid = cidRaw != null && cidRaw !== '' && Number(cidRaw) > 0 ? Number(cidRaw) : null;
    const tc = row.trello_card_id;
    return {
        id: row.id,
        text: row.task_name ?? '',
        task_name: row.task_name ?? '',
        completed: isTaskFlagTrue(row.completed),
        active: isTaskFlagTrue(row.active),
        due_date: row.due_date ?? null,
        category: row.category ?? null,
        category_name: row.category_name ?? null,
        category_color: row.category_color ?? null,
        trello_card_id: tc != null && tc !== undefined ? String(tc).trim() : '',
        card_id: cid,
        opportunity_id: hasOpp ? opp : null,
        Tiempo_Activo_Total_Minutos_Enteros: row.Tiempo_Activo_Total_Minutos_Enteros ?? 0,
        active_session_start: row.active_session_start ?? null,
        durationSyncedAtMs: row.durationSyncedAtMs ?? Date.now(),
        usr_id: row.usr_id != null && row.usr_id !== '' ? Number(row.usr_id) : null,
        email_id: row.email_id ?? '',
    };
}

/** Shape para update_task / modales a partir de tarea del mosaico o del preview. */
function mosaicTaskToUpdateTask(task) {
    if (!task || task.id == null) return null;
    const opp = task.opportunity_id;
    const hasOpp = opp != null && opp !== '' && Number(opp) > 0;
    const cidRaw = task.card_id;
    const cid = cidRaw != null && cidRaw !== '' && Number(cidRaw) > 0 ? Number(cidRaw) : null;
    const tc = task.trello_card_id;
    return {
        id: task.id,
        text: task.text ?? task.task_name ?? '',
        task_name: task.task_name ?? task.text ?? '',
        completed: isTaskFlagTrue(task.completed),
        active: isTaskFlagTrue(task.active),
        trello_card_id: tc != null && tc !== undefined ? String(tc).trim() : '',
        card_id: cid,
        opportunity_id: hasOpp ? opp : null,
        due_date: task.due_date ?? null,
        strategy: task.strategy ?? null,
        category: task.category ?? null,
        category_name: task.category_name ?? null,
        category_color: task.category_color ?? null,
        Tiempo_Activo_Total_Minutos_Enteros: task.Tiempo_Activo_Total_Minutos_Enteros ?? 0,
        active_session_start: task.active_session_start ?? null,
        durationSyncedAtMs: task.durationSyncedAtMs ?? null,
        usr_id: task.usr_id,
        email_id: task.email_id,
        createdAt: task.createdAt,
        completed_at: task.completed_at ?? null,
    };
}

/** Convierte fila de list_strategy_team_tasks al shape que usan modales y update_task. */
function teamPanelRowToTask(row) {
    if (!row || row.id == null) return null;
    const opp = row.opportunity_id;
    const hasOpp = opp != null && opp !== '' && Number(opp) > 0;
    const tc = row.trello_card_id;
    const cidRaw = row.card_id;
    const cid = cidRaw != null && cidRaw !== '' && Number(cidRaw) > 0 ? Number(cidRaw) : null;
    return {
        id: row.id,
        text: row.task_name ?? '',
        task_name: row.task_name ?? '',
        completed: !!row.completed,
        active: !!row.active,
        trello_card_id: tc != null && tc !== undefined ? tc : '',
        card_id: cid,
        opportunity_id: hasOpp ? opp : null,
        due_date: row.due_date ?? null,
        strategy: null,
        category: row.category ?? null,
        category_name: row.category_name ?? null,
        category_color: row.category_color ?? null,
        Tiempo_Activo_Total_Minutos_Enteros: row.Tiempo_Activo_Total_Minutos_Enteros ?? 0,
        active_session_start: row.active_session_start ?? null,
        durationSyncedAtMs: row.durationSyncedAtMs ?? null,
        usr_id: row.usr_id,
        email_id: row.email_id,
        createdAt: row.creation_date,
        completed_at: row.completed_at ?? null,
    };
}

/** Clave alineada con strategy_bar_stats del backend (load_task_list). */
/** Lunes 00:00 local de la semana ISO de creación (ms); -1 si no hay fecha. */
function teamPanelWeekStartMs(creationDateStr) {
    if (creationDateStr == null || creationDateStr === '') return -1;
    const d = new Date(creationDateStr);
    if (Number.isNaN(d.getTime())) return -1;
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    return monday.getTime();
}

const TEAM_PANEL_GROUP_BY = {
    creation: 'creation',
    due: 'due',
    completion: 'completion',
};

function teamPanelMissingWeekLabel(groupBy) {
    if (groupBy === TEAM_PANEL_GROUP_BY.due) return 'Sin fecha de vencimiento';
    if (groupBy === TEAM_PANEL_GROUP_BY.completion) return 'Sin fecha de terminación';
    return 'Sin fecha de creación';
}

function formatTeamPanelWeekLabel(weekStartMs, groupBy = TEAM_PANEL_GROUP_BY.creation) {
    if (weekStartMs < 0) return teamPanelMissingWeekLabel(groupBy);
    const start = new Date(weekStartMs);
    const end = new Date(weekStartMs);
    end.setDate(end.getDate() + 6);
    const opts = { day: 'numeric', month: 'short', year: 'numeric' };
    return `Semana: ${start.toLocaleDateString('es-ES', opts)} – ${end.toLocaleDateString('es-ES', opts)}`;
}

/** Fecha ancla (Date local) según modo de agrupación; null si no existe. */
function teamPanelRowAnchorDate(row, groupBy) {
    if (groupBy === TEAM_PANEL_GROUP_BY.due) {
        return parseDueDateOnlyString(row?.due_date);
    }
    if (groupBy === TEAM_PANEL_GROUP_BY.completion) {
        const raw = row?.completed_at;
        if (raw == null || raw === '') return null;
        const parsed = new Date(String(raw));
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const c = row?.creation_date;
    if (c == null || c === '') return null;
    const parsed = new Date(String(c));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Fecha usada para anclar la semana ISO según modo de agrupación (string parseable por Date). */
function teamPanelRowWeekAnchorString(row, groupBy) {
    const date = teamPanelRowAnchorDate(row, groupBy);
    if (!date) return '';
    if (groupBy === TEAM_PANEL_GROUP_BY.due) {
        const y = date.getFullYear();
        const mo = String(date.getMonth() + 1).padStart(2, '0');
        const da = String(date.getDate()).padStart(2, '0');
        return `${y}-${mo}-${da}T12:00:00`;
    }
    return date.toISOString();
}

/** Tier para filtro «Estado» del panel de equipo. */
function getTeamPanelTaskStatusKey(row) {
    const active = !!row.active;
    const completed = !!row.completed;
    if (active && !completed) return 'running';
    if (!completed) return 'pending';
    return 'completed';
}

const TEAM_PANEL_STATUS_FILTER_OPTIONS = [
    { key: 'completed', label: 'Terminada' },
    { key: 'running', label: 'En ejecución' },
    { key: 'pending', label: 'Pendiente' },
];

/** En ejecución primero; luego duración activa descendente; desempate por fecha de creación e id. */
function sortTeamPanelTasksByDurationDesc(rows) {
    return [...rows].sort((a, b) => {
        const aRunning = getTeamPanelTaskStatusKey(a) === 'running';
        const bRunning = getTeamPanelTaskStatusKey(b) === 'running';
        if (aRunning !== bRunning) return aRunning ? -1 : 1;

        const taTime = Number(a.Tiempo_Activo_Total_Minutos_Enteros) || 0;
        const tbTime = Number(b.Tiempo_Activo_Total_Minutos_Enteros) || 0;
        if (tbTime !== taTime) return tbTime - taTime;
        const da = a.creation_date ? new Date(a.creation_date).getTime() : 0;
        const db = b.creation_date ? new Date(b.creation_date).getTime() : 0;
        if (db !== da) return db - da;
        return Number(b.id) - Number(a.id);
    });
}

/** Agrupa filas del panel de equipo por semana ISO según `groupBy`. */
function buildTeamPanelGroupedWeeksFromRows(rows, options = {}) {
    const groupBy = options.groupBy === TEAM_PANEL_GROUP_BY.due || options.groupBy === TEAM_PANEL_GROUP_BY.completion
        ? options.groupBy
        : TEAM_PANEL_GROUP_BY.creation;
    if (!rows || !rows.length) return [];
    const byWeek = new Map();
    for (const row of rows) {
        const anchor = teamPanelRowWeekAnchorString(row, groupBy);
        const wk = teamPanelWeekStartMs(anchor);
        if (!byWeek.has(wk)) byWeek.set(wk, []);
        byWeek.get(wk).push(row);
    }
    const keys = [...byWeek.keys()].sort((a, b) => {
        if (a < 0) return 1;
        if (b < 0) return -1;
        return b - a;
    });
    return keys.map((weekStart) => ({
        weekStart,
        label: formatTeamPanelWeekLabel(weekStart, groupBy),
        tasks: sortTeamPanelTasksByDurationDesc(byWeek.get(weekStart)),
    }));
}

/** Meta de KR desde la primera tarea del grupo (load_task_list → cards → krs). */
function getKrMetaForStrategySection(groupKey, groups, strategyCanvasMeta) {
    const list = groups[groupKey] || [];
    const withKr = list.find(
        (t) => t.kr_id !== null && t.kr_id !== undefined && String(t.kr_id).trim() !== ''
    );
    if (withKr) {
        const n = Number(withKr.kr_id);
        const krId = Number.isFinite(n) ? n : null;
        const name = withKr.kr_name != null && String(withKr.kr_name).trim() !== '' ? String(withKr.kr_name).trim() : null;
        return { krId, krName: name };
    }
    const canvas = strategyCanvasMeta && strategyCanvasMeta[groupKey];
    if (canvas && canvas.kr_id != null && canvas.kr_id !== undefined && String(canvas.kr_id).trim() !== '') {
        const n = Number(canvas.kr_id);
        const krId = Number.isFinite(n) ? n : null;
        const name =
            canvas.kr_name != null && String(canvas.kr_name).trim() !== '' ? String(canvas.kr_name).trim() : null;
        return { krId, krName: name };
    }
    return { krId: null, krName: null };
}

function getKrFooterIdForStrategyGroup(groupKey, groups, strategyCanvasMeta) {
    const { krId } = getKrMetaForStrategySection(groupKey, groups, strategyCanvasMeta);
    return krId;
}

function getStrategyKrColorSuffix(krId, isPrivate = false) {
    if (isPrivate) return 'private';
    const n = Number(krId);
    if (n === 0) return 'kr-0';
    if (n === 1) return 'kr-1';
    if (n === 2) return 'kr-2';
    return null;
}

/** Vista resumida de tarjeta: en ejecución primero; luego pendientes antes que terminadas. */
function isTaskFlagTrue(value) {
    if (value === true || value === 1) return true;
    if (typeof value === 'string') {
        const v = value.trim().toLowerCase();
        return v === '1' || v === 'true';
    }
    return false;
}

/** Minutos mostrados: total del API + delta desde durationSyncedAtMs (o inicio de sesión) si está activa. */
function getLiveActiveMinutes(task, nowMs = Date.now()) {
    const base = Number(task?.Tiempo_Activo_Total_Minutos_Enteros) || 0;
    if (!isTaskFlagTrue(task?.active)) return base;
    let synced = task?.durationSyncedAtMs;
    if (synced == null || !Number.isFinite(Number(synced))) {
        if (task?.active_session_start) {
            const parsed = Date.parse(task.active_session_start);
            synced = Number.isFinite(parsed) ? parsed : null;
        } else {
            synced = null;
        }
    } else {
        synced = Number(synced);
    }
    if (synced == null) return base;
    return base + Math.max(0, Math.floor((nowMs - synced) / 60000));
}

function stampDurationSync(taskOrRow, syncedAtMs = Date.now()) {
    if (!taskOrRow || typeof taskOrRow !== 'object') return taskOrRow;
    return {
        ...taskOrRow,
        active_session_start: taskOrRow.active_session_start ?? null,
        durationSyncedAtMs: syncedAtMs,
    };
}

function isTaskRunning(task) {
    return isTaskFlagTrue(task?.active) && !isTaskFlagTrue(task?.completed);
}

function sortMosaicPreviewTasksRunningFirst(tasks) {
    return [...tasks].sort((a, b) => {
        const aRunning = isTaskRunning(a);
        const bRunning = isTaskRunning(b);
        if (aRunning !== bRunning) return aRunning ? -1 : 1;
        const aCompleted = isTaskFlagTrue(a.completed);
        const bCompleted = isTaskFlagTrue(b.completed);
        if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;
        return 0;
    });
}

/** Comparación laxa de id (API puede devolver número o string). */
function findTaskByIdInList(taskId, list) {
    if (!Array.isArray(list) || taskId == null || taskId === '') return undefined;
    const tid = Number(taskId);
    if (!Number.isFinite(tid)) return undefined;
    return list.find((t) => Number(t.id) === tid);
}

function findMosaicPreviewTaskById(previewMap, taskId) {
    if (!previewMap || taskId == null || taskId === '') return null;
    const tid = Number(taskId);
    if (!Number.isFinite(tid)) return null;
    for (const entry of Object.values(previewMap)) {
        if (!entry || !Array.isArray(entry.tasks)) continue;
        const hit = entry.tasks.find((t) => Number(t.id) === tid);
        if (hit) return hit;
    }
    return null;
}

/** Resuelve tarea para mutaciones (lista principal, panel equipo o preview del mosaico). */
function resolveTaskForMutation(taskId, tasks, teamDetailTasks, strategyTaskPreviews) {
    const fromMain = findTaskByIdInList(taskId, tasks);
    if (fromMain) return fromMain;
    const panelRow = findTaskByIdInList(taskId, teamDetailTasks);
    if (panelRow) return teamPanelRowToTask(panelRow);
    const previewHit = findMosaicPreviewTaskById(strategyTaskPreviews, taskId);
    if (previewHit) return mosaicTaskToUpdateTask(previewHit);
    return null;
}

/** Aplica un patch a la tarea en strategy_task_previews (mosaico Top 3). */
function patchTaskInStrategyTaskPreviews(prev, taskId, patch) {
    const tid = Number(taskId);
    if (!Number.isFinite(tid)) return prev;
    let touched = false;
    const next = {};
    for (const [sk, entry] of Object.entries(prev)) {
        if (!entry || !Array.isArray(entry.tasks)) {
            next[sk] = entry;
            continue;
        }
        let entryTouched = false;
        const mapped = entry.tasks.map((t) => {
            if (Number(t.id) !== tid) return t;
            entryTouched = true;
            touched = true;
            return { ...t, ...patch };
        });
        next[sk] = entryTouched ? { ...entry, tasks: mapped } : entry;
    }
    return touched ? next : prev;
}

function patchStrategyPreviewCounts(prev, statsKey, patch) {
    if (!statsKey || statsKey === 'none') return prev;
    const entry = prev[statsKey];
    if (!entry || typeof entry !== 'object') return prev;
    const next = { ...entry };
    if (patch.completedDelta != null) {
        next.completed_total = Math.max(0, (Number(entry.completed_total) || 0) + patch.completedDelta);
    }
    if (patch.totalDelta != null) {
        next.total = Math.max(0, (Number(entry.total) || 0) + patch.totalDelta);
    }
    return { ...prev, [statsKey]: next };
}

function patchStrategyBarStatsEntry(prev, statsKey, patch) {
    if (!statsKey || statsKey === 'none') return prev;
    const st = prev[statsKey];
    if (!st || typeof st !== 'object') return prev;
    const next = { ...st };
    if (patch.activeDelta != null) {
        next.active_count = Math.max(0, (Number(st.active_count) || 0) + patch.activeDelta);
    }
    if (patch.minutesDelta != null && patch.minutesDelta !== 0) {
        next.total_active_minutes = Math.max(
            0,
            (Number(st.total_active_minutes) || 0) + patch.minutesDelta
        );
    }
    return { ...prev, [statsKey]: next };
}

function taskMatchesTagFilter(task, tagFilterCategoryId) {
    if (tagFilterCategoryId == null) return true;
    return String(task?.category ?? '') === String(tagFilterCategoryId);
}

/** Misma lógica de coincidencia que filtered_tasks (texto / estrategia / oportunidad / KR). */
function taskMatchesSearchText(task, searchText) {
    const term = String(searchText ?? '')
        .toLowerCase()
        .trim();
    if (!term) return true;
    const hay = (v) => String(v ?? '').toLowerCase().includes(term);
    if (hay(task?.text)) return true;
    if (hay(task?.task_name)) return true;
    if (hay(task?.strategy)) return true;
    if (hay(task?.opportunity_name)) return true;
    if (hay(task?.kr_name)) return true;
    return false;
}

function getTaskCategoryPatch(category) {
    return {
        category: category?.id ?? null,
        category_name: category?.category_name ?? null,
        category_color: category?.color ?? null,
    };
}

/** Categoría de una fila/tarea: directa o por lookup en tasks / preview del mosaico. */
function resolveTaskCategoryId(taskOrRow, tasks, strategyTaskPreviews) {
    if (taskOrRow?.category != null && taskOrRow.category !== '') {
        return taskOrRow.category;
    }
    const tid = taskOrRow?.id;
    if (tid == null || tid === '') return null;
    const fromMain = findTaskByIdInList(tid, tasks);
    if (fromMain?.category != null && fromMain.category !== '') return fromMain.category;
    const previewHit = findMosaicPreviewTaskById(strategyTaskPreviews, tid);
    if (previewHit?.category != null && previewHit.category !== '') return previewHit.category;
    return null;
}

/** Asegura que tareas en ejecución del grupo local entren al pool del preview (p. ej. si el backend aún no las priorizó).
 *  Si ya están en el preview, sobrescribe con la copia local para conservar durationSyncedAtMs / sesión activa. */
function mergeOwnRunningTasksIntoPreview(previewTasks, ownTasksInGroup, teamAssigneeFilterIds = []) {
    const merged = Array.isArray(previewTasks) ? [...previewTasks] : [];
    const indexById = new Map();
    merged.forEach((t, i) => {
        const id = Number(t?.id);
        if (Number.isFinite(id)) indexById.set(id, i);
    });
    const filterActive = teamAssigneeFilterIds.length > 0;
    const allowedAssignees = filterActive
        ? new Set(teamAssigneeFilterIds.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0))
        : null;
    for (const t of ownTasksInGroup) {
        if (!isTaskRunning(t)) continue;
        if (filterActive) {
            const uid = t.usr_id != null && t.usr_id !== '' ? Number(t.usr_id) : null;
            if (uid == null || !allowedAssignees.has(uid)) continue;
        }
        const id = Number(t.id);
        if (!Number.isFinite(id)) continue;
        const existingIdx = indexById.get(id);
        if (existingIdx != null) {
            merged[existingIdx] = {
                ...merged[existingIdx],
                ...t,
                text: t.text ?? t.task_name ?? merged[existingIdx].text,
                task_name: t.task_name ?? t.text ?? merged[existingIdx].task_name,
            };
        } else {
            indexById.set(id, merged.length);
            merged.push(t);
        }
    }
    return merged;
}

function taskMatchesAssigneeFilter(task, teamAssigneeFilterIds) {
    if (!teamAssigneeFilterIds || teamAssigneeFilterIds.length === 0) return true;
    const uid = task?.usr_id != null && task.usr_id !== '' ? Number(task.usr_id) : null;
    if (uid == null || Number.isNaN(uid)) return false;
    return teamAssigneeFilterIds.some((x) => Number(x) === uid);
}

/** ¿La fila de preview pertenece a la tarjeta Trello / card_db_id del grupo? */
function taskBelongsToStrategyCard(task, trelloCardId, cardDbId) {
    if (!task) return false;
    const tid =
        trelloCardId != null && String(trelloCardId).trim() !== ''
            ? String(trelloCardId).trim()
            : '';
    const cid = cardDbId != null && Number(cardDbId) > 0 ? Number(cardDbId) : null;
    const taskTid =
        task.trello_card_id != null && String(task.trello_card_id).trim() !== ''
            ? String(task.trello_card_id).trim()
            : '';
    const taskCid =
        task.card_id != null && task.card_id !== '' && Number(task.card_id) > 0
            ? Number(task.card_id)
            : null;
    if (cid != null && taskCid != null && taskCid === cid) return true;
    if (tid !== '' && taskTid !== '' && taskTid === tid) return true;
    return false;
}

/**
 * Une previews del mosaico para una tarjeta Trello.
 * Compacta tareas indexadas por opportunity:* en el servidor (opp-first)
 * para que la tarjeta `trello:…` muestre el mismo conjunto que el modal por card.
 */
function collectTeamPreviewForTrelloCard(statsKey, trelloCardId, cardDbId, strategyTaskPreviews) {
    const byId = new Map();
    let displayTotal = 0;
    let displayCompleted = 0;
    const relatedStatsKeys = [];

    const previews =
        strategyTaskPreviews != null && typeof strategyTaskPreviews === 'object'
            ? strategyTaskPreviews
            : {};

    const absorbTasks = (tasks) => {
        if (!Array.isArray(tasks)) return;
        for (const t of tasks) {
            const id = Number(t?.id);
            if (!Number.isFinite(id)) continue;
            byId.set(id, t);
        }
    };

    const bumpTotals = (entry) => {
        if (!entry) return;
        displayTotal = Math.max(displayTotal, Number(entry.total) || 0);
        displayCompleted = Math.max(displayCompleted, Number(entry.completed_total) || 0);
    };

    const direct = previews[statsKey];
    if (direct) {
        relatedStatsKeys.push(statsKey);
        absorbTasks(direct.tasks);
        bumpTotals(direct);
    }

    for (const [sk, entry] of Object.entries(previews)) {
        if (sk === statsKey || entry == null || typeof entry !== 'object') continue;
        const tasks = Array.isArray(entry.tasks) ? entry.tasks : [];
        const matching = tasks.filter((t) => taskBelongsToStrategyCard(t, trelloCardId, cardDbId));
        if (matching.length === 0) continue;
        relatedStatsKeys.push(sk);
        absorbTasks(matching);
        // Si todas las muestras de este bucket pertenecen a la tarjeta, considerar sus totales
        // (backend opp-first). Se usa max (no suma) para no duplicar tras sync con el modal.
        if (tasks.length > 0 && matching.length === tasks.length) {
            bumpTotals(entry);
        }
    }

    displayTotal = Math.max(displayTotal, byId.size);
    const completedInList = [...byId.values()].filter((t) => isTaskFlagTrue(t.completed)).length;
    displayCompleted = Math.min(displayTotal, Math.max(displayCompleted, completedInList));

    return {
        tasks: [...byId.values()],
        total: displayTotal,
        completed_total: displayCompleted,
        relatedStatsKeys: [...new Set(relatedStatsKeys)],
    };
}

/** Preview del mosaico (lista resumida): tareas a mostrar y contadores. */
function resolveStrategyMosaicPreview(
    groupKey,
    groupedTasksForDisplay,
    strategyTaskPreviews,
    teamAssigneeFilterIds,
    onlyPendingTasks,
    newTaskStrategyContext,
    strategyCanvasMeta,
    previewLimit,
    tagFilterCategoryId = null,
    viewerUsrId = null,
    searchText = ''
) {
    const statsKey = getStrategyBarStatsKey(
        groupKey,
        groupedTasksForDisplay,
        newTaskStrategyContext,
        strategyCanvasMeta
    );
    const ownTasksInGroup = getOwnTasksForStrategyGroup(
        groupKey,
        groupedTasksForDisplay,
        strategyCanvasMeta
    );
    const isPrivateGroup = isPrivateStrategyGroup(
        groupKey,
        groupedTasksForDisplay,
        strategyCanvasMeta
    );
    const canvas = strategyCanvasMeta && strategyCanvasMeta[groupKey];
    const trelloCardId =
        (statsKey && String(statsKey).startsWith('trello:')
            ? String(statsKey).slice('trello:'.length).trim()
            : '') ||
        (canvas?.trello_card_id != null ? String(canvas.trello_card_id).trim() : '') ||
        getTrelloCardIdForStrategyGroup(
            groupKey,
            groupedTasksForDisplay,
            newTaskStrategyContext,
            strategyCanvasMeta
        ) ||
        '';
    const cardDbId =
        canvas?.card_db_id != null && Number(canvas.card_db_id) > 0
            ? Number(canvas.card_db_id)
            : getCardDbIdForStrategyGroup(
                  groupKey,
                  groupedTasksForDisplay,
                  newTaskStrategyContext,
                  strategyCanvasMeta
              );
    const isTrelloStatsKey = String(statsKey || '').startsWith('trello:');
    const collectedTeamPreview =
        !isPrivateGroup && isTrelloStatsKey
            ? collectTeamPreviewForTrelloCard(
                  statsKey,
                  trelloCardId,
                  cardDbId,
                  strategyTaskPreviews
              )
            : null;
    const teamPreview =
        collectedTeamPreview &&
        (collectedTeamPreview.tasks.length > 0 || collectedTeamPreview.total > 0)
            ? collectedTeamPreview
            : strategyTaskPreviews[statsKey];
    const preferTeam =
        !isPrivateGroup &&
        (teamAssigneeFilterIds.length > 0 ||
            (teamPreview && Array.isArray(teamPreview.tasks) && teamPreview.tasks.length > 0) ||
            (collectedTeamPreview && collectedTeamPreview.total > 0));

    let previewTasks = [];
    let displayTotal = 0;
    let displayCompleted = 0;

    if (isPrivateGroup) {
        previewTasks = ownTasksInGroup.filter((t) => taskMatchesAssigneeFilter(t, teamAssigneeFilterIds));
        displayTotal = previewTasks.length;
        displayCompleted = previewTasks.filter((t) => isTaskFlagTrue(t.completed)).length;
    } else if (preferTeam && teamPreview) {
        previewTasks = Array.isArray(teamPreview.tasks) ? [...teamPreview.tasks] : [];
        if (teamAssigneeFilterIds.length > 0) {
            previewTasks = previewTasks.filter((t) =>
                taskMatchesAssigneeFilter(t, teamAssigneeFilterIds)
            );
        }
        // Las tareas con card viven en la tarjeta Trello; no duplicarlas en grupos opportunity:*.
        if (String(statsKey || '').startsWith('opportunity:')) {
            previewTasks = previewTasks.filter((t) => !taskHasLinkedStrategyCard(t));
            displayTotal = previewTasks.length;
            displayCompleted = previewTasks.filter((t) => isTaskFlagTrue(t.completed)).length;
        } else {
            displayTotal = Number(teamPreview.total) || 0;
            displayCompleted = Number(teamPreview.completed_total) || 0;
            if (
                teamAssigneeFilterIds.length > 0 &&
                (displayTotal === 0 || displayTotal < previewTasks.length)
            ) {
                displayTotal = Math.max(displayTotal, previewTasks.length);
                displayCompleted = Math.min(
                    displayTotal,
                    Math.max(
                        displayCompleted,
                        previewTasks.filter((t) => isTaskFlagTrue(t.completed)).length
                    )
                );
            }
        }
    } else if (teamAssigneeFilterIds.length === 0) {
        previewTasks = [...ownTasksInGroup];
        displayTotal = ownTasksInGroup.length;
        displayCompleted = ownTasksInGroup.filter((t) => isTaskFlagTrue(t.completed)).length;
    } else {
        previewTasks = ownTasksInGroup.filter((t) => taskMatchesAssigneeFilter(t, teamAssigneeFilterIds));
        displayTotal = previewTasks.length;
        displayCompleted = previewTasks.filter((t) => isTaskFlagTrue(t.completed)).length;
    }

    if (onlyPendingTasks) {
        previewTasks = previewTasks.filter((t) => !isTaskFlagTrue(t.completed));
        // Con "Pendientes", el denominador debe ser la lista filtrada (no el total servidor con completadas).
        displayTotal = previewTasks.length;
        displayCompleted = 0;
    }

    previewTasks = mergeOwnRunningTasksIntoPreview(
        previewTasks,
        ownTasksInGroup,
        teamAssigneeFilterIds
    );
    // Incluir el resto de tareas propias del grupo (p. ej. pendientes) si faltan en el preview del equipo.
    {
        const indexById = new Map();
        previewTasks.forEach((t, i) => {
            const id = Number(t?.id);
            if (Number.isFinite(id)) indexById.set(id, i);
        });
        for (const t of ownTasksInGroup) {
            if (!taskMatchesAssigneeFilter(t, teamAssigneeFilterIds)) continue;
            if (onlyPendingTasks && isTaskFlagTrue(t.completed)) continue;
            const id = Number(t?.id);
            if (!Number.isFinite(id) || indexById.has(id)) continue;
            indexById.set(id, previewTasks.length);
            previewTasks.push(t);
        }
        displayTotal = Math.max(displayTotal, previewTasks.length);
        if (onlyPendingTasks) {
            displayCompleted = 0;
        } else {
            displayCompleted = previewTasks.filter((x) => isTaskFlagTrue(x.completed)).length;
            // Si el servidor reporta más tareas que las muestras + propias, conservar ese total.
            if (preferTeam && teamPreview) {
                const serverTotal = Number(teamPreview.total) || 0;
                if (serverTotal > displayTotal) displayTotal = serverTotal;
                const serverCompleted = Number(teamPreview.completed_total) || 0;
                if (serverCompleted > displayCompleted) {
                    displayCompleted = Math.min(displayTotal, serverCompleted);
                }
            }
        }
    }

    if (tagFilterCategoryId != null) {
        previewTasks = previewTasks.filter((t) => taskMatchesTagFilter(t, tagFilterCategoryId));
        displayTotal = previewTasks.length;
        displayCompleted = previewTasks.filter((t) => isTaskFlagTrue(t.completed)).length;
    }

    const searchTerm = String(searchText ?? '')
        .toLowerCase()
        .trim();
    if (searchTerm) {
        previewTasks = previewTasks.filter((t) => taskMatchesSearchText(t, searchTerm));
        displayTotal = previewTasks.length;
        displayCompleted = previewTasks.filter((t) => isTaskFlagTrue(t.completed)).length;
    }

    previewTasks = sortMosaicPreviewTasksRunningFirst(previewTasks);
    const listTasks = previewTasks.slice(0, previewLimit);
    const moreCount = Math.max(0, displayTotal - previewLimit);

    return {
        statsKey,
        previewTasks: listTasks,
        displayTotal,
        displayCompleted,
        moreCount,
        hasListableTasks: listTasks.length > 0,
    };
}

function getStrategyBarStatsKey(groupKey, groups, pinnedNewTaskCtx, strategyCanvasMeta) {
    const oppGroupId = getOpportunityGroupId(groupKey);
    if (groupKey === 'Sin estrategia') return 'none';
    if (oppGroupId != null) return `opportunity:${oppGroupId}`;
    const list = groups[groupKey] || [];
    const withCard = list.find((x) => x.trello_card_id != null && String(x.trello_card_id).trim() !== '');
    if (withCard) return `trello:${String(withCard.trello_card_id).trim()}`;
    const withOpp = list.find((x) => x.opportunity_id != null && x.opportunity_id !== '' && Number(x.opportunity_id) > 0);
    if (withOpp) return `opportunity:${Number(withOpp.opportunity_id)}`;
    const canvas = strategyCanvasMeta && strategyCanvasMeta[groupKey];
    if (canvas && canvas.stats_key != null && String(canvas.stats_key).trim() !== '') {
        return String(canvas.stats_key).trim();
    }
    const tid = getTrelloCardIdForStrategyGroup(groupKey, groups, pinnedNewTaskCtx, strategyCanvasMeta);
    if (tid) return `trello:${tid}`;
    return 'none';
}

/** Parámetros para list_strategy_team_tasks (misma lógica de agrupación que las tarjetas). */
function getStrategyTeamTasksParams(groupKey, groups, pinnedNewTaskCtx, strategyCanvasMeta) {
    const oppGroupId = getOpportunityGroupId(groupKey);
    if (groupKey === 'Sin estrategia') {
        return { strategy_mode: 'none', trello_card_id: '', opportunity_id: '' };
    }
    if (oppGroupId != null) {
        return { strategy_mode: 'opportunity', trello_card_id: '', opportunity_id: String(oppGroupId) };
    }
    const list = groups[groupKey] || [];
    const withCard = list.find((x) => {
        const cid = x.trello_card_id;
        return cid != null && String(cid).trim() !== '' && cid !== '99999999' && cid !== 99999999;
    });
    if (withCard) {
        return { strategy_mode: 'trello', trello_card_id: String(withCard.trello_card_id).trim(), opportunity_id: '' };
    }
    const tid = getTrelloCardIdForStrategyGroup(groupKey, groups, pinnedNewTaskCtx, strategyCanvasMeta);
    if (tid) {
        return { strategy_mode: 'trello', trello_card_id: tid, opportunity_id: '' };
    }
    return { strategy_mode: 'none', trello_card_id: '', opportunity_id: '' };
}

function parseOpportunityId(value) {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
}

function buildOpportunityGroupKey(opportunityId) {
    const n = parseOpportunityId(opportunityId);
    return n != null ? `opp:${n}` : 'Sin estrategia';
}

function getOpportunityGroupId(groupKey) {
    if (groupKey == null) return null;
    const m = String(groupKey).trim().match(/^opp:(\d+)$/);
    if (!m) return null;
    const id = Number(m[1]);
    return Number.isFinite(id) && id > 0 ? id : null;
}

function getOpportunityGroupTitle(groupKey, groups, strategyCanvasMeta) {
    const oppId = getOpportunityGroupId(groupKey);
    if (oppId == null) return groupKey;
    const list = groups[groupKey] || [];
    const fromTask = list.find(
        (t) => t.opportunity_name != null && String(t.opportunity_name).trim() !== ''
    );
    if (fromTask) return String(fromTask.opportunity_name).trim();
    const canvas = strategyCanvasMeta && strategyCanvasMeta[groupKey];
    if (canvas && canvas.opportunity_name != null && String(canvas.opportunity_name).trim() !== '') {
        return String(canvas.opportunity_name).trim();
    }
    return `Oportunidad #${oppId}`;
}

/** Descripción persistida en talent_db.cards.descript (prioriza tareas del grupo, luego canvas). */
function getStrategyCardDescript(groupKey, groups, strategyCanvasMeta) {
    const list = groups[groupKey] || [];
    const fromTask = list.find(
        (t) => t.card_descript != null && String(t.card_descript).trim() !== ''
    );
    if (fromTask) return String(fromTask.card_descript);
    const canvas = strategyCanvasMeta && strategyCanvasMeta[groupKey];
    if (canvas != null && canvas.card_descript != null && String(canvas.card_descript).trim() !== '') {
        return String(canvas.card_descript);
    }
    return '';
}

const URL_TAIL_PUNCT_RE = /[.,;:!?)\]}]+$/;

/** Quita signos de cierre típicos que no suelen formar parte del href. */
function trimUrlHrefForLink(raw) {
    let href = String(raw);
    while (href.length > 'http://'.length && URL_TAIL_PUNCT_RE.test(href)) {
        const next = href.replace(URL_TAIL_PUNCT_RE, '');
        if (next === href) break;
        href = next;
    }
    return href;
}

/** Convierte http(s)://… en enlaces (nueva pestaña). Solo vista lectura. */
function linkifyHttpsInPlainText(text, keyPrefix = 'desc-lnk', linkClassName = 'strategy-modal-card-desc-link') {
    if (text == null || text === '') return text;
    const s = String(text);
    const re = /https?:\/\/[^\s<]+/gi;
    const matches = [...s.matchAll(re)];
    if (matches.length === 0) return s;
    const out = [];
    let last = 0;
    let k = 0;
    for (const m of matches) {
        const mi = m.index;
        if (mi > last) out.push(s.slice(last, mi));
        const raw = m[0];
        const href = trimUrlHrefForLink(raw);
        if (!/^https?:\/\/.+/i.test(href)) {
            out.push(raw);
        } else {
            const after = raw.slice(href.length);
            out.push(
                <a
                    key={`${keyPrefix}-${mi}-${k++}`}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClassName}
                >
                    {href}
                </a>
            );
            if (after) out.push(after);
        }
        last = mi + raw.length;
    }
    if (last < s.length) out.push(s.slice(last));
    return <>{out}</>;
}

/** ID sintético para tarjetas creadas solo en la app (no Trello). */
function generateLocalTrelloCardId() {
    return `local-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function formatStrategyCommentDate(dateStr) {
    if (dateStr == null || dateStr === '') return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

/**
 * Fila de meta/acciones de tarea (fecha, tag, tiempo | play, mover, eliminar).
 * Debe vivir fuera de TacticFollowup: si se define dentro del page, cada render crea un tipo
 * de componente distinto y React remonta hijos (p. ej. el input de edición del mosaico).
 */
function StrategyTaskActionRow({
    task,
    hideTag = false,
    hidePlay = false,
    readOnly = false,
    nowMs = Date.now(),
    truncateCategoryLabel,
    onOpenTaskModal,
    onOpenCategoryModal,
    onOpenDueDateModal,
    onDeleteTask,
    onTogglePriority,
}) {
    const MESES_ABREV = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    const formatDueDate = (d) => {
        if (!d) return null;
        const [y, m, day] = d.split('-');
        if (!day || !m) return d;
        const mesIdx = parseInt(m, 10) - 1;
        const dia = parseInt(day, 10);
        return `${dia} ${MESES_ABREV[mesIdx] ?? m}`;
    };
    const t = new Date();
    const todayMidnight = new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime();
    const parts = task.due_date && task.due_date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const dueMidnight = parts ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3])).getTime() : NaN;
    const isOverdue = !task.completed && task.due_date && !isNaN(dueMidnight) && dueMidnight <= todayMidnight;
    const durationLabel = formatoDuracion(getLiveActiveMinutes(task, nowMs));
    const dueLabel = task.due_date ? `${formatDueDate(task.due_date)}` : 'Vence';

    const tryDelete = (e) => {
        e?.stopPropagation?.();
        if (window.confirm('¿Eliminar esta tarea?')) onDeleteTask(task.id);
    };

    const metaLeft = (
        <div className="strategy-task-meta-left">
            {readOnly ? (
                <span className={`strategy-task-meta-item ${isOverdue ? 'is-overdue' : ''}`}>
                    <Calendar size={14} strokeWidth={2} className="flex-shrink-0" />
                    <span>{dueLabel}</span>
                </span>
            ) : (
                <button
                    type="button"
                    className={`strategy-task-meta-btn strategy-task-meta-item ${isOverdue ? 'is-overdue' : ''}`}
                    onClick={() => onOpenDueDateModal(task)}
                    aria-label={task.due_date ? `Vencimiento: ${formatDueDate(task.due_date)}` : 'Establecer fecha de vencimiento'}
                >
                    <Calendar size={14} strokeWidth={2} className="flex-shrink-0" />
                    <span>{dueLabel}</span>
                </button>
            )}
            {!hideTag && !readOnly && (
                <button
                    type="button"
                    className={`strategy-task-meta-btn ${task.category_name ? 'strategy-task-tag-btn' : 'strategy-task-tag-btn--empty'}`}
                    onClick={() => onOpenCategoryModal(task)}
                    aria-label={task.category_name ? 'Cambiar etiqueta' : 'Asignar etiqueta'}
                >
                    {task.category_name ? (
                        <span
                            className="strategy-task-tag-pill"
                            style={{
                                backgroundColor: task.category_color || '#ede9fe',
                                color: task.category_color ? '#1f2937' : '#6d28d9',
                            }}
                        >
                            <Tag size={12} strokeWidth={2} className="flex-shrink-0" />
                            <span className="strategy-task-tag-pill-label">{truncateCategoryLabel(task.category_name)}</span>
                        </span>
                    ) : (
                        <span className="strategy-task-tag-empty">
                            <Tag size={12} strokeWidth={2} className="flex-shrink-0" />
                            Tag
                        </span>
                    )}
                </button>
            )}
            <span className="strategy-task-meta-item">
                <Clock size={14} strokeWidth={2} className="flex-shrink-0" />
                <span>{durationLabel}</span>
            </span>
        </div>
    );

    if (readOnly) {
        return <div className="strategy-card-meta strategy-task-card-bottom">{metaLeft}</div>;
    }

    return (
        <div className="strategy-card-meta strategy-task-card-bottom">
            {metaLeft}
            <div className="strategy-task-meta-right">
                {!hidePlay && !task.completed && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onTogglePriority(task.id, task.active);
                        }}
                        className={`strategy-task-action-icon ${task.active ? 'is-pause' : 'is-play'}`}
                        aria-label={task.active ? 'Pausar ejecución' : 'Poner en ejecución'}
                    >
                        {task.active ? (
                            <Pause size={18} fill="currentColor" />
                        ) : (
                            <Play size={18} fill="currentColor" />
                        )}
                    </button>
                )}
                <button
                    type="button"
                    className="strategy-task-action-icon is-move"
                    onClick={(e) => {
                        e?.stopPropagation?.();
                        onOpenTaskModal(task);
                    }}
                    aria-label="Mover"
                    title="Mover"
                >
                    <Folder size={17} strokeWidth={2} />
                </button>
                <button
                    type="button"
                    onClick={tryDelete}
                    className="strategy-task-action-icon is-delete"
                    aria-label="Eliminar tarea"
                >
                    <Trash2 size={17} strokeWidth={2} />
                </button>
            </div>
        </div>
    );
}

/** Fila compacta de tarea en el mosaico de estrategias (mismo motivo: tipo estable → input controlado no se remonta). */
function StrategyCardTaskRow({
    task,
    isMosaicEditing,
    mosaicEditText,
    onMosaicEditTextChange,
    setMosaicEditTaskId,
    maxChars,
    mosaicNavigateTimeoutRef,
    onOpenStatistics,
    cardsLength,
    truncateCategoryLabel,
    onUpdateTaskText,
    onToggleTaskComplete,
    onOpenTaskModal,
    onOpenCategoryModal,
    onOpenDueDateModal,
    onDeleteTask,
    onTogglePriority,
    onStartMosaicTitleEdit,
    readOnly = false,
    assigneeUsrId = null,
    assigneeLabel = '',
    nowMs = Date.now(),
}) {
    const saveMosaicTitle = () => {
        if (!mosaicEditText.trim() || mosaicEditText.length > maxChars) {
            onMosaicEditTextChange(task.text);
            setMosaicEditTaskId(null);
            return;
        }
        if (mosaicEditText.trim() !== task.text) {
            onUpdateTaskText(task.id, mosaicEditText.trim());
        }
        setMosaicEditTaskId(null);
    };
    const onMosaicTitleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            saveMosaicTitle();
        } else if (e.key === 'Escape') {
            onMosaicEditTextChange(task.text);
            setMosaicEditTaskId(null);
        }
    };
    return (
        <div className={`strategy-card-task-compact${task.completed ? ' is-completed' : ''}${task.active ? ' task-priority-active' : ''}`}>
            <div className="strategy-task-card-top">
                <div className="strategy-task-card-top-left">
                    <div className="task-icon-container flex-shrink-0">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleTaskComplete(task.id, task.completed);
                            }}
                            className={`strategy-task-check-btn ${task.completed ? 'is-completed' : ''} ${task.active ? 'is-active' : ''} ${cardsLength === 0 || readOnly ? 'is-disabled' : ''}`}
                            aria-label={task.completed ? 'Marcar como pendiente' : 'Marcar como completada'}
                            disabled={cardsLength === 0 || readOnly}
                        >
                            {task.completed ? <CheckSquare size={18} /> : <Square size={18} />}
                        </button>
                    </div>
                    {isMosaicEditing ? (
                        <div className="strategy-task-title-edit d-flex align-items-center gap-1 flex-grow-1 min-w-0" onClick={(e) => e.stopPropagation()}>
                            <input
                                type="text"
                                value={mosaicEditText}
                                onChange={(e) => onMosaicEditTextChange(e.target.value)}
                                onKeyDown={onMosaicTitleKeyDown}
                                onBlur={saveMosaicTitle}
                                maxLength={maxChars}
                                className="form-control form-control-sm flex-grow-1"
                                style={{ minWidth: '80px' }}
                                autoFocus
                                aria-label="Editar nombre de la tarea"
                            />
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-success p-0 px-1"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    saveMosaicTitle();
                                }}
                                aria-label="Guardar nombre"
                            >
                                <Check size={16} />
                            </button>
                        </div>
                    ) : (
                        <div
                            className={`strategy-card-task-desc flex-grow-1 min-w-0 ${task.completed ? 'is-completed' : ''}`}
                            role="button"
                            tabIndex={0}
                            title="Clic para ver estadísticas"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (mosaicNavigateTimeoutRef.current) clearTimeout(mosaicNavigateTimeoutRef.current);
                                mosaicNavigateTimeoutRef.current = setTimeout(() => {
                                    mosaicNavigateTimeoutRef.current = null;
                                    onOpenStatistics(task.id, task.usr_id);
                                }, 300);
                            }}
                            onDoubleClick={(e) => {
                                e.stopPropagation();
                                if (mosaicNavigateTimeoutRef.current) {
                                    clearTimeout(mosaicNavigateTimeoutRef.current);
                                    mosaicNavigateTimeoutRef.current = null;
                                }
                                onOpenStatistics(task.id, task.usr_id);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    if (mosaicNavigateTimeoutRef.current) clearTimeout(mosaicNavigateTimeoutRef.current);
                                    mosaicNavigateTimeoutRef.current = setTimeout(() => {
                                        mosaicNavigateTimeoutRef.current = null;
                                        onOpenStatistics(task.id, task.usr_id);
                                    }, 300);
                                }
                            }}
                        >
                            {task.text}
                        </div>
                    )}
                </div>
                <div className="strategy-task-card-top-right">
                    {!readOnly && !isMosaicEditing ? (
                        <button
                            type="button"
                            className="strategy-task-action-icon is-edit"
                            title="Editar nombre"
                            aria-label="Editar nombre de la tarea"
                            onClick={(e) => {
                                e.stopPropagation();
                                onStartMosaicTitleEdit();
                            }}
                        >
                            <Pencil size={15} strokeWidth={2} />
                        </button>
                    ) : null}
                    {assigneeUsrId != null ? (
                        <div className="strategy-card-task-avatar flex-shrink-0">
                            <UserAvatar usr_id={assigneeUsrId} usr_name={assigneeLabel || undefined} size={26} />
                        </div>
                    ) : null}
                </div>
            </div>
            <StrategyTaskActionRow
                task={task}
                hidePlay={readOnly}
                nowMs={nowMs}
                truncateCategoryLabel={truncateCategoryLabel}
                onOpenTaskModal={onOpenTaskModal}
                onOpenCategoryModal={onOpenCategoryModal}
                onOpenDueDateModal={onOpenDueDateModal}
                onDeleteTask={onDeleteTask}
                onTogglePriority={onTogglePriority}
            />
        </div>
    );
}

/**
 * Popup "+ nueva tarea" desde el pie del modal de estrategia.
 * El borrador vive aquí para no disparar un re-render de todo TacticFollowup en cada tecla
 * (en Equipo + modal abierto el árbol DOM es muy grande y la escritura se notaba a tirones).
 */
const NewTaskFabPopup = React.memo(function NewTaskFabPopup({
    maxChars,
    strategyContext,
    onClose,
    onAdd,
    isAdding = false,
    assigneeUsrId,
    assigneeLabel,
    onOpenAssigneePicker,
    hideAssigneePicker = false,
}) {
    const [draft, setDraft] = useState('');
    const taRef = useRef(null);

    useLayoutEffect(() => {
        taRef.current?.focus?.();
    }, []);

    const syncFabTextareaHeight = useCallback(() => {
        const el = taRef.current;
        if (!el) return;
        el.style.height = 'auto';
        const maxH = parseFloat(getComputedStyle(el).maxHeight);
        const cap = Number.isFinite(maxH) && maxH > 0 ? maxH : el.scrollHeight;
        el.style.height = `${Math.min(el.scrollHeight, cap)}px`;
    }, []);

    useLayoutEffect(() => {
        syncFabTextareaHeight();
    }, [draft, syncFabTextareaHeight]);

    const submit = () => {
        if (isAdding) return;
        const t = draft.trim();
        if (!t || t.length > maxChars) return;
        onAdd(t);
    };

    const handleOverlayClose = () => {
        if (isAdding) return;
        onClose();
    };

    return (
        <div className="new-task-fab-popup-overlay" role="presentation" onClick={handleOverlayClose}>
            <div
                className="new-task-fab-popup-panel"
                role="dialog"
                aria-modal="true"
                aria-label="Nueva tarea"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="new-task-fab-popup-row">
                    <div className="new-task-fab-popup-main">
                        <textarea
                            ref={taRef}
                            rows={1}
                            className="form-control form-control-lg border-0 new-task-fab-popup-textarea"
                            style={{
                                minWidth: 0,
                                boxShadow: 'none',
                                paddingLeft: 0,
                                paddingRight: 0,
                            }}
                            placeholder="ie: Crear pieza gráfica para campaña"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            maxLength={maxChars}
                            disabled={isAdding}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    submit();
                                }
                            }}
                        />
                        {strategyContext ? (
                            <p className="new-task-fab-popup-strategy-label mb-0">{strategyContext.displayLabel}</p>
                        ) : null}
                    </div>
                    <div className="new-task-fab-popup-actions">
                        <div className="new-task-fab-popup-actions-top">
                            <span className="text-sm text-muted px-1 flex-shrink-0">
                                {draft.length}/{maxChars}
                            </span>
                            {!hideAssigneePicker ? (
                            <button
                                type="button"
                                className="new-task-assignee-btn ms-1"
                                title={assigneeLabel ? `Asignar a: ${assigneeLabel}` : 'Elegir asignatario'}
                                aria-label="Elegir usuario asignado a la tarea"
                                onClick={onOpenAssigneePicker}
                                disabled={isAdding}
                            >
                                <UserAvatar usr_id={assigneeUsrId} usr_name={assigneeLabel || undefined} />
                            </button>
                            ) : null}
                            <button
                                type="button"
                                onClick={submit}
                                disabled={isAdding || draft.length === 0 || draft.length > maxChars}
                                className="btn btn-success shadow-sm flex-shrink-0 ms-2 tactic-new-task-add-btn"
                                aria-busy={isAdding}
                                aria-label={isAdding ? 'Creando tarea…' : 'Agregar tarea'}
                            >
                                {isAdding ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                ) : (
                                    <Plus size={22} strokeWidth={2.5} aria-hidden />
                                )}
                            </button>
                        </div>
                        {isAdding ? (
                            <span className="small text-muted">Procesando…</span>
                        ) : null}
                        <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-decoration-underline"
                            style={{ color: '#0060b9' }}
                            onClick={handleOverlayClose}
                            disabled={isAdding}
                        >
                            Quitar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
});

/**
 * Nombre editable en fila del panel de equipo.
 * Estado de edición local para no re-renderizar todo el acordeón en cada tecla.
 * El lápiz vive en el shell (top-right); este componente expone startEdit vía ref.
 */
const TeamPanelEditableTaskName = React.memo(
    forwardRef(function TeamPanelEditableTaskName(
        { taskId, taskName, completed, maxChars, onSaveName, onOpenStatistics },
        ref
    ) {
        const [isEditing, setIsEditing] = useState(false);
        const [editText, setEditText] = useState('');

        const save = useCallback(() => {
            const next = editText.trim();
            if (next.length > 0 && next !== String(taskName ?? '')) {
                onSaveName(taskId, next);
            }
            setIsEditing(false);
            setEditText('');
        }, [editText, onSaveName, taskId, taskName]);

        const cancel = useCallback(() => {
            setIsEditing(false);
            setEditText('');
        }, []);

        const startEdit = useCallback(() => {
            setEditText(String(taskName ?? ''));
            setIsEditing(true);
        }, [taskName]);

        useImperativeHandle(ref, () => ({ startEdit }), [startEdit]);

        if (isEditing) {
            return (
                <div className="strategy-task-title-edit d-flex align-items-center gap-1 flex-grow-1 min-w-0" onClick={(e) => e.stopPropagation()}>
                    <input
                        type="text"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                save();
                            } else if (e.key === 'Escape') {
                                e.preventDefault();
                                cancel();
                            }
                        }}
                        onBlur={save}
                        maxLength={maxChars}
                        className="form-control form-control-sm"
                        autoFocus
                        aria-label="Editar nombre de la tarea"
                    />
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-success p-0 px-1"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => {
                            e.stopPropagation();
                            save();
                        }}
                        aria-label="Guardar nombre"
                    >
                        <Check size={16} />
                    </button>
                </div>
            );
        }

        return (
            <button
                type="button"
                className={`strategy-team-task-open-btn strategy-card-task-desc flex-grow-1 min-w-0${completed ? ' is-completed' : ''}`}
                title="Ver detalle de la tarea"
                onClick={(e) => {
                    e.stopPropagation();
                    onOpenStatistics(taskId);
                }}
            >
                {taskName}
            </button>
        );
    })
);

/** Fila de tarea del modal de equipo (card de dos filas; tipo estable fuera del page). */
function TeamPanelTaskRow({
    row,
    panelTask,
    isOwnTask,
    cardsLength,
    maxChars,
    nowMs,
    truncateCategoryLabel,
    onToggleTask,
    onSaveName,
    onOpenStatistics,
    onOpenTaskModal,
    onOpenCategoryModal,
    onOpenDueDateModal,
    onDeleteTask,
    onTogglePriority,
    onReassign,
}) {
    const nameEditRef = useRef(null);
    const running = row.active && !row.completed;

    return (
        <div className={`strategy-team-panel-row${row.completed ? ' is-completed' : ''}${running ? ' strategy-team-panel-row--active' : ''}`}>
            <div className="strategy-task-card-top">
                <div className="strategy-task-card-top-left">
                    <div className="task-icon-container flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => onToggleTask(row.id, row.completed)}
                            className={`strategy-task-check-btn ${row.completed ? 'is-completed' : ''} ${row.active ? 'is-active' : ''} ${cardsLength === 0 ? 'is-disabled' : ''}`}
                            aria-label={row.completed ? 'Marcar como pendiente' : 'Marcar como completada'}
                            disabled={cardsLength === 0}
                        >
                            {row.completed ? <CheckSquare size={18} /> : <Square size={18} />}
                        </button>
                    </div>
                    <TeamPanelEditableTaskName
                        ref={nameEditRef}
                        taskId={row.id}
                        taskName={row.task_name}
                        completed={!!row.completed}
                        maxChars={maxChars}
                        onSaveName={onSaveName}
                        onOpenStatistics={onOpenStatistics}
                    />
                </div>
                <div className="strategy-task-card-top-right">
                    {isOwnTask ? (
                        <button
                            type="button"
                            className="strategy-task-action-icon is-edit"
                            title="Editar nombre"
                            aria-label="Editar nombre de la tarea"
                            onClick={(e) => {
                                e.stopPropagation();
                                nameEditRef.current?.startEdit?.();
                            }}
                        >
                            <Pencil size={15} strokeWidth={2} />
                        </button>
                    ) : null}
                    <div className="strategy-team-avatar-wrap">
                        <button
                            type="button"
                            className="strategy-team-avatar-btn"
                            title="Reasignar tarea a otro usuario"
                            aria-label="Reasignar tarea a otro usuario"
                            onClick={(e) => {
                                e.stopPropagation();
                                onReassign(row);
                            }}
                        >
                            <UserAvatar usr_id={row.usr_id} usr_name={row.email_id || 'Usuario'} size={26} />
                        </button>
                    </div>
                </div>
            </div>
            {panelTask ? (
                <StrategyTaskActionRow
                    task={panelTask}
                    hidePlay={!isOwnTask}
                    nowMs={nowMs}
                    truncateCategoryLabel={truncateCategoryLabel}
                    onOpenTaskModal={onOpenTaskModal}
                    onOpenCategoryModal={onOpenCategoryModal}
                    onOpenDueDateModal={onOpenDueDateModal}
                    onDeleteTask={onDeleteTask}
                    onTogglePriority={onTogglePriority}
                />
            ) : null}
        </div>
    );
}

/***
 * Mapa personal de goals diarios
 */
const TacticFollowup = ({usr_id, token, cards, crm, cardsLoading = false, onMyRunningTasksChange, onMyRunningTaskPatch}) => {
     const navigate = useNavigate();   
     const [duration, setDuration] = useState("");
    // --- Custom CSS para elementos no estándar de Bootstrap (Botón 'e', iconos, etc.) ---
    const customStyles = `
        .task-item {
            background-color: #fff;
            border: 1px solid #e0e0e0;
            border-radius: 0.5rem; /* rounded-lg */
            transition: box-shadow 0.15s ease-in-out;
        }
        .task-item:hover {
            box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
        }
        .task-action-btn {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background-color: #fcd34d; /* bg-yellow-300 */
            color: #1f2937; /* text-gray-800 */
            font-weight: bold;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            transition: background-color 0.15s ease-in-out;
            border: none;
            flex-shrink: 0;
        }
        .task-action-btn:hover {
            background-color: #fbbf24; /* hover:bg-yellow-400 */
        }
        .task-header {
            font-size: 1.125rem;
            font-weight: 600;
            color: #4b5563;
            text-transform: uppercase;
            border-bottom: 1px solid #d1d5db;
        }
        .task-group-header {
            font-size: 1rem;
            font-weight: 600;
            color: #4b5563;
            cursor: pointer;
            text-transform: none;
        }
        .task-group-header:hover {
            color: #1f2937;
        }
        /* Para asegurar que los SVG de lucide no sean reemplazados por iconos de tema */
        .task-icon-container button {
            background: none !important;
            border: none !important;
            padding: 0;
        }
        .task-icon-container svg {
            color: currentColor !important;
            fill: none !important;
        }
                    /* Estilo para la tarea de alta prioridad (llama activa) */
        .task-priority-active {
            background-color: #ffedd5; /* Fondo suave anaranjado */
            border-left: 5px solid #f97316; /* Borde de prioridad */
        }
       .btn-success-green {
            /* Fondo verde */
            background-color: #28a745; 
            /* Color de texto claro para contraste */
            color: white; 
            /* Opcional: Asegúrate de que no tenga bordes feos */
            border-color: #28a745;
        }

         .task-dropdown.dropdown-menu {
            position: absolute !important;
            z-index: 1050; /* Mayor que el modal de bootstrap por defecto */
            /* ANCLA EL MENÚ AL BORDE DERECHO Y LO FUERZA A DESPLAZARSE HACIA LA IZQUIERDA */
            right: 0 !important;
            left: auto !important; 
        }
            .cursor-not-allowed {
            cursor: not-allowed !important; /* El !important podría ser necesario */
        }
            .opacity-50 {
                    opacity: 0.5;
                    
                }
                    .opacity-10 {
                    opacity: 0.1 !important; 
                    
                }
        .category-pill {
            min-height: 28px;
            padding: 0.25rem 0.5rem;
            font-size: 0.8rem;
            border: none;
            transition: opacity 0.15s ease, transform 0.15s ease;
        }
        .category-pill:hover { opacity: 0.95; }
        .category-pill-wrap {
            display: inline-flex;
            align-items: stretch;
            border-radius: 0.25rem;
            overflow: hidden;
            vertical-align: middle;
            transition: box-shadow 0.15s ease, opacity 0.15s ease;
            max-width: 100%;
        }
        .category-pill-wrap:hover { opacity: 0.95; }
        .category-pill-wrap--filter-active {
            box-shadow: 0 0 0 2px #1f2937, 0 0 0 4px rgba(31, 41, 55, 0.18);
        }
        .category-pill-filter-part {
            border: none;
            background: transparent;
            color: inherit;
            font-size: inherit;
            display: flex;
            align-items: center;
            padding: 0.25rem 0.35rem 0.25rem 0.5rem;
            flex: 1 1 auto;
            min-width: 0;
            text-align: left;
            line-height: 1.2;
        }
        .category-pill-edit-btn {
            border: none;
            border-left: 1px solid rgba(0, 0, 0, 0.12);
            background: rgba(0, 0, 0, 0.06);
            color: #374151;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0 0.35rem;
            flex-shrink: 0;
        }
        .category-pill-edit-btn:hover {
            background: rgba(0, 0, 0, 0.12);
            color: #111827;
        }
        .category-add-btn {
            flex-shrink: 0;
            padding: 0;
            overflow: visible;
        }
        .category-add-btn svg {
            flex-shrink: 0;
            display: block;
        }
        /* Fila vencimiento + Estado + Miembros: controles alineados como form-select-sm */
        .tactic-filters-toolbar-period {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.5rem;
            min-width: 0;
            max-width: 100%;
            /* Baja a su propia fila cuando no cabe junto a las etiquetas */
            flex: 1 1 14rem;
        }
        .tactic-filter-inline-field {
            display: inline-flex;
            align-items: center;
            gap: 0.35rem;
            min-width: 0;
            max-width: 100%;
        }
        .tactic-filter-inline-field > .small {
            margin-bottom: 0;
            line-height: 1.25;
        }
        .tactic-filter-pending-check {
            flex: 0 0 auto;
        }
        .tactic-filter-pending-check .form-check {
            display: inline-flex;
            align-items: center;
            margin: 0;
            min-height: calc(1.5em + 0.5rem + 2px);
            padding-left: 1.2rem;
        }
        .tactic-filter-pending-check .form-check-input {
            margin-top: 0;
        }
        .tactic-filter-pending-check .form-check-label {
            margin-bottom: 0;
            margin-left: 0.2rem;
            line-height: 1;
        }
        .tactic-filter-due-date-field {
            flex: 1 1 10rem;
            max-width: 100%;
        }
        .tactic-filter-due-date-field .tactic-filter-due-date-input-group {
            width: 100%;
            max-width: 100%;
        }
        .tactic-filter-due-date-field .form-control {
            font-size: 0.875rem;
            line-height: 1.5;
            min-width: 0;
            max-width: 100%;
        }
        .tactic-filter-due-date-field .btn {
            min-height: calc(1.5em + 0.5rem + 2px);
            padding: 0.25rem 0.5rem;
        }
        .tactic-filter-due-date-popover {
            position: absolute;
            top: calc(100% + 0.25rem);
            left: 0;
            z-index: 1200;
            background: #fff;
            border-radius: 0.375rem;
            overflow: hidden;
            max-width: min(96vw, 760px);
        }
        .tactic-filter-estado-row {
            margin-left: 0.25rem;
            flex: 1 1 10rem;
            min-width: 0;
            max-width: 100%;
        }
        .tactic-filter-estado-select {
            display: block;
            width: 100%;
            min-width: 0;
            max-width: 100%;
        }
        .tactic-filter-estado-select .react-select__control {
            min-height: calc(1.5em + 0.5rem + 2px);
            border-color: #ced4da;
            box-shadow: none;
            align-items: center;
        }
        .tactic-filter-estado-select .react-select__control:hover {
            border-color: #adb5bd;
        }
        .tactic-filter-estado-select .react-select__value-container {
            padding-top: 0;
            padding-bottom: 0;
            flex-wrap: nowrap;
            overflow: hidden;
            align-items: center;
        }
        .tactic-filter-estado-select .react-select__multi-value {
            margin: 2px;
            min-width: 0;
            max-width: 100%;
            align-items: center;
        }
        .tactic-filter-estado-select .react-select__placeholder,
        .tactic-filter-estado-select .react-select__multi-value__label {
            font-size: 0.8125rem;
        }
        .tactic-filter-estado-select .react-select__multi-value__label {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        .tactic-filter-estado-select .react-select__input-container {
            margin: 0;
            padding: 0;
            min-width: 0 !important;
        }
        .tactic-filter-estado-select .react-select__menu {
            z-index: 1200;
        }
        .tactic-filter-estado-dropdown.dropdown {
            display: inline-flex;
            align-items: center;
        }
        /* Misma caja que .form-select.form-select-sm (altura y padding) */
        .tactic-filter-estado-dropdown .dropdown-toggle.btn-sm {
            display: inline-flex;
            align-items: center;
            justify-content: space-between;
            gap: 0.35rem;
            font-size: 0.875rem;
            line-height: 1.5;
            padding: 0.25rem 0.5rem;
            min-height: calc(1.5em + 0.5rem + 2px);
            box-sizing: border-box;
        }
        .tactic-filter-estado-dropdown .dropdown-toggle.btn-sm::after {
            margin-top: 0;
            vertical-align: 0.15em;
        }
        .tactic-team-assignee-menu {
            min-width: 15rem !important;
        }
        .tactic-team-assignee-menu .tactic-team-assignee-search {
            position: sticky;
            top: 0;
            z-index: 1;
            background: #fff;
            padding-bottom: 0.35rem;
            margin: -0.15rem -0.25rem 0.4rem;
            padding-left: 0.25rem;
            padding-right: 0.25rem;
            padding-top: 0.15rem;
            border-bottom: 1px solid #e9ecef;
        }
        .tactic-team-assignee-menu .tactic-team-assignee-row {
            display: flex;
            align-items: center;
            gap: 0.45rem;
            margin-bottom: 0.35rem;
        }
        .tactic-team-assignee-menu .tactic-team-assignee-row:last-of-type {
            margin-bottom: 0;
        }
        .tactic-team-assignee-menu .tactic-team-assignee-row .tactic-team-assignee-avatar-wrap .avatar-container {
            line-height: 0;
        }
        .tactic-team-assignee-menu .tactic-team-assignee-row .tactic-team-assignee-avatar-wrap .profile-pic-circle,
        .tactic-team-assignee-menu .tactic-team-assignee-row .tactic-team-assignee-avatar-wrap .initials-circle {
            width: 28px;
            height: 28px;
            font-size: 0.62rem;
        }
        .tactic-team-assignee-menu .tactic-team-assignee-label {
            cursor: pointer;
            margin-bottom: 0;
            flex: 1;
            min-width: 0;
            line-height: 1.25;
            display: flex;
            align-items: center;
            gap: 0.45rem;
        }
        .tactic-team-assignee-menu .tactic-team-assignee-label .tactic-team-assignee-name {
            flex: 1;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        @media (max-width: 576px) {
            .category-pill { font-size: 0.75rem; padding: 0.2rem 0.4rem !important; min-height: 26px; }
            .category-pill-wrap { font-size: 0.75rem !important; min-height: 26px; }
            .tactic-filters-toolbar-period {
                flex: 1 1 100%;
                width: 100%;
                margin-left: 0 !important;
                padding-left: 0 !important;
                border-left: none !important;
                margin-top: 0.35rem;
            }
            .tactic-filter-estado-row,
            .tactic-filter-due-date-field {
                flex: 1 1 calc(50% - 0.5rem);
                margin-left: 0 !important;
            }
            .tactic-filter-pending-check {
                margin-left: 0 !important;
            }
        }
        .strategy-card {
            background: #fff;
            border: 1px solid #e5e7eb;
            border-radius: 0.75rem;
            box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04);
            display: flex;
            flex-direction: column;
            min-height: 280px;
            overflow: hidden;
            position: relative;
        }
        .strategy-card--team-compact {
            min-height: 210px;
        }
        .strategy-card--completed {
            background: #f0fdf4;
            border-color: #bbf7d0;
        }
        .strategy-card--sin-estrategia {
            background: #f9fafb;
        }
        .strategy-card--private {
            background: #faf8ff;
            border-color: #ddd6fe;
        }
        .strategy-card--private.strategy-card--completed {
            background: #f5f3ff;
            border-color: #c4b5fd;
        }
        .strategy-card-main {
            position: relative;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
        .strategy-card-expand-hit {
            flex: 1 1 auto;
            display: flex;
            flex-direction: column;
            min-height: 0;
            cursor: pointer;
        }
        .strategy-card-title-row {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 0.5rem;
            padding: 1rem 1rem 0.75rem;
        }
        .strategy-card-title {
            font-size: 1.125rem;
            font-weight: 700;
            color: #374151;
            line-height: 1.35;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            flex: 1;
            min-width: 0;
            padding: 0;
        }
        .strategy-card-expand-btn {
            flex-shrink: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 2rem;
            height: 2rem;
            margin: -0.15rem -0.15rem 0 0;
            padding: 0;
            border: none;
            border-radius: 0.25rem;
            background: transparent;
            color: #6b7280;
            cursor: pointer;
        }
        .strategy-card-expand-btn:hover {
            background: rgba(0, 0, 0, 0.06);
            color: #374151;
        }
        .strategy-card-expand-btn:focus-visible {
            outline: 2px solid #3b82f6;
            outline-offset: 2px;
        }
        .strategy-card-body {
            flex: 1;
            padding: 0 0.35rem 0.75rem;
            display: flex;
            flex-direction: column;
            min-height: 0;
        }
        .strategy-card-body--team-compact {
            padding: 0 0.35rem 0.6rem;
        }
        .strategy-card-team-description {
            margin: 0;
            font-size: 0.92rem;
            line-height: 1.4;
            color: #4b5563;
            white-space: pre-wrap;
            overflow-wrap: anywhere;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .strategy-card-empty-hint {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 1rem 0.75rem 1.25rem;
            margin: 0 -0.25rem;
            min-height: 120px;
            border: none;
            background: transparent;
            border-radius: 0.25rem;
            cursor: pointer;
            color: #6b7280;
            transition: background-color 0.15s ease;
        }
        .strategy-card-empty-hint:hover {
            background: rgba(0, 0, 0, 0.04);
        }
        .strategy-card-empty-hint:focus {
            outline: 2px solid #0d6efd;
            outline-offset: 2px;
        }
        .strategy-card-empty-hint__text {
            margin: 0;
            font-size: 0.9375rem;
            line-height: 1.45;
            max-width: 18rem;
            color: #6b7280;
        }
        .strategy-card-empty-hint__line {
            display: block;
            margin-bottom: 0.35rem;
        }
        .strategy-card-empty-hint__action {
            display: block;
            text-decoration: underline;
            text-underline-offset: 2px;
            color: #5b6470;
            font-weight: 500;
        }
        .strategy-card-task-compact {
            display: flex;
            flex-direction: column;
            gap: 0.45rem;
            padding: 0.65rem 0.75rem;
            margin-bottom: 0.45rem;
            background: #fff;
            border: 1px solid #e5e7eb;
            border-radius: 0.65rem;
        }
        .strategy-card-task-compact:last-of-type {
            margin-bottom: 0;
        }
        .strategy-card-task-compact.is-completed {
            background: #bbf7d0;
            border-color: #86efac;
        }
        .strategy-card-task-compact.task-priority-active {
            background: #ffedd5;
            border: 1px solid #fdba74;
            box-shadow: inset 3px 0 0 #f97316;
        }
        .strategy-task-card-top {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 0.5rem;
            min-width: 0;
        }
        .strategy-task-card-top-left {
            display: flex;
            align-items: flex-start;
            gap: 0.45rem;
            min-width: 0;
            flex: 1 1 auto;
        }
        .strategy-task-card-top-right {
            display: flex;
            align-items: center;
            gap: 0.35rem;
            flex-shrink: 0;
        }
        .strategy-task-check-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0;
            margin: 0;
            border: none;
            background: transparent;
            color: #94a3b8;
            line-height: 0;
            cursor: pointer;
        }
        .strategy-task-check-btn.is-completed {
            color: #16a34a;
        }
        .strategy-task-check-btn.is-active {
            color: #dc2626;
        }
        .strategy-task-check-btn.is-disabled {
            opacity: 0.15;
            cursor: not-allowed;
        }
        .strategy-card-task-avatar {
            line-height: 0;
        }
        .strategy-card-task-desc {
            font-size: 0.9rem;
            font-weight: 600;
            color: #1e293b;
            line-height: 1.35;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-align: left;
            border: none;
            background: transparent;
            padding: 0;
            cursor: pointer;
        }
        .strategy-card-task-desc.is-completed {
            text-decoration: line-through;
            color: #16a34a;
            font-weight: 500;
        }
        .strategy-team-task-open-btn.strategy-card-task-desc {
            -webkit-line-clamp: unset;
            display: block;
            overflow: visible;
            white-space: normal;
            word-break: break-word;
        }
        .strategy-card-meta,
        .strategy-task-card-bottom {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 0.45rem 0.65rem;
            width: 100%;
            font-size: 0.78rem;
            line-height: 1.3;
            margin-top: 0;
            flex-wrap: wrap;
        }
        .strategy-task-meta-left {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.35rem 0.65rem;
            min-width: 0;
            flex: 1 1 auto;
        }
        .strategy-task-meta-right {
            display: flex;
            align-items: center;
            gap: 0.15rem;
            flex-shrink: 0;
            margin-left: auto;
        }
        .strategy-task-meta-item {
            display: inline-flex;
            align-items: center;
            gap: 0.28rem;
            color: #64748b;
            white-space: nowrap;
        }
        .strategy-task-meta-item.is-overdue {
            color: #dc2626;
        }
        .strategy-task-meta-btn {
            display: inline-flex;
            align-items: center;
            gap: 0.28rem;
            padding: 0;
            border: none;
            background: transparent;
            color: #64748b;
            font-size: inherit;
            line-height: inherit;
            cursor: pointer;
        }
        .strategy-task-meta-btn.is-overdue {
            color: #dc2626;
        }
        .strategy-task-tag-btn {
            padding: 0;
        }
        .strategy-task-tag-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
            max-width: 10rem;
            padding: 0.12rem 0.45rem;
            border-radius: 999px;
            font-size: 0.75rem;
            font-weight: 600;
            line-height: 1.35;
            overflow: hidden;
        }
        .strategy-task-tag-pill-label {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        .strategy-task-tag-empty {
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
            color: #94a3b8;
            text-decoration: underline;
            text-underline-offset: 2px;
            font-weight: 500;
        }
        .strategy-task-action-icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 1.7rem;
            height: 1.7rem;
            padding: 0;
            border: none;
            background: transparent;
            border-radius: 0.35rem;
            color: #64748b;
            cursor: pointer;
            line-height: 0;
        }
        .strategy-task-action-icon:hover {
            background: #f1f5f9;
        }
        .strategy-task-action-icon.is-play {
            color: #7c3aed;
        }
        .strategy-task-action-icon.is-pause {
            color: #dc2626;
        }
        .strategy-task-action-icon.is-move {
            color: #64748b;
        }
        .strategy-task-action-icon.is-delete {
            color: #ef4444;
        }
        .strategy-task-action-icon.is-edit {
            color: #94a3b8;
        }
        .strategy-card-meta .btn,
        .strategy-card-meta .btn-link {
            font-size: inherit !important;
            line-height: inherit;
            font-weight: 400;
            vertical-align: baseline;
            min-height: 1.45rem;
            padding: 0.1rem 0.22rem !important;
        }
        .strategy-card-meta .strategy-card-meta-actions {
            gap: 0.28rem 0.65rem;
        }
        .strategy-card-more-link {
            background: none;
            border: none;
            padding: 0.35rem 0.5rem;
            margin-top: 0.15rem;
            color: #0d6efd;
            text-decoration: underline;
            font-size: 0.875rem;
            cursor: pointer;
            border-radius: 0.25rem;
        }
        .strategy-card-more-link:hover,
        .strategy-card-more-link:focus-visible {
            background-color: #e7f1ff !important;
            color: #084298 !important;
        }
        .strategy-card-footer-bar {
            background: #f8fafc;
            border-top: 1px solid #e5e7eb;
            min-height: 2.5rem;
            padding: 0.3rem 0.5rem 0.3rem 0.65rem;
            display: flex;
            align-items: center;
            gap: 0.4rem;
            position: relative;
            z-index: 15;
            flex-shrink: 0;
        }
        .strategy-card-footer-bar--kr-0 {
            background: #f0fdf4;
            border-top-color: #bbf7d0;
            box-shadow: inset 3px 0 0 #15803d;
        }
        .strategy-card-footer-bar--kr-1 {
            background: #eff6ff;
            border-top-color: #bfdbfe;
            box-shadow: inset 3px 0 0 #1d4ed8;
        }
        .strategy-card-footer-bar--kr-2 {
            background: #fdf2f4;
            border-top-color: #f9d0d7;
            box-shadow: inset 3px 0 0 #722f37;
        }
        .strategy-card-footer-bar--private {
            background: #f5f3ff;
            border-top-color: #ddd6fe;
            box-shadow: inset 3px 0 0 #7c3aed;
        }
        .strategy-kr-accordion .accordion-item {
            border: 1px solid #e5e7eb;
            border-radius: 0.5rem;
            margin-bottom: 0.45rem;
            overflow: hidden;
            background: #fff;
            box-shadow: none;
        }
        .strategy-kr-accordion .accordion-button {
            font-weight: 600;
            font-size: 0.875rem;
            line-height: 1.25;
            min-height: 0;
            padding: 0.38rem 0.7rem 0.38rem 0.65rem;
            color: #1e293b;
            background-color: #fff;
            box-shadow: none !important;
            border: none;
            gap: 0.25rem;
        }
        .strategy-kr-accordion .accordion-button::after {
            width: 0.9rem;
            height: 0.9rem;
            background-size: 0.9rem;
            margin-left: 0.35rem;
        }
        .strategy-kr-accordion .accordion-button:hover {
            background-color: #f8fafc;
        }
        .strategy-kr-accordion .accordion-button:focus {
            border-color: transparent;
            box-shadow: none !important;
        }
        .strategy-kr-accordion .accordion-button:not(.collapsed) {
            background-color: #f8fafc;
            color: #1e293b;
            box-shadow: none !important;
        }
        .strategy-kr-accordion-item--kr-0,
        .strategy-kr-accordion-item--kr-1,
        .strategy-kr-accordion-item--kr-2,
        .strategy-kr-accordion-item--private {
            border-left: 4px solid transparent;
        }
        .strategy-kr-accordion-item--kr-0 {
            border-left-color: #15803d;
        }
        .strategy-kr-accordion-item--kr-1 {
            border-left-color: #1d4ed8;
        }
        .strategy-kr-accordion-item--kr-2 {
            border-left-color: #722f37;
        }
        .strategy-kr-accordion-item--private {
            border-left-color: #7c3aed;
        }
        .strategy-kr-accordion-item--kr-0 .accordion-button {
            background-color: #f0fdf4;
            color: #166534;
        }
        .strategy-kr-accordion-item--kr-0 .accordion-button:hover,
        .strategy-kr-accordion-item--kr-0 .accordion-button:not(.collapsed) {
            background-color: #dcfce7;
            color: #14532d;
        }
        .strategy-kr-accordion-item--kr-0 .accordion-button:focus {
            box-shadow: 0 0 0 0.15rem rgba(21, 128, 61, 0.2) !important;
        }
        .strategy-kr-accordion-item--kr-1 .accordion-button {
            background-color: #eff6ff;
            color: #1e40af;
        }
        .strategy-kr-accordion-item--kr-1 .accordion-button:hover,
        .strategy-kr-accordion-item--kr-1 .accordion-button:not(.collapsed) {
            background-color: #dbeafe;
            color: #1e3a8a;
        }
        .strategy-kr-accordion-item--kr-1 .accordion-button:focus {
            box-shadow: 0 0 0 0.15rem rgba(29, 78, 216, 0.2) !important;
        }
        .strategy-kr-accordion-item--kr-2 .accordion-button {
            background-color: #fdf2f4;
            color: #722f37;
        }
        .strategy-kr-accordion-item--kr-2 .accordion-button:hover,
        .strategy-kr-accordion-item--kr-2 .accordion-button:not(.collapsed) {
            background-color: #fce7eb;
            color: #5c1d2e;
        }
        .strategy-kr-accordion-item--kr-2 .accordion-button:focus {
            box-shadow: 0 0 0 0.15rem rgba(114, 47, 55, 0.2) !important;
        }
        .strategy-kr-accordion-item--private .accordion-button {
            background-color: #f5f3ff;
            color: #6d28d9;
        }
        .strategy-kr-accordion-item--private .accordion-button:hover,
        .strategy-kr-accordion-item--private .accordion-button:not(.collapsed) {
            background-color: #ede9fe;
            color: #5b21b6;
        }
        .strategy-kr-accordion-item--private .accordion-button:focus {
            box-shadow: 0 0 0 0.15rem rgba(124, 58, 237, 0.2) !important;
        }
        .strategy-kr-accordion-item--kr-0 .accordion-button::after {
            filter: invert(28%) sepia(45%) saturate(900%) hue-rotate(100deg) brightness(0.85);
        }
        .strategy-kr-accordion-item--kr-1 .accordion-button::after {
            filter: invert(22%) sepia(90%) saturate(1800%) hue-rotate(215deg) brightness(0.9);
        }
        .strategy-kr-accordion-item--kr-2 .accordion-button::after {
            filter: invert(18%) sepia(35%) saturate(1200%) hue-rotate(320deg) brightness(0.85);
        }
        .strategy-kr-accordion-item--private .accordion-button::after {
            filter: invert(24%) sepia(70%) saturate(2200%) hue-rotate(250deg) brightness(0.9);
        }
        .strategy-kr-accordion .accordion-body {
            background: #f8fafc;
            padding: 0.75rem;
            border-top: 1px solid #e5e7eb;
        }
        .strategy-kr-accordion-header-inner {
            display: flex;
            align-items: center;
            width: 100%;
            gap: 0.25rem;
            padding-right: 0.15rem;
        }
        .strategy-kr-accordion-header-text {
            flex: 1 1 auto;
            min-width: 0;
            text-align: start;
            font-weight: 600;
            letter-spacing: -0.01em;
            line-height: 1.25;
        }
        .strategy-kr-accordion-header-count {
            font-weight: 500;
            font-size: 0.75rem;
            opacity: 0.85;
            background: rgba(15, 23, 42, 0.08);
            border-radius: 999px;
            padding: 0.05rem 0.4rem;
            line-height: 1.2;
        }
        .strategy-kr-new-strategy-btn {
            flex-shrink: 0;
            color: #64748b;
            line-height: 1;
            border: none;
            background: transparent;
            padding: 0.1rem;
            border-radius: 5px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
        }
        .strategy-kr-new-strategy-btn:hover {
            background: rgba(100, 116, 139, 0.12);
            color: #334155;
        }
        .strategy-kr-accordion-item--kr-0 .strategy-kr-new-strategy-btn {
            color: #15803d;
        }
        .strategy-kr-accordion-item--kr-0 .strategy-kr-new-strategy-btn:hover {
            background: rgba(21, 128, 61, 0.12);
            color: #166534;
        }
        .strategy-kr-accordion-item--kr-1 .strategy-kr-new-strategy-btn {
            color: #1d4ed8;
        }
        .strategy-kr-accordion-item--kr-1 .strategy-kr-new-strategy-btn:hover {
            background: rgba(29, 78, 216, 0.12);
            color: #1e40af;
        }
        .strategy-kr-accordion-item--kr-2 .strategy-kr-new-strategy-btn {
            color: #722f37;
        }
        .strategy-kr-accordion-item--kr-2 .strategy-kr-new-strategy-btn:hover {
            background: rgba(114, 47, 55, 0.12);
            color: #5c1d2e;
        }
        .strategy-kr-accordion-item--private .strategy-kr-new-strategy-btn {
            color: #7c3aed;
        }
        .strategy-kr-accordion-item--private .strategy-kr-new-strategy-btn:hover {
            background: rgba(124, 58, 237, 0.12);
            color: #6d28d9;
        }
        .strategy-sin-estrategia-standalone {
            margin-bottom: 1rem;
        }
        .strategy-sin-estrategia-standalone-title {
            font-weight: 700;
            font-size: 1rem;
            margin-bottom: 0.5rem;
            color: #374151;
        }
        .strategy-card-footer-bar > .strategy-card-footer-metrics {
            align-self: center;
        }
        .strategy-card-footer-bar > .strategy-card-add-fab {
            align-self: center;
        }
        .strategy-card-footer-spacer {
            flex: 1 1 auto;
            min-width: 0.75rem;
            min-height: 28px;
            align-self: stretch;
            background: transparent;
            border: none;
            padding: 0 0.45rem;
            cursor: pointer;
            border-radius: 6px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            color: #64748b;
            font-size: 0.72rem;
            font-weight: 600;
            letter-spacing: 0.02em;
            text-transform: uppercase;
        }
        .strategy-card-footer-spacer:hover:not(:disabled) {
            background: rgba(15, 23, 42, 0.05);
        }
        .strategy-card-footer-spacer--inert {
            cursor: default;
            pointer-events: none;
        }
        .strategy-card-footer-metrics {
            display: flex;
            align-items: center;
            gap: 0.65rem;
            min-width: 0;
        }
        .strategy-card-footer-metric {
            display: flex;
            align-items: center;
            gap: 0.3rem;
            color: #334155;
            font-weight: 600;
            font-size: 0.875rem;
            line-height: 1;
        }
        .strategy-card-footer-metric-duration-text {
            font-size: 0.8125rem;
            line-height: 1;
            font-weight: 600;
            color: #1e293b;
        }
        .strategy-card-footer-bar > .strategy-card-footer-cstatus {
            align-self: center;
        }
        .strategy-card-footer-cstatus {
            flex-shrink: 1;
            min-width: 0;
            width: auto;
            max-width: 8.4375rem;
            height: auto;
            min-height: 1.4rem;
            padding: 0.15rem 0.5rem;
            border-radius: 999px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 0.6875rem;
            font-weight: 700;
            line-height: 1.1;
            letter-spacing: 0.01em;
            color: #1f2937;
            border: 1px solid transparent;
            box-shadow: none;
            cursor: pointer;
            font-family: inherit;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .strategy-card-footer-cstatus--alineacion {
            background: #bae6fd;
            border-color: #7dd3fc;
        }
        .strategy-card-footer-cstatus--ejecucion {
            background: #fde047;
            border-color: #facc15;
        }
        .strategy-card-footer-cstatus--terminado {
            background: #bbf7d0;
            border-color: #86efac;
        }
        .strategy-card-footer-cstatus--archivado {
            background: #e5e7eb;
            color: #374151;
            border-color: #d1d5db;
        }
        .strategy-card-footer-cstatus--alineacion:hover,
        .strategy-card-footer-cstatus--ejecucion:hover,
        .strategy-card-footer-cstatus--terminado:hover,
        .strategy-card-footer-cstatus--archivado:hover {
            filter: brightness(0.97);
        }
        .strategy-card-footer-cstatus--unset {
            text-transform: lowercase;
            letter-spacing: 0.02em;
            background: #e2e8f0;
            color: #475569;
            border: 1px solid #cbd5e1;
            box-shadow: none;
        }
        .strategy-card-footer-cstatus--unset:hover {
            background: #cbd5e1;
            color: #334155;
        }
        .strategy-card-footer-cstatus:focus-visible {
            outline: 2px solid #93c5fd;
            outline-offset: 1px;
        }
        .strategy-card-footer-comments {
            flex-shrink: 0;
            align-self: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            padding: 0;
            border: none;
            border-radius: 6px;
            background: transparent;
            color: #64748b;
            cursor: pointer;
        }
        .strategy-card-footer-comments:hover {
            background: rgba(15, 23, 42, 0.06);
            color: #334155;
        }
        .strategy-card-footer-comments--active {
            background: rgba(15, 23, 42, 0.08);
            color: #1e293b;
        }
        .strategy-card-footer-comments-icon {
            display: block;
            stroke: currentColor;
        }
        .strategy-card-add-fab {
            box-sizing: border-box;
            width: 28px;
            height: 28px;
            min-width: 28px;
            max-width: 28px;
            min-height: 28px;
            max-height: 28px;
            aspect-ratio: 1;
            padding: 0;
            margin: 0;
            border-radius: 50%;
            background: #e2e8f0;
            border: 1px solid #cbd5e1;
            color: #334155;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            box-shadow: none;
            flex: 0 0 28px;
            overflow: hidden;
            line-height: 1;
        }
        .strategy-card-add-fab-plus {
            font-size: 1.15rem;
            font-weight: 500;
            line-height: 1;
            color: #334155;
            display: block;
            user-select: none;
            margin: 0;
            padding: 0;
        }
        .strategy-card-add-fab svg {
            display: block;
            stroke: #334155 !important;
            color: #334155 !important;
        }
        .strategy-card-add-fab:hover {
            background: #cbd5e1;
            color: #1e293b;
        }
        .strategy-card-add-fab:hover .strategy-card-add-fab-plus {
            color: #1e293b;
        }
        .strategy-modal-expanded-layout {
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
            min-height: 0;
            width: 100%;
        }
        .strategy-modal-expanded-main {
            position: relative;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
            overflow: hidden;
        }
        .strategy-modal-expanded-main > .modal-inner {
            flex: 1 1 auto;
            min-height: 0;
            overflow-y: auto;
        }
        .strategy-modal-fab-footer {
            flex-shrink: 0;
            margin-top: 0;
            position: relative;
            z-index: 15;
        }
        .strategy-modal-fab-footer .strategy-card-footer-bar {
            border-radius: 0 0 12.5px 12.5px;
        }
        @media (max-width: 576px) {
            .strategy-modal-fab-footer .strategy-card-footer-bar {
                border-radius: 0 0 8px 8px;
            }
            .strategy-modal-card-desc-scroll,
            .strategy-modal-card-desc-textarea {
                max-height: calc((100vh - 10px) / 2);
            }
        }
        .strategy-modal-comments-panel {
            flex-shrink: 0;
            background: #e8eaed;
            border-top: 1px solid #c5cad3;
            padding: 0.6rem 0.7rem 0.55rem;
            max-height: min(42vh, 340px);
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            min-height: 0;
            z-index: 14;
        }
        .strategy-modal-comments-composer {
            display: flex;
            align-items: flex-end;
            gap: 0.35rem;
            background: #fff;
            border: 1px solid #b8c0cc;
            border-radius: 6px;
            padding: 0.35rem 0.4rem 0.35rem 0.55rem;
        }
        .strategy-modal-comments-input {
            flex: 1;
            min-width: 0;
            border: none;
            outline: none;
            font-size: 0.875rem;
            resize: none;
            min-height: 2.35rem;
            max-height: 5.5rem;
            line-height: 1.35;
        }
        .strategy-modal-comments-send {
            flex-shrink: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 2.25rem;
            height: 2.25rem;
            padding: 0;
            border: none;
            border-radius: 6px;
            background: #1e40af;
            color: #fff;
            cursor: pointer;
        }
        .strategy-modal-comments-send:hover:not(:disabled) {
            background: #1d4ed8;
        }
        .strategy-modal-comments-send:disabled {
            opacity: 0.45;
            cursor: not-allowed;
        }
        .strategy-modal-comments-list {
            overflow-y: auto;
            flex: 1;
            min-height: 0;
            display: flex;
            flex-direction: column;
            gap: 0.45rem;
            padding-right: 0.15rem;
        }
        .strategy-modal-comments-list::-webkit-scrollbar {
            width: 8px;
        }
        .strategy-modal-comments-list::-webkit-scrollbar-thumb {
            background: #bfc5ce;
            border-radius: 4px;
        }
        .strategy-modal-comments-item {
            background: #ecfdf5;
            border: 1px solid #0f5132;
            border-radius: 6px;
            padding: 0.45rem 0.5rem;
            display: flex;
            gap: 0.5rem;
            align-items: flex-start;
        }
        .strategy-modal-comments-item-text {
            flex: 1;
            min-width: 0;
            font-size: 0.8125rem;
            color: #374151;
            line-height: 1.4;
            white-space: pre-wrap;
            word-break: break-word;
        }
        .strategy-modal-comments-item-aside {
            flex-shrink: 0;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 0.3rem;
        }
        .strategy-modal-comments-item-aside .avatar-container .profile-pic-circle {
            width: 28px;
            height: 28px;
            min-width: 28px;
            min-height: 28px;
            font-size: 0.62rem;
        }
        .strategy-modal-comments-date {
            font-size: 0.65rem;
            color: #6b7280;
            white-space: nowrap;
            line-height: 1.2;
        }
        .strategy-modal-card-desc {
            width: 100%;
            max-width: 100%;
        }
        .strategy-modal-card-desc-snippet {
            font-size: 0.8125rem;
            font-family: inherit;
            color: #6b7280;
            line-height: 1.4;
            cursor: pointer;
            text-align: left;
            width: 100%;
            margin: 0;
            padding: 0;
            border: none;
            background: transparent;
            box-shadow: none;
            -webkit-appearance: none;
            appearance: none;
            text-decoration: underline;
            text-decoration-color: rgba(107, 114, 128, 0.65);
            text-underline-offset: 0.15em;
        }
        .strategy-modal-card-desc-snippet:hover {
            color: #4b5563;
            background: transparent;
            text-decoration-color: rgba(75, 85, 99, 0.75);
        }
        .strategy-modal-card-desc-snippet:focus {
            outline: none;
            background: transparent;
        }
        .strategy-modal-card-desc-snippet:focus-visible {
            outline: 2px solid #93c5fd;
            outline-offset: 2px;
            background: transparent;
        }
        .strategy-modal-card-desc-snippet:active {
            background: transparent;
            color: #374151;
        }
        /* Mismo alto útil que .modal-content2--full-viewport-height; mitad → tope del texto ampliado */
        .strategy-modal-card-desc-scroll {
            max-height: calc((100vh - 40px) / 2);
            overflow-y: auto;
            min-height: 0;
        }
        .strategy-modal-card-desc-scroll::-webkit-scrollbar {
            width: 8px;
        }
        .strategy-modal-card-desc-scroll::-webkit-scrollbar-thumb {
            background: #cfcfcf;
            border-radius: 4px;
        }
        .strategy-modal-card-desc-textarea {
            max-height: calc((100vh - 40px) / 2);
            min-height: 4.5rem;
            overflow-y: auto;
            resize: none;
        }
        .strategy-modal-card-desc-full {
            font-size: 0.875rem;
            color: #374151;
            line-height: 1.45;
            margin: 0;
            white-space: pre-wrap;
            word-break: break-word;
        }
        .strategy-modal-card-desc-link {
            color: #0060b9;
            text-decoration: underline;
            text-underline-offset: 0.12em;
            word-break: break-all;
        }
        .strategy-modal-card-desc-link:hover {
            color: #004a8f;
        }
        .strategy-modal-card-desc-actions {
            display: flex;
            align-items: center;
            gap: 0.65rem;
            margin-top: 0.35rem;
        }
        .strategy-modal-card-desc-edit-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0.15rem;
            border: none;
            background: transparent;
            color: #2563eb;
            cursor: pointer;
            border-radius: 4px;
        }
        .strategy-modal-card-desc-edit-btn:hover {
            background: rgba(37, 99, 235, 0.08);
        }
        .strategy-mosaic {
            display: grid;
            grid-template-columns: repeat(1, 1fr);
            gap: 1rem;
        }
        @media (min-width: 768px) {
            .strategy-mosaic { grid-template-columns: repeat(2, 1fr); }
        }
        @media (min-width: 1200px) {
            .strategy-mosaic { grid-template-columns: repeat(3, 1fr); }
        }
        .strategy-team-panel {
            position: absolute;
            inset: 0;
            z-index: 12;
            background: #3d4f5f;
            overflow-y: auto;
            overflow-x: hidden;
            padding: 0.55rem 0.65rem 0.65rem;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.22);
            border-radius: 0.2rem 0.2rem 0 0;
            -webkit-overflow-scrolling: touch;
        }
        .strategy-team-panel-row {
            display: flex;
            flex-direction: column;
            gap: 0.45rem;
            background: #fff;
            border: 1px solid #e5e7eb;
            border-radius: 0.65rem;
            padding: 0.65rem 0.75rem;
            margin-bottom: 0.45rem;
        }
        .strategy-team-panel-row:last-child {
            margin-bottom: 0;
        }
        .strategy-team-panel-row.is-completed {
            background: #bbf7d0;
            border-color: #86efac;
        }
        .strategy-team-panel-row--active {
            background: #ffedd5;
            border-color: #fdba74;
            box-shadow: inset 3px 0 0 #f97316;
        }
        .strategy-team-panel .strategy-team-avatar-wrap .profile-pic-circle,
        .strategy-team-panel .strategy-team-avatar-wrap .initials-circle,
        .strategy-card-task-compact .strategy-card-task-avatar .profile-pic-circle,
        .strategy-card-task-compact .strategy-card-task-avatar .initials-circle {
            width: 26px;
            height: 26px;
            font-size: 0.65rem;
        }
        .strategy-team-avatar-btn {
            border: none;
            padding: 0;
            background: transparent;
            border-radius: 50%;
            cursor: pointer;
            line-height: 0;
        }
        .strategy-team-avatar-btn:hover {
            box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.45);
        }
        .strategy-team-avatar-btn:focus-visible {
            outline: 2px solid #86b7fe;
            outline-offset: 2px;
        }
        .new-task-assignee-btn {
            flex-shrink: 0;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            padding: 0;
            border: 1px solid #dee2e6;
            background: #fff;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            cursor: pointer;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
        }
        .new-task-assignee-btn:hover {
            border-color: #adb5bd;
            background: #f8f9fa;
        }
        .new-task-assignee-btn:focus-visible {
            outline: 2px solid #86b7fe;
            outline-offset: 2px;
        }
        .new-task-assignee-btn .avatar-container {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .new-task-assignee-btn .profile-pic-circle,
        .new-task-assignee-btn .initials-circle {
            width: 100%;
            height: 100%;
            font-size: 0.75rem;
        }
        .tactic-new-task-bar {
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            gap: 0.5rem;
            width: 100%;
        }
        .tactic-new-task-input-wrap {
            position: relative;
            flex: 1 1 12rem;
            min-width: 0;
        }
        .tactic-new-task-input-wrap .tactic-new-task-textarea {
            width: 100%;
            padding-right: 2.4rem;
        }
        .tactic-new-task-clear-btn {
            position: absolute;
            top: 0.55rem;
            right: 0.35rem;
            width: 28px;
            height: 28px;
            z-index: 2;
        }
        .tactic-new-task-actions {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 0.4rem;
            flex: 0 0 auto;
            margin-left: auto;
            padding-top: 0.15rem;
        }
        .tactic-new-task-counter {
            font-size: 0.8125rem;
            color: #6c757d;
            line-height: 1;
            white-space: nowrap;
            min-width: 2.75rem;
            text-align: right;
        }
        .tactic-new-task-add-btn {
            box-sizing: border-box;
            display: inline-flex !important;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            min-width: 40px;
            min-height: 40px;
            padding: 0 !important;
            margin: 0;
            line-height: 0;
            border-radius: 50%;
            background-color: #5cb85c;
            border-color: #4cae4c;
        }
        .tactic-new-task-add-btn svg {
            display: block;
            flex-shrink: 0;
            margin: 0;
        }
        .tactic-new-task-add-btn .spinner-border {
            display: block;
            margin: 0;
        }
        @media (max-width: 576px) {
            .tactic-new-task-input-wrap {
                flex: 1 1 100%;
            }
            .tactic-new-task-actions {
                width: 100%;
                margin-left: 0;
                padding-top: 0;
                justify-content: flex-end;
            }
            .tactic-new-task-textarea,
            .tactic-new-task-input-wrap .tactic-new-task-textarea {
                font-size: 1rem;
                min-height: 2.75rem;
                padding-top: 0.55rem;
                padding-bottom: 0.55rem;
            }
            .tactic-new-task-clear-btn {
                top: 0.45rem;
            }
        }
        .strategy-team-panel-duration {
            font-size: 0.75rem;
            font-weight: 600;
            color: #374151;
        }
        .strategy-team-task-open-btn {
            border: 0;
            background: transparent;
            background-color: transparent;
            padding: 0.15rem 0.35rem;
            margin: -0.15rem -0.35rem 0.15rem;
            width: 100%;
            text-align: left;
            cursor: pointer;
            text-decoration: underline;
            text-decoration-thickness: 1px;
            text-underline-offset: 2px;
            border-radius: 0.25rem;
        }
        .strategy-team-task-open-btn:hover,
        .strategy-team-task-open-btn:focus-visible {
            background-color: #e7f1ff !important;
            color: #084298 !important;
        }
        .strategy-team-task-open-btn.text-secondary:hover,
        .strategy-team-task-open-btn.text-secondary:focus-visible {
            background-color: #f1f5f9 !important;
            color: #495057 !important;
        }
        .strategy-team-task-open-btn:focus-visible {
            outline: 2px solid #86b7fe;
            outline-offset: 2px;
        }
        .strategy-team-accordion .accordion-item {
            background: transparent;
            border: 1px solid rgba(255, 255, 255, 0.18);
            margin-bottom: 0.4rem;
            border-radius: 0.35rem !important;
            overflow: hidden;
        }
        .strategy-team-accordion .accordion-button {
            background: rgba(0, 0, 0, 0.22);
            color: #f1f5f9;
            font-size: 0.8rem;
            font-weight: 600;
            padding: 0.45rem 0.65rem;
            box-shadow: none !important;
        }
        .strategy-team-accordion .accordion-button:not(.collapsed) {
            background: rgba(0, 0, 0, 0.32);
            color: #fff;
        }
        .strategy-team-accordion .accordion-button::after {
            filter: invert(1);
        }
        .strategy-team-accordion .accordion-body {
            background: rgba(0, 0, 0, 0.14);
            padding: 0.35rem 0.45rem 0.5rem !important;
        }
        .strategy-team-accordion .accordion-item.strategy-team-accordion-item--week-complete {
            border: 1px solid rgba(74, 222, 128, 0.95);
            box-shadow: 0 0 0 1px rgba(34, 197, 94, 0.25);
        }
        .strategy-team-accordion-item--week-complete .accordion-button {
            border-bottom: 1px solid rgba(74, 222, 128, 0.55) !important;
        }
        .strategy-team-accordion-item--week-complete .accordion-button:not(.collapsed) {
            border-bottom-color: rgba(74, 222, 128, 0.35) !important;
        }
        .strategy-team-panel-filters {
            --stpf-control-h: calc(1.5em + 0.5rem + 2px);
            display: flex;
            flex-direction: column;
            align-items: stretch;
            gap: 0.35rem;
            margin-left: 0;
            padding-left: 0;
        }
        .strategy-team-panel-filters .form-control,
        .strategy-team-panel-filters .form-control:focus {
            background: rgba(255, 255, 255, 0.96);
            border: 1px solid rgba(0, 0, 0, 0.12);
            font-size: 0.875rem;
        }
        .strategy-team-panel-filters .dropdown-menu {
            max-height: min(280px, 45vh);
            overflow-y: auto;
            min-width: 220px;
        }
        .strategy-team-panel-filters-search-row {
            display: flex;
            flex-wrap: nowrap;
            align-items: center;
            gap: 0.5rem;
            width: 100%;
            margin: 0;
        }
        .strategy-team-panel-filters-search-row .strategy-team-panel-search-field {
            flex: 1 1 auto;
            min-width: 0;
            margin: 0;
            display: flex;
            align-items: stretch;
        }
        .strategy-team-panel-filters-search-row .input-group .form-control,
        .strategy-team-panel-filters-search-row .input-group .input-group-text {
            min-height: var(--stpf-control-h);
            height: var(--stpf-control-h);
            padding-top: 0.25rem;
            padding-bottom: 0.25rem;
            line-height: 1.5;
            font-size: 0.875rem;
            box-sizing: border-box;
            display: flex;
            align-items: center;
        }
        .strategy-team-panel-filters-search-row .dropdown {
            display: flex;
            align-items: center;
            flex-shrink: 0;
            margin: 0;
        }
        .strategy-team-panel-filters-search-row .dropdown > .btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 0.35rem;
            min-height: var(--stpf-control-h);
            height: var(--stpf-control-h);
            padding: 0.25rem 0.5rem;
            line-height: 1.5;
            font-size: 0.875rem;
            box-sizing: border-box;
        }
        .strategy-team-panel-filters-search-row .dropdown > .btn::after {
            margin-top: 0;
        }
        .strategy-team-panel-filters-toolbar {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: flex-start;
            align-content: flex-start;
            gap: 0.4rem 0.65rem;
            width: 100%;
            margin: 0;
            padding: 0;
        }
        .strategy-team-panel-filters-toolbar .dropdown > .btn {
            display: inline-flex;
            align-items: center;
        }
        .strategy-team-panel-filter-group {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: var(--stpf-control-h);
            margin: 0;
            padding: 0;
            color: rgba(255, 255, 255, 0.95);
            font-size: 0.8125rem;
        }
        .strategy-team-panel-filter-group .form-check-input {
            width: 1rem;
            height: 1rem;
            margin: 0;
            flex-shrink: 0;
            cursor: pointer;
        }
        .strategy-team-panel-filter-group .form-check-input:disabled {
            cursor: not-allowed;
            opacity: 0.65;
        }
        .strategy-team-panel-filter-group label {
            margin: 0;
            line-height: 1.2;
            cursor: pointer;
            user-select: none;
        }
        .strategy-team-panel-filter-group .form-select {
            margin: 0;
            min-height: calc(1.5em + 0.5rem + 2px);
            font-size: 0.875rem;
            line-height: 1.5;
        }
        @media (max-width: 420px) {
            .strategy-team-panel-filters-search-row {
                flex-wrap: wrap;
            }
            .strategy-team-panel-filters-search-row .strategy-team-panel-search-field {
                flex: 1 1 100%;
            }
        }
        .strategy-team-panel-user-dropdown {
            min-width: 15rem;
            padding-top: 0;
        }
        .strategy-team-panel-user-dropdown .strategy-team-panel-user-search {
            position: sticky;
            top: 0;
            z-index: 1;
            background: #fff;
            padding: 0.5rem 0.65rem 0.4rem;
            border-bottom: 1px solid #e9ecef;
        }
        .strategy-team-panel-user-dropdown .strategy-team-panel-user-list {
            max-height: min(50vh, 260px);
            overflow-y: auto;
        }
        .strategy-team-panel-user-item {
            cursor: pointer;
        }
        .strategy-team-panel-user-item .form-check-input {
            margin-top: 0.35rem;
        }
        .strategy-team-panel-user-item .strategy-team-avatar-wrap .profile-pic-circle,
        .strategy-team-panel-user-item .strategy-team-avatar-wrap .initials-circle {
            width: 28px;
            height: 28px;
            font-size: 0.65rem;
        }
        .new-task-fab-popup-overlay {
            position: fixed;
            inset: 0;
            z-index: 1100;
            background: rgba(0, 0, 0, 0.45);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 1rem;
            box-sizing: border-box;
        }
        .new-task-fab-popup-panel {
            width: 100%;
            max-width: 36rem;
            max-height: min(90vh, 32rem);
            overflow-y: auto;
            background: #fff;
            border-radius: 0.75rem;
            box-shadow: 0 12px 40px rgba(0, 0, 0, 0.22);
            padding: 1rem 1.1rem;
            box-sizing: border-box;
        }
        .new-task-fab-popup-row {
            display: flex;
            align-items: flex-start;
            gap: 0.65rem;
            min-width: 0;
        }
        .new-task-fab-popup-main {
            flex: 1 1 auto;
            min-width: 0;
        }
        .new-task-fab-popup-actions {
            flex: 0 0 auto;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 0.35rem;
        }
        .new-task-fab-popup-actions-top {
            display: flex;
            align-items: center;
            gap: 0.25rem;
        }
        .new-task-fab-popup-strategy-label {
            font-size: 0.8125rem;
            color: #6b7280;
            margin: 0.35rem 0 0;
            line-height: 1.35;
        }
        .tactic-new-task-textarea,
        .new-task-fab-popup-textarea {
            resize: none;
            overflow-y: auto;
            min-height: 3rem;
            max-height: min(8rem, 35vh);
            line-height: 1.5;
            word-break: break-word;
            overflow-wrap: anywhere;
        }
 
    `;

    // --- ESTADO LOCAL ---
    const [tasks, setTasks] = useState([]);
    /** Evita pisar el header con [] antes de la primera carga de tareas. */
    const [tasksHydrated, setTasksHydrated] = useState(false);
    const [nowMs, setNowMs] = useState(() => Date.now());
    const [member_trelloid, setMemberTrelloid] = useState("");
    const [newTaskText, setNewTaskText] = useState('');
    /** Texto de filtro del listado/mosaico (independiente del borrador de alta y del popup FAB). */
    const [taskFilterText, setTaskFilterText] = useState('');
    const [isModalOpen, setModalOpen] = useState(false);
    const [modal_content, setModalContent] = useState('');
    const [selectedTask, setSelectedTask] = useState(null);
    /** Contexto al pulsar + en una tarjeta de estrategia: creación vinculada */
    const [newTaskStrategyContext, setNewTaskStrategyContext] = useState(null);
    /** Contadores globales por estrategia (load_task_list → strategy_bar_stats) */
    const [strategyBarStats, setStrategyBarStats] = useState({});
    /** Preview mosaico: hasta 3 tareas por strategy_key (equipo) + totales. */
    const [strategyTaskPreviews, setStrategyTaskPreviews] = useState({});
    /** Panel de tareas del equipo por estrategia (clave = groupKey); incluye usuario actual y completadas */
    const [teamDetailGroupKey, setTeamDetailGroupKey] = useState(null);
    const [teamDetailTasks, setTeamDetailTasks] = useState([]);
    const [teamDetailLoading, setTeamDetailLoading] = useState(false);
    const [teamDetailRefreshTick, setTeamDetailRefreshTick] = useState(0);
    const [teamDetailQuery, setTeamDetailQuery] = useState(null);
    const mosaicSyncPendingRef = useRef(false);
    const teamDetailTasksRef = useRef([]);
    const patchMosaicStatsRef = useRef(null);
    const [teamPanelSearchText, setTeamPanelSearchText] = useState('');
    const [teamPanelUserFilterSearch, setTeamPanelUserFilterSearch] = useState('');
    const [teamPanelFilterUserIds, setTeamPanelFilterUserIds] = useState([]);
    const [teamPanelFilterStatusKeys, setTeamPanelFilterStatusKeys] = useState([]);
    const [teamPanelGroupBy, setTeamPanelGroupBy] = useState(() => TEAM_PANEL_GROUP_BY.creation);
    /** Modal para cambiar talent_db.cards.status (Mis goals, pie de estrategia). */
    const [cardStatusPicker, setCardStatusPicker] = useState(null);
    /** Modal para reasignar KR (clic en hueco de la barra inferior). */
    const [krReassignModal, setKrReassignModal] = useState(null);
    const [krListRows, setKrListRows] = useState([]);
    const [krListLoading, setKrListLoading] = useState(false);
    const listTopRef = useRef(null);
    const newTaskInputRef = useRef(null);
    const handleAddTaskRef = useRef(null);
    const [isAddingTask, setIsAddingTask] = useState(false);
    const isAddingTaskRef = useRef(false);
    const [newTaskFabPopupOpen, setNewTaskFabPopupOpen] = useState(false);
    const searchText = taskFilterText;
    const personalRunningTasks = useMemo(() => {
        const ownerUsrId = usr_id != null ? Number(usr_id) : null;
        const seen = new Set();
        const running = [];
        for (const t of tasks) {
            if (!t || t.completed || !t.active) continue;
            if (ownerUsrId != null && !Number.isNaN(ownerUsrId)) {
                const taskUsrId = t.usr_id != null && t.usr_id !== '' ? Number(t.usr_id) : null;
                // Si la API no trae usr_id en este endpoint, en modo personal asumimos que son del usuario actual.
                if (taskUsrId != null && !Number.isNaN(taskUsrId) && taskUsrId !== ownerUsrId) continue;
            }
            const title = String(t.task_name ?? t.text ?? '').trim();
            if (!title) continue;
            const key = `${t.id ?? title}`;
            if (seen.has(key)) continue;
            seen.add(key);
            running.push({ key, title });
        }
        return running.slice(0, 8);
    }, [tasks, usr_id]);

    useEffect(() => {
        if (!tasksHydrated || typeof onMyRunningTasksChange !== 'function') return;
        onMyRunningTasksChange(personalRunningTasks);
    }, [personalRunningTasks, tasksHydrated, onMyRunningTasksChange]);

    const PREVIEW_TASKS_PER_STRATEGY_CARD = 3;
    /** Edición inline del título en el mosaico de estrategias (vista compacta). */
    const [mosaicEditTaskId, setMosaicEditTaskId] = useState(null);
    const [mosaicEditText, setMosaicEditText] = useState('');
    const mosaicNavigateTimeoutRef = useRef(null);
    useEffect(() => () => {
        if (mosaicNavigateTimeoutRef.current) clearTimeout(mosaicNavigateTimeoutRef.current);
    }, []);

    const hasActiveRunningTask = useMemo(() => {
        if (tasks.some((t) => isTaskRunning(t))) return true;
        if (teamDetailTasks.some((t) => isTaskRunning(t))) return true;
        for (const entry of Object.values(strategyTaskPreviews)) {
            if (entry?.tasks?.some((t) => isTaskRunning(t))) return true;
        }
        return false;
    }, [tasks, teamDetailTasks, strategyTaskPreviews]);

    useEffect(() => {
        if (!hasActiveRunningTask) return undefined;
        setNowMs(Date.now());
        const id = setInterval(() => setNowMs(Date.now()), 30000);
        return () => clearInterval(id);
    }, [hasActiveRunningTask]);

    /** Asignatario al crear tarea desde la barra superior (por defecto: usuario logueado). */
    const [newTaskAssignUsrId, setNewTaskAssignUsrId] = useState(() => (usr_id != null ? Number(usr_id) : null));
    const [assigneePickerOpen, setAssigneePickerOpen] = useState(false);
    const [assigneePickerMode, setAssigneePickerMode] = useState('new_task');
    const [reassignTaskId, setReassignTaskId] = useState(null);
    const [reassignStrategyGroupKey, setReassignStrategyGroupKey] = useState(null);
    const [assigneeSearch, setAssigneeSearch] = useState('');
    const [assigneeOptions, setAssigneeOptions] = useState([]);

    // Categorías del usuario (task_usrs_categories)
    const [categories, setCategories] = useState([]);
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [categoryEditing, setCategoryEditing] = useState(null); // null = crear, { id, category_name, color } = editar
    const [taskForCategoryModal, setTaskForCategoryModal] = useState(null); // tarea para la que se elige categoría
    const [taskForDueDateModal, setTaskForDueDateModal] = useState(null); // tarea para la que se elige fecha de vencimiento
    const [selectedTaskForStats, setSelectedTaskForStats] = useState(null); // { id, usr_id } para popup Statistics
    const [dueDateCalendarView, setDueDateCalendarView] = useState({ month: new Date().getMonth(), year: new Date().getFullYear() });
    const [dueDateCalendarSelected, setDueDateCalendarSelected] = useState(null); // YYYY-MM-DD o null
    const [dueDateFilterMode, setDueDateFilterMode] = useState(() =>
        parseStoredDueDateFilterMode(readTacticFollowupPrefs(usr_id)?.dueDateFilterMode)
    );
    const [dueDateFilterRange, setDueDateFilterRange] = useState(() =>
        parseStoredDueDateFilterRange(readTacticFollowupPrefs(usr_id)?.dueDateFilterRange)
    );
    const [isDueDatePickerOpen, setIsDueDatePickerOpen] = useState(false);
    const dueDateFilterRef = useRef(null);
    /** Códigos 0–4 de card_status; vacío = sin filtrar por estado de tarjeta. */
    const [cardStatusFilterSelected, setCardStatusFilterSelected] = useState(() =>
        parseStoredCardStatusFilter(readTacticFollowupPrefs(usr_id)?.cardStatusFilterSelected)
    );
    /** Filtrar estrategias con tareas asignadas a estos usr_id (vacío = todas). */
    const [teamAssigneeFilterIds, setTeamAssigneeFilterIds] = useState(() =>
        resolveInitialTeamAssigneeFilter(readTacticFollowupPrefs(usr_id), usr_id)
    );
    /** Catálogo talent_db.krs (id, trello_id, kr_name) para crear estrategias vinculadas al KR. */
    const [krsCatalog, setKrsCatalog] = useState([]);
    /** Tarjetas recién creadas sin tareas: mantienen el mosaico hasta que exista al menos una tarea. */
    const [newlyCreatedStrategyPlaceholders, setNewlyCreatedStrategyPlaceholders] = useState([]);
    const [newStrategyModalSection, setNewStrategyModalSection] = useState(null);
    const [newStrategyTitle, setNewStrategyTitle] = useState('');
    const [newStrategyDesc, setNewStrategyDesc] = useState('');
    const [newStrategySaving, setNewStrategySaving] = useState(false);
    const [movePopupRows, setMovePopupRows] = useState(cards || []);
    const [strategyCanvasMeta, setStrategyCanvasMeta] = useState({});
    /** null = sin filtro; al elegir una etiqueta solo se listan tareas con esa categoría */
    const [tagFilterCategoryId, setTagFilterCategoryId] = useState(() => {
        const p = readTacticFollowupPrefs(usr_id);
        const c = p?.tagFilterCategoryId;
        return c != null && c !== '' ? c : null;
    });
    /** Solo listar tareas no completadas (se combina con el resto de filtros). */
    const [onlyPendingTasks, setOnlyPendingTasks] = useState(() => {
        const p = readTacticFollowupPrefs(usr_id);
        return typeof p?.onlyPendingTasks === 'boolean' ? p.onlyPendingTasks : false;
    });
    /** null = todos los acordeones KR abiertos; string[] = solo esos sectionId expandidos. */
    const [krAccordionOpenIds, setKrAccordionOpenIds] = useState(() =>
        parseKrAccordionOpenIdsFromPrefs(readTacticFollowupPrefs(usr_id))
    );
    const tacticPrefsUsrRef = useRef(usr_id);
    const [categoryFormName, setCategoryFormName] = useState('');
    const [categoryFormColor, setCategoryFormColor] = useState('#86efac');
    const CATEGORY_COLORS = ['#86efac', '#fde047', '#93c5fd', '#f9a8d4', '#a5b4fc', '#fcd34d', '#6b7280'];

    const MAX_CHARS = 90;
    /** Título de estrategia / tarjeta en talent_db.cards (modal ampliado). */
    const STRATEGY_GROUP_TITLE_MAX = 200;
    const CATEGORY_LABEL_MAX = 10;
    const truncateCategoryLabel = (name) => (name && name.length > CATEGORY_LABEL_MAX ? `${name.slice(0, CATEGORY_LABEL_MAX)}...` : name || '');

    const displayCategories = useMemo(
        () => (categories || []).filter((c) => c.category_name !== 'Estrategia' && c.id !== 'estrategia'),
        [categories]
    );

    useEffect(() => {
        setMovePopupRows(cards || []);
    }, [cards]);

    const refreshMovePopupRows = useCallback(async () => {
        if (!token) return;
        const currentCardslist_byKR = await get_current_cards(token);
        if (currentCardslist_byKR === 'INVALID') {
            navigate('/');
            return;
        }
        if (!Array.isArray(currentCardslist_byKR)) {
            setMovePopupRows([]);
            return;
        }
        const nextRows = [];
        currentCardslist_byKR.forEach((element) => {
            nextRows.push({
                isKr: true,
                krid: element.id,
                krname: element.name,
                private_section: element.private_section === true,
            });
            const currentKr = Array.isArray(element.cards)
                ? element.cards.map((celement, index) => ({
                      isKr: false,
                      krid: element.id,
                      kr_trello_id: celement.kr_trello_id ?? null,
                      card_db_id: celement.card_db_id,
                      cardid: celement.cardid,
                      cardname: celement.cardname,
                      shortUrl: celement.shortUrl,
                      members: celement.members,
                      desc: celement.desc,
                      card_status: celement.card_status,
                      estado: celement.estado,
                      private: celement.private === true,
                      index,
                  }))
                : [];
            nextRows.push(...currentKr);
        });
        setMovePopupRows(nextRows);
    }, [token, navigate]);

    const handleLinkStrategy = async (payload) => {
        const isOpportunityPayload = typeof payload === 'object' && payload !== null && 'opportunityId' in payload;
        if (isOpportunityPayload) {
            if (!selectedTask) return;
            const oldTask = resolveTaskForMutation(
                selectedTask.id,
                tasks,
                teamDetailTasks,
                strategyTaskPreviews
            );
            if (!oldTask) {
                alert('No se encontró la tarea.');
                return;
            }
            const tid = Number(selectedTask.id);
            const oppId = payload.opportunityId == null || payload.opportunityId === '' ? null : payload.opportunityId;
            const updatedTask = {
                ...oldTask,
                opportunity_id: oppId,
                trello_card_id: '',
                card_id: null,
            };
            fetch_update_task(updatedTask, { reloadMosaic: true, reloadTeamPanel: false });
            fetch_register_log(updatedTask.id, oppId != null ? 'opportunity' : 'strategy');
            setTasks((prev) => {
                if (!prev.some((t) => Number(t.id) === tid)) return prev;
                return prev.map((t) => (Number(t.id) === tid ? updatedTask : t));
            });
            setTeamDetailTasks((prev) => {
                if (!prev.some((t) => Number(t.id) === tid)) return prev;
                return prev.map((t) =>
                    Number(t.id) === tid
                        ? {
                              ...t,
                              opportunity_id: oppId,
                              trello_card_id: '',
                              card_id: null,
                          }
                        : t
                );
            });
            setStrategyTaskPreviews((prev) =>
                patchTaskInStrategyTaskPreviews(prev, tid, {
                    opportunity_id: oppId,
                    trello_card_id: '',
                    card_id: null,
                })
            );
            setModalOpen(false);
            return;
        }
        const strategy_id = payload;
        if (!selectedTask) return;
        if (strategy_id === undefined || strategy_id === null) {
            alert("Por favor, selecciona una estrategia para vincular.");
            return;
        }
        const oldTask =
            resolveTaskForMutation(
                selectedTask.id,
                tasks,
                teamDetailTasks,
                strategyTaskPreviews
            ) ?? selectedTask;
        const isNoStrategy = strategy_id === 99999999 || strategy_id === '99999999';
        let ensuredCardDbId = null;
        const popupRow = movePopupRows.find(
            (r) => !r.isKr && String(r.cardid) === String(strategy_id)
        );
        const isPrivateTarget = popupRow?.private === true;
        if (!isNoStrategy) {
            const card =
                getCardById(strategy_id) ||
                (popupRow
                    ? {
                          cardid: popupRow.cardid,
                          cardname: popupRow.cardname,
                          shortUrl: popupRow.shortUrl,
                          desc: popupRow.desc,
                          kr_trello_id: popupRow.kr_trello_id,
                      }
                    : null);
            if (card) {
                const cardName = (card.cardname && String(card.cardname).trim()) || card.cardid || 'Sin nombre';
                const ensureResult = await ensure_card(
                    token,
                    card.cardid,
                    cardName,
                    card.shortUrl ?? null,
                    card.desc ?? null,
                    isPrivateTarget ? null : resolveCardKrTrelloId(card),
                    isPrivateTarget ? { private: 1 } : { set_creator: true }
                );
                const isInvalid = ensureResult === 'INVALID' || ensureResult?.error === 'INVALID';
                if (isInvalid) {
                    const reason = ensureResult?.reason;
                    const detail = ensureResult?.detail;
                    const messages = {
                        session_expired: 'La sesión ha expirado. Vuelve a iniciar sesión.',
                        session_invalid: 'Sesión no válida. Vuelve a iniciar sesión.',
                        access_denied: 'No tienes permiso para registrar estrategias.',
                        empty_params: 'Faltan datos de la tarjeta. Inténtalo de nuevo.',
                        insert_failed: 'No se pudo crear la estrategia en la base de datos.',
                        update_failed: 'No se pudo actualizar la estrategia.'
                    };
                    let msg = messages[reason] || 'No se pudo registrar la estrategia. Comprueba tu conexión e inténtalo de nuevo.';
                    if (detail) msg += '\n\nDetalle: ' + detail;
                    alert(msg);
                    return;
                }
                if (ensureResult && ensureResult.id != null && Number(ensureResult.id) > 0) {
                    ensuredCardDbId = Number(ensureResult.id);
                }
            }
        }
        const ok = await handleToggleStrategy(selectedTask.id, strategy_id, ensuredCardDbId, oldTask);
        if (ok) setModalOpen(false);
    };

         useEffect(() => {
             if (!token) return;
             fetch_load_task();
            }, [token, teamAssigneeFilterIds, onlyPendingTasks]);

    useEffect(() => {
        if (tacticPrefsUsrRef.current === usr_id) return;
        tacticPrefsUsrRef.current = usr_id;
        const p = readTacticFollowupPrefs(usr_id);
        setDueDateFilterMode(parseStoredDueDateFilterMode(p?.dueDateFilterMode));
        setDueDateFilterRange(parseStoredDueDateFilterRange(p?.dueDateFilterRange));
        setIsDueDatePickerOpen(false);
        setCardStatusFilterSelected(parseStoredCardStatusFilter(p?.cardStatusFilterSelected));
        setTeamAssigneeFilterIds(resolveInitialTeamAssigneeFilter(p, usr_id));
        const c = p?.tagFilterCategoryId;
        setTagFilterCategoryId(c != null && c !== '' ? c : null);
        setOnlyPendingTasks(typeof p?.onlyPendingTasks === 'boolean' ? p.onlyPendingTasks : false);
        setKrAccordionOpenIds(parseKrAccordionOpenIdsFromPrefs(p));
    }, [usr_id]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (usr_id == null || usr_id === '') return;
        try {
            const payload = {
                v: 1,
                dueDateFilterMode,
                dueDateFilterRange: {
                    startDate:
                        dueDateFilterRange.startDate && !Number.isNaN(dueDateFilterRange.startDate.getTime())
                            ? dueDateFilterRange.startDate.toISOString()
                            : null,
                    endDate:
                        dueDateFilterRange.endDate && !Number.isNaN(dueDateFilterRange.endDate.getTime())
                            ? dueDateFilterRange.endDate.toISOString()
                            : null,
                },
                cardStatusFilterSelected,
                teamAssigneeFilterIds,
                tagFilterCategoryId,
                onlyPendingTasks,
                krAccordionOpenIds,
            };
            window.localStorage.setItem(tacticFollowupPrefsStorageKey(usr_id), JSON.stringify(payload));
        } catch {
            /* quota / private mode */
        }
    }, [
        usr_id,
        dueDateFilterMode,
        dueDateFilterRange,
        cardStatusFilterSelected,
        teamAssigneeFilterIds,
        tagFilterCategoryId,
        onlyPendingTasks,
        krAccordionOpenIds,
    ]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dueDateFilterRef.current && !dueDateFilterRef.current.contains(event.target)) {
                setIsDueDatePickerOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        listKrs(token).then((res) => {
            if (cancelled) return;
            if (res && res.ok === true && Array.isArray(res.krs)) {
                setKrsCatalog(res.krs);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [token]);

    useEffect(() => {
        setNewlyCreatedStrategyPlaceholders((prev) =>
            prev.filter(
                (p) =>
                    !tasks.some(
                        (t) => String(t.trello_card_id ?? '').trim() === String(p.trello_card_id)
                    )
            )
        );
    }, [tasks]);

         useEffect(() => {
             if (token && usr_id) fetch_load_categories();
            }, [token, usr_id]);

    useEffect(() => {
        if (usr_id == null || usr_id === '') return;
        setNewTaskAssignUsrId(Number(usr_id));
    }, [usr_id]);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        (async () => {
            const list = await get_all_members(token);
            if (cancelled) return;
            if (list === 'INVALID') return;
            if (!Array.isArray(list)) return;
            const normalized = list
                .map((row) => {
                    const rawId = row.usr_id;
                    const uid = rawId != null && rawId !== '' ? Number(rawId) : null;
                    const nombre = row.nombre ?? row.Nombre;
                    const displayName =
                        (row.team_usr_name && String(row.team_usr_name).trim()) ||
                        (nombre && String(nombre).trim()) ||
                        (row.usr_name && String(row.usr_name).trim()) ||
                        (row.email && String(row.email).trim()) ||
                        'Usuario';
                    return { usr_id: uid, displayName, email: row.email || '' };
                })
                .filter((x) => x.usr_id != null && !Number.isNaN(x.usr_id));
            const selfNum = usr_id != null ? Number(usr_id) : null;
            const seen = new Set();
            const deduped = [];
            let selfRow = null;
            for (const row of normalized) {
                if (seen.has(row.usr_id)) continue;
                seen.add(row.usr_id);
                if (selfNum != null && !Number.isNaN(selfNum) && row.usr_id === selfNum) {
                    selfRow = row;
                } else {
                    deduped.push(row);
                }
            }
            if (selfNum != null && !Number.isNaN(selfNum)) {
                deduped.unshift(selfRow || { usr_id: selfNum, displayName: 'Tú', email: '' });
            }
            setAssigneeOptions(deduped);
        })();
        return () => {
            cancelled = true;
        };
    }, [token, usr_id]);

    const filteredAssigneeOptions = useMemo(() => {
        const q = assigneeSearch.trim().toLowerCase();
        if (!q) return assigneeOptions;
        return assigneeOptions.filter(
            (o) =>
                (o.displayName && o.displayName.toLowerCase().includes(q)) ||
                (o.email && String(o.email).toLowerCase().includes(q))
        );
    }, [assigneeOptions, assigneeSearch]);

    const newTaskAssigneeLabel = useMemo(() => {
        if (newTaskAssignUsrId == null) return '';
        const found = assigneeOptions.find((o) => o.usr_id === newTaskAssignUsrId);
        return found ? found.displayName : '';
    }, [assigneeOptions, newTaskAssignUsrId]);
    // --- FUNCIONES DE UTILERÍA ---

    const formatDate = useCallback((timestamp) => {
        if (!timestamp) return '';
        
        const date = new Date(timestamp);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);

        const isToday = today.toDateString() === date.toDateString();
        const isYesterday = yesterday.toDateString() === date.toDateString();
        
        if (isToday) return "Hoy";
        if (isYesterday) return "Ayer";

        
        return date.toLocaleDateString("es-ES", { day: '2-digit', month: '2-digit', year: 'numeric' });
    }, []);

    // --- MANEJADORES DE TAREAS ---

    const handleAddTaskImpl = async (textOverride) => {
        const raw = typeof textOverride === 'string' ? textOverride : newTaskText;
        const text = String(raw).trim();
        if (!text || text.length > MAX_CHARS) return;
        if (isAddingTaskRef.current) return;

        isAddingTaskRef.current = true;
        setIsAddingTask(true);
        try {
        const ctx = newTaskStrategyContext;
        const createOptions = {};
        const isPrivateCtx =
            ctx?.isPrivate === true ||
            (ctx?.strategyGroupKey != null &&
                isPrivateStrategyGroup(ctx.strategyGroupKey, groupedTasksForDisplay, strategyCanvasMeta));

        if (ctx?.linkType === 'opportunity' && ctx.opportunityId) {
            createOptions.opportunity_id = ctx.opportunityId;
        } else if (ctx?.linkType === 'trello' && ctx.trelloCardId != null && String(ctx.trelloCardId).trim() !== '') {
            const tid = ctx.trelloCardId;
            const isNoStrategy = tid === 99999999 || tid === '99999999';
            if (!isNoStrategy) {
                const tidStr = String(tid).trim();
                const card = getCardById(tid);
                const canvasMeta =
                    ctx.strategyGroupKey != null && strategyCanvasMeta != null
                        ? strategyCanvasMeta[ctx.strategyGroupKey]
                        : null;
                if (!card && process.env.NODE_ENV !== 'production') {
                    console.warn(
                        '[Tacticfollowup] Añadir tarea: la estrategia no está en la lista Trello en memoria; se usa talent_db via ensure_card.',
                        { trelloCardId: tidStr, strategyGroupKey: ctx.strategyGroupKey, canvasMeta }
                    );
                }
                const cardName = card
                    ? (card.cardname && String(card.cardname).trim()) || card.cardid || 'Sin nombre'
                    : String(ctx.displayLabel || ctx.strategyGroupKey || '').trim() || 'Estrategia';
                const shortUrl = card?.shortUrl ?? null;
                const descFromCanvas =
                    canvasMeta?.card_descript != null && String(canvasMeta.card_descript).trim() !== ''
                        ? String(canvasMeta.card_descript)
                        : null;
                const descForEnsure = card?.desc ?? descFromCanvas;
                const krFromCanvas =
                    canvasMeta?.kr_trello_id != null && String(canvasMeta.kr_trello_id).trim() !== ''
                        ? String(canvasMeta.kr_trello_id).trim()
                        : null;
                const krFromCtx =
                    ctx?.krTrelloIdHint != null && String(ctx.krTrelloIdHint).trim() !== ''
                        ? String(ctx.krTrelloIdHint).trim()
                        : null;
                const krFromCard = resolveCardKrTrelloId(card);
                const existingCardDbId =
                    ctx?.strategyGroupKey != null
                        ? getCardDbIdForStrategyGroup(ctx.strategyGroupKey, groupedTasksForDisplay, ctx, strategyCanvasMeta)
                        : null;
                const ensureOptions = isPrivateCtx ? { private: 1 } : { set_creator: true };
                if (existingCardDbId != null && Number(existingCardDbId) > 0) {
                    createOptions.card_id = Number(existingCardDbId);
                    if (isPrivateCtx) {
                        createOptions.private = 1;
                    }
                    const krForUpdate =
                        !isPrivateCtx && !krFromCanvas ? (krFromCtx ?? krFromCard ?? null) : null;
                    const ensureResult = await ensure_card(
                        token,
                        card ? card.cardid : tidStr,
                        cardName,
                        shortUrl,
                        descForEnsure,
                        krForUpdate,
                        ensureOptions
                    );
                    const isInvalid = ensureResult === 'INVALID' || ensureResult?.error === 'INVALID';
                    if (isInvalid) {
                        const reason = ensureResult?.reason;
                        const detail = ensureResult?.detail;
                        const messages = {
                            session_expired: 'La sesión ha expirado. Vuelve a iniciar sesión.',
                            session_invalid: 'Sesión no válida. Vuelve a iniciar sesión.',
                            access_denied: 'No tienes permiso para registrar estrategias.',
                            empty_params: 'Faltan datos de la tarjeta. Inténtalo de nuevo.',
                            insert_failed: 'No se pudo crear la estrategia en la base de datos.',
                            update_failed: 'No se pudo actualizar la estrategia.'
                        };
                        let msg = messages[reason] || 'No se pudo registrar la estrategia. Comprueba tu conexión e inténtalo de nuevo.';
                        if (detail) msg += '\n\nDetalle: ' + detail;
                        alert(msg);
                        return;
                    }
                } else {
                    // Alta/primer vínculo de tarjeta: prioriza KR de BD/canvas para no moverla por metadata temporal.
                    const krForEnsure = isPrivateCtx ? null : (krFromCanvas ?? krFromCtx ?? krFromCard);
                    const ensureResult = await ensure_card(
                        token,
                        card ? card.cardid : tidStr,
                        cardName,
                        shortUrl,
                        descForEnsure,
                        krForEnsure,
                        ensureOptions
                    );
                    const isInvalid = ensureResult === 'INVALID' || ensureResult?.error === 'INVALID';
                    if (isInvalid) {
                        const reason = ensureResult?.reason;
                        const detail = ensureResult?.detail;
                        const messages = {
                            session_expired: 'La sesión ha expirado. Vuelve a iniciar sesión.',
                            session_invalid: 'Sesión no válida. Vuelve a iniciar sesión.',
                            access_denied: 'No tienes permiso para registrar estrategias.',
                            empty_params: 'Faltan datos de la tarjeta. Inténtalo de nuevo.',
                            insert_failed: 'No se pudo crear la estrategia en la base de datos.',
                            update_failed: 'No se pudo actualizar la estrategia.'
                        };
                        let msg = messages[reason] || 'No se pudo registrar la estrategia. Comprueba tu conexión e inténtalo de nuevo.';
                        if (detail) msg += '\n\nDetalle: ' + detail;
                        alert(msg);
                        return;
                    }
                    if (ensureResult && ensureResult.id != null && Number(ensureResult.id) > 0) {
                        createOptions.card_id = Number(ensureResult.id);
                    }
                }
            }
            createOptions.trello_card_id = String(tid).trim();
        }

        if (isPrivateCtx) {
            createOptions.private = 1;
        }

        const assignId = isPrivateCtx
            ? Number(usr_id)
            : newTaskAssignUsrId != null && !Number.isNaN(Number(newTaskAssignUsrId))
              ? Number(newTaskAssignUsrId)
              : Number(usr_id);
        if (!isPrivateCtx) {
            createOptions.assignee_usr_id = assignId;
        }
        const newtaskAnswer = await create_new_task(token, text, createOptions);
        if (newtaskAnswer == null || newtaskAnswer === 'INVALID') {
            navigate("/");
            return;
        }
        if (newtaskAnswer === 'ERROR') {
            alert('No se pudo crear la tarea. Comprueba el asignatario e inténtalo de nuevo.');
            return;
        }
        await fetch_load_task();
        setNewTaskText('');
        setNewTaskStrategyContext(null);
        setNewTaskFabPopupOpen(false);
        if (usr_id != null) setNewTaskAssignUsrId(Number(usr_id));
        } finally {
            isAddingTaskRef.current = false;
            setIsAddingTask(false);
        }
    };
    handleAddTaskRef.current = handleAddTaskImpl;
    const handleAddTask = useCallback((textOverride) => handleAddTaskRef.current(textOverride), []);

    const syncNewTaskTextareaHeights = useCallback(() => {
        const apply = (el) => {
            if (!el) return;
            el.style.height = 'auto';
            const maxH = parseFloat(getComputedStyle(el).maxHeight);
            const cap = Number.isFinite(maxH) && maxH > 0 ? maxH : el.scrollHeight;
            el.style.height = `${Math.min(el.scrollHeight, cap)}px`;
        };
        apply(newTaskInputRef.current);
    }, []);

    useLayoutEffect(() => {
        syncNewTaskTextareaHeights();
    }, [newTaskText, syncNewTaskTextareaHeights]);

    const handleTextTask = (taskId, textTask) => {
        const oldTask = resolveTaskForMutation(taskId, tasks, teamDetailTasks, strategyTaskPreviews);
        if (!oldTask) {
            alert('No se encontró la tarea.');
            return;
        }
        const tid = Number(taskId);
        const updatedTask = {
            ...oldTask,
            text: textTask,
            task_name: textTask,
        };
        persistTaskUpdate(updatedTask, { reloadMosaic: false, reloadTeamPanel: false });
        fetch_register_log(updatedTask.id, 'text_updated');
        setTasks((prevTasks) => {
            if (!prevTasks.some((task) => Number(task.id) === tid)) return prevTasks;
            return prevTasks.map((task) => (Number(task.id) === tid ? updatedTask : task));
        });
        setTeamDetailTasks((prev) => {
            if (!prev.some((t) => Number(t.id) === tid)) return prev;
            return prev.map((t) =>
                Number(t.id) === tid
                    ? {
                          ...t,
                          task_name: textTask,
                      }
                    : t
            );
        });
        setStrategyTaskPreviews((prev) =>
            patchTaskInStrategyTaskPreviews(prev, tid, {
                text: textTask,
                task_name: textTask,
            })
        );
    };

  const handleToggleTask = (taskId, currentStatus) => {
    const newCompletedStatus = !isTaskFlagTrue(currentStatus);
    const tid = Number(taskId);

    const oldTask = resolveTaskForMutation(taskId, tasks, teamDetailTasks, strategyTaskPreviews);

    if (!oldTask) {
        alert('No se encontró la tarea.');
        return;
    }

    const wasActive = isTaskFlagTrue(oldTask.active) && !isTaskFlagTrue(oldTask.completed);
    const now = Date.now();
    let minutesDelta = 0;
    let updatedTask = {
        ...oldTask,
        completed: newCompletedStatus,
        ...(newCompletedStatus === true ? { active: false } : {}),
    };
    if (newCompletedStatus && wasActive) {
        const liveMinutes = getLiveActiveMinutes(oldTask, now);
        const prevMinutes = Number(oldTask.Tiempo_Activo_Total_Minutos_Enteros) || 0;
        minutesDelta = liveMinutes - prevMinutes;
        updatedTask = {
            ...updatedTask,
            active: false,
            Tiempo_Activo_Total_Minutos_Enteros: liveMinutes,
            active_session_start: null,
            durationSyncedAtMs: now,
        };
    }

    fetch_update_task(updatedTask, { reloadMosaic: false, reloadTeamPanel: false });

    const mutationGroupKey = teamDetailGroupKey || expandedStrategyKey;
    if (mutationGroupKey) {
        const completedDelta = newCompletedStatus ? 1 : -1;
        let activeDelta = 0;
        if (newCompletedStatus && wasActive) activeDelta = -1;
        patchMosaicStatsRef.current?.(mutationGroupKey, {
            activeDelta,
            completedDelta,
            minutesDelta: newCompletedStatus && wasActive ? minutesDelta : 0,
        });
    }

    if (newCompletedStatus) {
        const cardId = updatedTask.trello_card_id;
        const noCard = cardId == null || cardId === '' || cardId === 99999999 || cardId === '99999999';
        if (noCard) {
            fetch_register_log(updatedTask.id, "completed", "", updatedTask.text);
        } else {
            const card = getCardById(cardId);
            fetch_register_log(updatedTask.id, "completed", card ? JSON.stringify(card.members) : "", updatedTask.text);
        }
    } else {
        fetch_register_log(updatedTask.id, "reopened");
    }

    setTasks((prevTasks) => {
        if (!prevTasks.some((t) => Number(t.id) === tid)) return prevTasks;
        return prevTasks.map((task) => (Number(task.id) === tid ? updatedTask : task));
    });

    setTeamDetailTasks((prev) => {
        if (!prev.some((t) => Number(t.id) === tid)) return prev;
        return prev.map((t) =>
            Number(t.id) === tid
                ? {
                      ...t,
                      completed: newCompletedStatus,
                      active: newCompletedStatus ? false : t.active,
                      ...(newCompletedStatus && wasActive
                          ? {
                                Tiempo_Activo_Total_Minutos_Enteros:
                                    updatedTask.Tiempo_Activo_Total_Minutos_Enteros,
                                active_session_start: null,
                                durationSyncedAtMs: updatedTask.durationSyncedAtMs,
                            }
                          : {}),
                  }
                : t
        );
    });

    setStrategyTaskPreviews((prev) => {
        let touched = false;
        const next = {};
        for (const [sk, entry] of Object.entries(prev)) {
            if (!entry || !Array.isArray(entry.tasks)) {
                next[sk] = entry;
                continue;
            }
            const mapped = entry.tasks.map((t) => {
                if (Number(t.id) !== tid) return t;
                touched = true;
                return {
                    ...t,
                    completed: newCompletedStatus,
                    active: newCompletedStatus ? false : t.active,
                    ...(newCompletedStatus && wasActive
                        ? {
                              Tiempo_Activo_Total_Minutos_Enteros:
                                  updatedTask.Tiempo_Activo_Total_Minutos_Enteros,
                              active_session_start: null,
                              durationSyncedAtMs: updatedTask.durationSyncedAtMs,
                          }
                        : {}),
                };
            });
            next[sk] = touched ? { ...entry, tasks: mapped } : entry;
        }
        return touched ? next : prev;
    });
};

/**
 * Busca y devuelve una única tarjeta del array 'cards' basada en su 'cardid'.
 * @param {Array<Object>} cards - El array de objetos tarjeta.
 * @param {string} targetCardId - El cardid de la tarjeta que se desea encontrar.
 * @returns {Object|undefined} La tarjeta encontrada o 'undefined' si no se encuentra.
 */
const getCardById = (targetCardId) => {
    // Usamos el método find() para buscar la primera tarjeta cuyo cardid
    // coincida con el targetCardId.
    return cards.find(card => card.cardid === targetCardId);
};

/** Obtiene trello_id del KR evitando usar el id numérico interno (krs.id). */
const resolveCardKrTrelloId = (card) => {
    if (!card || card.isKr) return null;
    const direct = card.kr_trello_id != null ? String(card.kr_trello_id).trim() : '';
    if (direct !== '') return direct;
    const legacy = card.krid != null ? String(card.krid).trim() : '';
    if (legacy === '' || /^\d+$/.test(legacy)) return null;
    return legacy;
};



     async function fetch_register_log(taskId, log, additional_info="", task_name="") {
              const newtaskAnswer = await task_log(token, taskId, log, additional_info, task_name);
      
              if (newtaskAnswer==null){
                navigate("/");
              }else {
            
              }
    };

    async function fetch_delete_task(taskId) {
              const newtaskAnswer = await delete_task(token, taskId);
              if (newtaskAnswer==null){
                navigate("/");
              }else {
                fetch_load_task();
                setTeamDetailRefreshTick((x) => x + 1);
              }
    };

    async function persistTaskUpdate(currentTask, { reloadMosaic = false, reloadTeamPanel = false } = {}) {
              const taskDate = await update_task(token, currentTask.text, 
                                                currentTask.completed, currentTask.id, 
                                                currentTask.active, currentTask.trello_card_id,
                                                currentTask.opportunity_id ?? '',
                                                currentTask.card_id);
              if (taskDate == null || taskDate === 'INVALID') {
                navigate("/");
                return false;
              }
              if (taskDate === 'ERROR') {
                return false;
              }
              if (typeof taskDate === 'string' && /<(b|br)|warning|fatal error/i.test(taskDate)) {
                return false;
              }
              if (reloadMosaic) {
                fetch_load_task();
              } else {
                mosaicSyncPendingRef.current = true;
              }
              if (reloadTeamPanel) {
                setTeamDetailRefreshTick((x) => x + 1);
              }
              return true;
    };

    /** Alias conservado para llamadas que aún esperan recarga completa. */
    async function fetch_update_task(currentTask, options) {
        const reloadMosaic = options?.reloadMosaic !== false;
        const reloadTeamPanel = options?.reloadTeamPanel !== false;
        return persistTaskUpdate(currentTask, { reloadMosaic, reloadTeamPanel });
    }

    const syncMosaicIfPending = useCallback(() => {
        if (mosaicSyncPendingRef.current) {
            mosaicSyncPendingRef.current = false;
            fetch_load_task();
        }
    }, [token, usr_id, teamAssigneeFilterIds, onlyPendingTasks]);

    

 
       



    async function fetch_load_task() {
              const task_list = await load_task_list(
                  token,
                  usr_id,
                  false,
                  teamAssigneeFilterIds.length > 0 ? teamAssigneeFilterIds : null,
                  onlyPendingTasks
              );
              if (task_list==null){
                navigate("/");
              }else {
                  if(task_list!=null){
                    setMemberTrelloid(task_list['member_id']);
                    const listSyncedAt = Date.now();
                    const tempCH = task_list['task_cards'].map((celement, index) => {
                      const cardId = celement.trello_card_id;
                      const oppId = celement.opportunity_id;
                      const hasCard = cardId != null && String(cardId).trim() !== '';
                      const isIsolated = hasCard && (cardId === '99999999' || Number(cardId) === 99999999);
                      const hasOpp = oppId != null && oppId !== '' && Number(oppId) > 0;
                      const isActive = celement.active == 1 || celement.active === true;

                      let strategyLabel;
                      if (hasCard && !isIsolated) {
                        strategyLabel = (celement.title && celement.title !== '') ? celement.title : cardId;
                      } else if (hasOpp) {
                        strategyLabel = celement.opportunity_name || null;
                      } else {
                        strategyLabel = null;
                      }
                    return {
                      id: celement.id,
                      trello_card_id: celement.trello_card_id,
                      card_id:
                          celement.card_id != null && celement.card_id !== '' && Number(celement.card_id) > 0
                              ? Number(celement.card_id)
                              : null,
                      opportunity_id: hasOpp ? celement.opportunity_id : null,
                      opportunity_name: celement.opportunity_name ?? null,
                      strategy: strategyLabel,
                      text: celement.task_name,
                      completed: isTaskFlagTrue(celement.completed),
                      createdAt: celement.creation_date,
                      active: isActive,
                      Tiempo_Activo_Total_Minutos_Enteros: celement.Tiempo_Activo_Total_Minutos_Enteros,
                      active_session_start:
                          isActive && celement.active_session_start
                              ? celement.active_session_start
                              : null,
                      durationSyncedAtMs: listSyncedAt,
                      category: celement.category ?? null,
                      category_name: celement.category_name ?? null,
                      category_color: celement.category_color ?? null,
                      usr_id:
                          celement.usr_id != null && celement.usr_id !== '' && Number.isFinite(Number(celement.usr_id))
                              ? Number(celement.usr_id)
                              : null,
                      due_date: celement.due_date ?? null,
                      card_status: celement.card_status != null && celement.card_status !== '' ? Number(celement.card_status) : 0,
                      private: celement.private == 1 || celement.private === true,
                      card_private: celement.card_private != null && Number(celement.card_private) === 1,
                      kr_id: celement.kr_id !== null && celement.kr_id !== undefined && String(celement.kr_id).trim() !== ''
                          ? Number(celement.kr_id)
                          : null,
                      kr_name: celement.kr_name ?? null,
                      card_descript:
                          celement.card_descript != null && celement.card_descript !== undefined
                              ? String(celement.card_descript)
                              : '',
                      index:index
                    };
                  });
                    setTasks(tempCH);
                    setTasksHydrated(true);
                    const rawStats = task_list.strategy_bar_stats;
                    const rawStatsObj =
                        rawStats != null && typeof rawStats === 'object' && !Array.isArray(rawStats) ? rawStats : {};
                    setStrategyBarStats((prevStats) => {
                        const merged = {};
                        for (const sk of Object.keys(rawStatsObj)) {
                            const raw = rawStatsObj[sk];
                            if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
                                const assignee_ids = Array.isArray(raw.assignee_ids)
                                    ? [
                                          ...new Set(
                                              raw.assignee_ids
                                                  .map((x) => Number(x))
                                                  .filter((x) => Number.isFinite(x) && x > 0)
                                          ),
                                      ].sort((a, b) => a - b)
                                    : [];
                                merged[sk] = {
                                    ...raw,
                                    user_count: Number(raw.user_count) || 0,
                                    active_count: Number(raw.active_count) || 0,
                                    total_active_minutes: Number(raw.total_active_minutes) || 0,
                                    assignee_ids,
                                };
                            }
                        }
                        for (const k of Object.keys(prevStats)) {
                            if (k.startsWith('trello:local-') && merged[k] === undefined) {
                                merged[k] = prevStats[k];
                            }
                        }
                        return merged;
                    });
                    const rawPreviews = task_list.strategy_task_previews;
                    const previewMap = {};
                    if (rawPreviews != null && typeof rawPreviews === 'object' && !Array.isArray(rawPreviews)) {
                        const previewSyncedAt = Date.now();
                        for (const sk of Object.keys(rawPreviews)) {
                            const entry = rawPreviews[sk];
                            if (entry == null || typeof entry !== 'object') continue;
                            const rawTasks = Array.isArray(entry.tasks) ? entry.tasks : [];
                            const tasks = rawTasks
                                .map((r) => previewRowToMosaicTask(stampDurationSync(r, previewSyncedAt)))
                                .filter((t) => t != null);
                            previewMap[sk] = {
                                total: Number(entry.total) || 0,
                                completed_total: Number(entry.completed_total) || 0,
                                tasks,
                            };
                        }
                    }
                    // Enriquecer preview con tareas propias (misma clave card-first que el mosaico).
                    // Evita (0/1) cuando el bucket del servidor no se resuelve o llega incompleto.
                    {
                        const previewSyncedAt = Date.now();
                        for (const own of tempCH) {
                            if (!own || own.id == null) continue;
                            const tid =
                                own.trello_card_id != null && String(own.trello_card_id).trim() !== ''
                                    ? String(own.trello_card_id).trim()
                                    : '';
                            const isIsolated = tid === '99999999' || Number(tid) === 99999999;
                            let sk = 'none';
                            if (tid && !isIsolated) sk = `trello:${tid}`;
                            else if (own.card_id != null && Number(own.card_id) > 0) sk = `trello:${Number(own.card_id)}`;
                            else if (own.opportunity_id != null && Number(own.opportunity_id) > 0) {
                                sk = `opportunity:${Number(own.opportunity_id)}`;
                            }
                            if (!previewMap[sk]) {
                                previewMap[sk] = { total: 0, completed_total: 0, tasks: [] };
                            }
                            const entry = previewMap[sk];
                            const oid = Number(own.id);
                            const exists = (entry.tasks || []).some((t) => Number(t.id) === oid);
                            if (!exists) {
                                const mosaicOwn = stampDurationSync(
                                    {
                                        id: own.id,
                                        text: own.text,
                                        task_name: own.text,
                                        completed: own.completed,
                                        active: own.active,
                                        due_date: own.due_date,
                                        category: own.category,
                                        category_name: own.category_name,
                                        category_color: own.category_color,
                                        trello_card_id: own.trello_card_id,
                                        card_id: own.card_id,
                                        opportunity_id: own.opportunity_id,
                                        Tiempo_Activo_Total_Minutos_Enteros: own.Tiempo_Activo_Total_Minutos_Enteros,
                                        active_session_start: own.active_session_start,
                                        usr_id: own.usr_id,
                                        email_id: own.email_id || '',
                                    },
                                    previewSyncedAt
                                );
                                entry.tasks.push(mosaicOwn);
                            }
                            entry.total = Math.max(Number(entry.total) || 0, entry.tasks.length);
                            entry.completed_total = Math.max(
                                Number(entry.completed_total) || 0,
                                entry.tasks.filter((t) => isTaskFlagTrue(t.completed)).length
                            );
                        }
                        for (const sk of Object.keys(previewMap)) {
                            const entry = previewMap[sk];
                            if (!entry || !Array.isArray(entry.tasks)) continue;
                            entry.tasks = sortMosaicPreviewTasksRunningFirst(entry.tasks).slice(
                                0,
                                PREVIEW_TASKS_PER_STRATEGY_CARD
                            );
                        }
                    }
                    setStrategyTaskPreviews(previewMap);
                    const rawCanvas = task_list.strategies_canvas;
                    if (Array.isArray(rawCanvas) && rawCanvas.length > 0) {
                        const meta = {};
                        for (const row of rawCanvas) {
                            const gk = row.group_key;
                            if (gk == null || String(gk).trim() === '') continue;
                            const kid = row.kr_id;
                            const krTrelloRaw = row.kr_trello_id;
                            const dbPk =
                                row.card_db_id != null && row.card_db_id !== '' && Number(row.card_db_id) > 0
                                    ? Number(row.card_db_id)
                                    : null;
                            meta[String(gk).trim()] = {
                                trello_card_id: row.trello_card_id != null ? String(row.trello_card_id).trim() : '',
                                card_db_id: dbPk,
                                card_status: row.card_status != null && row.card_status !== '' ? Number(row.card_status) : 0,
                                kr_id:
                                    kid !== null && kid !== undefined && String(kid).trim() !== ''
                                        ? Number(kid)
                                        : null,
                                kr_name: row.kr_name ?? null,
                                kr_trello_id:
                                    krTrelloRaw != null && String(krTrelloRaw).trim() !== ''
                                        ? String(krTrelloRaw).trim()
                                        : null,
                                stats_key: row.stats_key != null ? String(row.stats_key).trim() : '',
                                card_descript:
                                    row.card_descript != null && row.card_descript !== undefined
                                        ? String(row.card_descript)
                                        : '',
                                opportunity_name:
                                    row.opportunity_name != null && row.opportunity_name !== undefined
                                        ? String(row.opportunity_name)
                                        : '',
                                private: row.private === true || row.private === 1 || row.private === '1',
                                creator_usr_id:
                                    row.creator_usr_id != null &&
                                    row.creator_usr_id !== '' &&
                                    Number.isFinite(Number(row.creator_usr_id))
                                        ? Number(row.creator_usr_id)
                                        : null,
                            };
                        }
                        setStrategyCanvasMeta((prev) => {
                            const merged = { ...meta };
                            for (const key of Object.keys(prev)) {
                                const tid = prev[key]?.trello_card_id;
                                if (tid != null && String(tid).startsWith('local-') && merged[key] === undefined) {
                                    merged[key] = prev[key];
                                }
                            }
                            return merged;
                        });
                    } else {
                        setStrategyCanvasMeta((prev) => {
                            const next = {};
                            for (const key of Object.keys(prev)) {
                                const tid = prev[key]?.trello_card_id;
                                if (tid != null && String(tid).startsWith('local-')) {
                                    next[key] = prev[key];
                                }
                            }
                            return next;
                        });
                    }
                }

    
              }
    };

    async function fetch_load_categories() {
        const list = await list_task_categories(token, usr_id);
        if (list != null && Array.isArray(list)) {
            setCategories(list);
        } else {
            console.warn('[Tacticfollowup] categorías no son un array:', list);
        }
    }

    const openCategoryModal = (editCategory = null) => {
        if (editCategory && editCategory.isSystem) return;
        setCategoryEditing(editCategory);
        setCategoryFormName(editCategory ? editCategory.category_name : '');
        setCategoryFormColor(editCategory && editCategory.color ? editCategory.color : '#86efac');
        setCategoryModalOpen(true);
    };

    const closeCategoryModal = () => {
        setCategoryModalOpen(false);
        setCategoryEditing(null);
        setCategoryFormName('');
        setCategoryFormColor('#86efac');
    };

    const handleSaveCategory = async () => {
        if (categoryEditing && categoryEditing.isSystem) return;
        const name = categoryFormName.trim();
        if (!name) return;
        if (categoryEditing) {
            const res = await update_task_category(token, categoryEditing.id, name, categoryFormColor);
            if (res && res.status === 'OK') {
                fetch_load_categories();
                closeCategoryModal();
            } else {
                alert(res?.message || 'No se pudo actualizar la categoría.');
            }
        } else {
            const res = await create_task_category(token, name, categoryFormColor);
            if (res && res.status === 'OK') {
                fetch_load_categories();
                closeCategoryModal();
            } else {
                alert(res?.message || 'No se pudo crear la categoría.');
            }
        }
    };

    const handleDeleteCategory = async () => {
        if (!categoryEditing || !categoryEditing.id || categoryEditing.isSystem) return;
        if (!window.confirm('¿Eliminar esta categoría?')) return;
        const deletedId = categoryEditing.id;
        const res = await delete_task_category(token, deletedId);
        if (res && res.status === 'OK') {
            if (tagFilterCategoryId != null && String(deletedId) === String(tagFilterCategoryId)) {
                setTagFilterCategoryId(null);
            }
            fetch_load_categories();
            closeCategoryModal();
        } else {
            alert(res?.message || 'No se pudo eliminar la categoría.');
        }
    };

    const openTaskCategoryModal = (task) => {
        setTaskForCategoryModal(task);
    };

    const closeTaskCategoryModal = () => {
        setTaskForCategoryModal(null);
    };

    const openDueDateModal = (task) => {
        setTaskForDueDateModal(task);
        const hasValidDueDate = task.due_date && typeof task.due_date === 'string' && task.due_date.length >= 10;
        const parsed = hasValidDueDate ? new Date(task.due_date) : null;
        const isValid = parsed && !isNaN(parsed.getTime());
        const d = isValid ? parsed : new Date();
        setDueDateCalendarView({ month: d.getMonth(), year: d.getFullYear() });
        setDueDateCalendarSelected(isValid ? task.due_date : null);
    };

    const closeDueDateModal = () => {
        setTaskForDueDateModal(null);
    };

    const handleApplyDueDate = async () => {
        if (!taskForDueDateModal) return;
        const value = dueDateCalendarSelected || '';
        const res = await update_task_due_date(token, taskForDueDateModal.id, value);
        if (res && res.ok) {
            const tid = Number(taskForDueDateModal.id);
            setTasks(prev => prev.map(t => t.id === taskForDueDateModal.id ? { ...t, due_date: value || null } : t));
            setTeamDetailTasks((prev) => {
                if (!prev.some((t) => Number(t.id) === tid)) return prev;
                return prev.map((t) =>
                    Number(t.id) === tid ? { ...t, due_date: value || null } : t
                );
            });
            setStrategyTaskPreviews((prev) =>
                patchTaskInStrategyTaskPreviews(prev, tid, { due_date: value || null })
            );
            mosaicSyncPendingRef.current = true;
        }
        closeDueDateModal();
    };

    const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    const DAY_HEADERS = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'];
    const dueDateCalendarTodayStr = (() => {
        const t = new Date();
        return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    })();
    const dueDateCalendarGrid = useMemo(() => {
        let { month, year } = dueDateCalendarView;
        const now = new Date();
        if (typeof month !== 'number' || isNaN(month) || month < 0 || month > 11) month = now.getMonth();
        if (typeof year !== 'number' || isNaN(year) || year < 1970 || year > 2100) year = now.getFullYear();
        const first = new Date(year, month, 1);
        const firstDay = first.getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < firstDay; i++) cells.push({ type: 'empty' });
        for (let d = 1; d <= daysInMonth; d++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            cells.push({ type: 'day', date: dateStr, day: d });
        }
        while (cells.length < 42) cells.push({ type: 'empty' });
        return cells;
    }, [dueDateCalendarView]);

    const handleSelectTaskCategory = async (task, category) => {
        const res = await update_task_category_link(token, task.id, category == null ? null : category.id);
        if (res && res.status === 'OK') {
            const tid = Number(task.id);
            const categoryPatch = getTaskCategoryPatch(category);
            setTasks((prev) => {
                if (!prev.some((t) => Number(t.id) === tid)) return prev;
                return prev.map((t) => (Number(t.id) === tid ? { ...t, ...categoryPatch } : t));
            });
            setTeamDetailTasks((prev) => {
                if (!prev.some((t) => Number(t.id) === tid)) return prev;
                return prev.map((t) => (Number(t.id) === tid ? { ...t, ...categoryPatch } : t));
            });
            setStrategyTaskPreviews((prev) => patchTaskInStrategyTaskPreviews(prev, tid, categoryPatch));
            closeTaskCategoryModal();
        } else {
            alert(res?.message || 'No se pudo vincular la categoría.');
        }
    };
     

    const handleDeleteTask = (taskId) => {
        const tid = Number(taskId);
        setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
        setTeamDetailTasks((prev) => prev.filter((t) => Number(t.id) !== tid));
        fetch_delete_task(taskId);
        if (selectedTask && selectedTask.id === taskId) {
            setModalOpen(false);
            setSelectedTask(null);
        }
    };

        // Nueva función para manejar la prioridad única
    const handleTogglePriority = async (taskId, currentStatus) => {
            const newActiveStatus = !isTaskFlagTrue(currentStatus);
            const tid = Number(taskId);

            const oldTask = resolveTaskForMutation(taskId, tasks, teamDetailTasks, strategyTaskPreviews);

            if (!oldTask) return;
            if (isTaskFlagTrue(oldTask.completed)) return;

            const now = Date.now();
            let minutesDelta = 0;
            let updatedTask;
            if (newActiveStatus) {
                updatedTask = {
                    ...oldTask,
                    active: true,
                    active_session_start: new Date(now).toISOString(),
                    durationSyncedAtMs: now,
                };
            } else {
                const liveMinutes = getLiveActiveMinutes(oldTask, now);
                const prevMinutes = Number(oldTask.Tiempo_Activo_Total_Minutos_Enteros) || 0;
                minutesDelta = liveMinutes - prevMinutes;
                updatedTask = {
                    ...oldTask,
                    active: false,
                    Tiempo_Activo_Total_Minutos_Enteros: liveMinutes,
                    active_session_start: null,
                    durationSyncedAtMs: now,
                };
            }

            const durationPatch = {
                active: newActiveStatus,
                Tiempo_Activo_Total_Minutos_Enteros: updatedTask.Tiempo_Activo_Total_Minutos_Enteros,
                active_session_start: updatedTask.active_session_start,
                durationSyncedAtMs: updatedTask.durationSyncedAtMs,
            };

            setTasks((prevTasks) => {
                if (!prevTasks.some((t) => Number(t.id) === tid)) return prevTasks;
                return prevTasks.map((task) => (Number(task.id) === tid ? { ...task, ...durationPatch } : task));
            });

            setTeamDetailTasks((prev) => {
                if (!prev.some((t) => Number(t.id) === tid)) return prev;
                return prev.map((t) =>
                    Number(t.id) === tid ? { ...t, ...durationPatch } : t
                );
            });

            setStrategyTaskPreviews((prev) => {
                let touched = false;
                const next = {};
                for (const [sk, entry] of Object.entries(prev)) {
                    if (!entry || !Array.isArray(entry.tasks)) {
                        next[sk] = entry;
                        continue;
                    }
                    const mapped = entry.tasks.map((t) => {
                        if (Number(t.id) !== tid) return t;
                        touched = true;
                        return { ...t, ...durationPatch };
                    });
                    next[sk] = touched ? { ...entry, tasks: mapped } : entry;
                }
                return touched ? next : prev;
            });

            setNowMs(now);

            const mutationGroupKey = teamDetailGroupKey || expandedStrategyKey;
            if (mutationGroupKey) {
                const activeDelta = newActiveStatus ? 1 : -1;
                patchMosaicStatsRef.current?.(mutationGroupKey, {
                    activeDelta,
                    minutesDelta: newActiveStatus ? 0 : minutesDelta,
                });
            }

            const ok = await persistTaskUpdate(updatedTask, { reloadMosaic: false, reloadTeamPanel: false });
            if (!ok) return;
            await fetch_register_log(updatedTask.id, newActiveStatus ? 'activated' : 'deactivated');

            // Al pausar, refrescar minutos desde el servidor (incluye la sesión recién cerrada).
            if (!newActiveStatus) {
                mosaicSyncPendingRef.current = false;
                await fetch_load_task();
                setTeamDetailRefreshTick((x) => x + 1);
            }
    };

    const handleToggleStrategy = async (taskId, strategy_card_trello_id, cardDbId = null, taskFallback = null) => {
            const newCardTrello = strategy_card_trello_id;
            const oldTask =
                resolveTaskForMutation(
                    taskId,
                    tasks,
                    teamDetailTasks,
                    strategyTaskPreviews
                ) ?? taskFallback;
            if (!oldTask) {
                alert('No se encontró la tarea.');
                return false;
            }
            const tid = Number(taskId);
            const isNoCard = newCardTrello === 99999999 || newCardTrello === '99999999';
            const trelloVal = isNoCard ? '' : String(newCardTrello).trim();
            const cardIdVal = isNoCard ? null : cardDbId != null && cardDbId > 0 ? cardDbId : null;
            const updatedTask = {
                ...oldTask,
                trello_card_id: trelloVal,
                card_id: cardIdVal,
                opportunity_id: null,
                ...(isNoCard ? { strategy: null, opportunity_name: null } : {}),
            };
            const ok = await persistTaskUpdate(updatedTask, { reloadMosaic: true, reloadTeamPanel: true });
            if (!ok) {
                alert('No se pudo actualizar el vínculo de la tarea. Inténtalo de nuevo.');
                return false;
            }
            fetch_register_log(updatedTask.id, 'strategy');
            setTasks((prevTasks) => {
                if (!prevTasks.some((t) => Number(t.id) === tid)) return prevTasks;
                return prevTasks.map((task) => (Number(task.id) === tid ? updatedTask : task));
            });
            setTeamDetailTasks((prev) => {
                if (!prev.some((t) => Number(t.id) === tid)) return prev;
                return prev.map((t) =>
                    Number(t.id) === tid
                        ? {
                              ...t,
                              trello_card_id: trelloVal,
                              card_id: cardIdVal,
                              opportunity_id: null,
                              ...(isNoCard ? { strategy: null, opportunity_name: null } : {}),
                          }
                        : t
                );
            });
            setStrategyTaskPreviews((prev) =>
                patchTaskInStrategyTaskPreviews(prev, tid, {
                    trello_card_id: trelloVal,
                    card_id: cardIdVal,
                    opportunity_id: null,
                    ...(isNoCard ? { strategy: null, opportunity_name: null } : {}),
                })
            );
            return true;
    };

    //  --- FILTRADOR DE TAREAS PROPIAS ----

    const filtered_tasks = useMemo(() => {
        let result = tasks;

        if (searchText) {
            const term = searchText.toLowerCase().trim();
            if (term) {
                result = result.filter((r) => {
                    const hay = (v) => String(v ?? '').toLowerCase().includes(term);
                    if (hay(r.text)) return true;
                    if (hay(r.strategy)) return true;
                    if (hay(r.opportunity_name)) return true;
                    if (hay(r.kr_name)) return true;
                    return false;
                });
            }
        }

        if (tagFilterCategoryId != null) {
            result = result.filter((t) => String(t.category ?? '') === String(tagFilterCategoryId));
        }

        if (onlyPendingTasks) {
            result = result.filter((t) => !t.completed);
        }

        if (dueDateFilterMode === 'no_due_date') {
            result = result.filter((t) => !hasTaskDueDate(t));
        } else if (
            dueDateFilterMode === 'range' &&
            dueDateFilterRange.startDate &&
            dueDateFilterRange.endDate
        ) {
            const startDate = toLocalMidnight(dueDateFilterRange.startDate);
            const endDate = toLocalMidnight(dueDateFilterRange.endDate);
            if (startDate && endDate) {
                result = result.filter((t) => {
                    const dueDate = parseDueDateOnlyString(t.due_date);
                    if (!dueDate) return false;
                    return dueDate >= startDate && dueDate <= endDate;
                });
            }
        }

        return result;
    }, [searchText, tasks, tagFilterCategoryId, onlyPendingTasks, dueDateFilterMode, dueDateFilterRange]);

    const clearDueDateFilter = useCallback((event) => {
        if (event) event.stopPropagation();
        setDueDateFilterMode('none');
        setDueDateFilterRange({ startDate: null, endDate: null, key: 'selection' });
        setIsDueDatePickerOpen(false);
    }, []);

    const handleDueDateFilterRangeChange = useCallback((item) => {
        const selection = item?.selection;
        if (!selection) return;
        const startDate = selection.startDate ? toLocalMidnight(selection.startDate) : null;
        const endDate = selection.endDate ? toLocalMidnight(selection.endDate) : null;
        // react-date-range emite onChange con ambas fechas null al interactuar con el panel;
        // eso no debe pisar el modo «sin fecha» ni vaciar un filtro activo sin intención.
        if (!startDate && !endDate) {
            return;
        }
        setDueDateFilterMode('range');
        setDueDateFilterRange({
            startDate,
            endDate,
            key: 'selection',
        });
        if (
            startDate &&
            endDate &&
            startDate.getTime() !== endDate.getTime()
        ) {
            setIsDueDatePickerOpen(false);
        }
    }, []);

    const handleDueDateFilterNoDate = useCallback(() => {
        setDueDateFilterMode('no_due_date');
        setDueDateFilterRange({ startDate: null, endDate: null, key: 'selection' });
        setIsDueDatePickerOpen(false);
    }, []);

    const dueDateFilterDisplayText = useMemo(() => {
        if (dueDateFilterMode === 'no_due_date') return 'Sin fecha de vencimiento';
        if (
            dueDateFilterMode === 'range' &&
            dueDateFilterRange.startDate &&
            dueDateFilterRange.endDate
        ) {
            return `${dueDateFilterRange.startDate.toLocaleDateString('es-ES')} - ${dueDateFilterRange.endDate.toLocaleDateString('es-ES')}`;
        }
        return 'Vencimiento...';
    }, [dueDateFilterMode, dueDateFilterRange]);

    const dueDateFilterInputWidth = useMemo(() => {
        const minChars = 14;
        const displayChars = (dueDateFilterDisplayText || '').length;
        return `${Math.max(minChars, displayChars + 1)}ch`;
    }, [dueDateFilterDisplayText]);

    const toggleTagFilter = useCallback((categoryId) => {
        setTagFilterCategoryId((prev) =>
            prev != null && String(prev) === String(categoryId) ? null : categoryId
        );
    }, []);

    const selectedCardStatusOptions = useMemo(
        () => CARD_STATUS_FILTER_OPTIONS.filter((opt) => cardStatusFilterSelected.includes(opt.value)),
        [cardStatusFilterSelected]
    );

    const handleCardStatusFilterChange = useCallback((selectedOptions) => {
        const values = Array.isArray(selectedOptions)
            ? selectedOptions
                  .map((opt) => Number(opt?.value))
                  .filter((n) => !Number.isNaN(n) && ALLOWED_CARD_STATUS_FILTER_VALUES.has(n))
            : [];
        setCardStatusFilterSelected((prev) => {
            const next = [...new Set(values)].sort((a, b) => a - b);
            if (prev.length === next.length && prev.every((v, idx) => v === next[idx])) return prev;
            return next;
        });
    }, []);

    const teamAssigneeFilterOptions = useMemo(
        () =>
            assigneeOptions.map((o) => ({
                value: Number(o.usr_id),
                usr_id: Number(o.usr_id),
                label: o.displayName || `Usuario ${o.usr_id}`,
                email: o.email || '',
            })),
        [assigneeOptions]
    );

    const selectedTeamAssigneeFilterOptions = useMemo(
        () => teamAssigneeFilterOptions.filter((opt) => teamAssigneeFilterIds.includes(Number(opt.value))),
        [teamAssigneeFilterIds, teamAssigneeFilterOptions]
    );

    const handleTeamAssigneeFilterChange = useCallback((selectedOptions) => {
        const nextIds = Array.isArray(selectedOptions)
            ? selectedOptions
                  .map((opt) => Number(opt?.value))
                  .filter((uid) => Number.isFinite(uid) && uid > 0)
            : [];
        setTeamAssigneeFilterIds([...new Set(nextIds)].sort((a, b) => a - b));
    }, []);

    const TeamAssigneeOption = (props) => {
        const { data } = props;
        return (
            <components.Option {...props}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <UserAvatar usr_id={data.usr_id} usr_name={data.label} />
                    <span>{data.label}</span>
                </div>
            </components.Option>
        );
    };

    const TeamAssigneeMultiValueLabel = (props) => {
        const { data } = props;
        return (
            <components.MultiValueLabel {...props}>
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <UserAvatar usr_id={data.usr_id} usr_name={data.label} size={20} />
                </span>
            </components.MultiValueLabel>
        );
    };

    const handleSaveNewStrategyFromKr = useCallback(async () => {
        const title = newStrategyTitle.trim();
        const section = newStrategyModalSection;
        if (!title || !section) return;
        const isPrivateSection = section.sectionId === 'special:privadas' || section.isPrivate === true;
        const krRow = !isPrivateSection && section.krId != null
            ? krsCatalog.find((k) => Number(k.id) === Number(section.krId))
            : null;
        const krTrello = krRow?.trello_id != null ? String(krRow.trello_id).trim() : '';
        if (!isPrivateSection && !krTrello) {
            window.alert(
                'Este KR no tiene trello_id registrado en la base de datos; no se puede vincular la tarjeta nueva.'
            );
            return;
        }
        const tid = generateLocalTrelloCardId();
        setNewStrategySaving(true);
        const res = await ensure_card(
            token,
            tid,
            title,
            null,
            newStrategyDesc.trim() || null,
            isPrivateSection ? null : krTrello,
            isPrivateSection ? { private: 1 } : { set_creator: true }
        );
        setNewStrategySaving(false);
        if (res === 'INVALID') {
            navigate('/');
            return;
        }
        if (
            res == null ||
            typeof res !== 'object' ||
            res.error === 'INVALID' ||
            !Number.isFinite(Number(res.id)) ||
            Number(res.id) <= 0
        ) {
            const reason = typeof res === 'object' ? res?.reason : null;
            window.alert(
                reason === 'insert_failed'
                    ? 'No se pudo crear la tarjeta en la base de datos.'
                    : reason === 'empty_params'
                      ? 'Indica un título.'
                      : 'No se pudo crear la estrategia.'
            );
            return;
        }
        const resolvedCreatorId =
            res.creator_usr_id != null &&
            res.creator_usr_id !== '' &&
            Number.isFinite(Number(res.creator_usr_id)) &&
            Number(res.creator_usr_id) > 0
                ? Number(res.creator_usr_id)
                : usr_id != null && Number.isFinite(Number(usr_id))
                  ? Number(usr_id)
                  : null;
        const descTrim = newStrategyDesc.trim();
        setStrategyCanvasMeta((prev) => ({
            ...prev,
            [title]: {
                trello_card_id: tid,
                card_db_id: Number(res.id),
                card_status: 0,
                kr_id: isPrivateSection ? null : section.krId,
                kr_name: isPrivateSection ? null : section.title,
                kr_trello_id: isPrivateSection ? null : (krTrello !== '' ? krTrello : null),
                stats_key: `trello:${tid}`,
                card_descript: descTrim,
                private: isPrivateSection,
                creator_usr_id: resolvedCreatorId,
            },
        }));
        setStrategyBarStats((prev) => ({
            ...prev,
            [`trello:${tid}`]: { user_count: 0, active_count: 0, total_active_minutes: 0, assignee_ids: [] },
        }));
        setNewlyCreatedStrategyPlaceholders((prev) => [
            ...prev,
            {
                groupKey: title,
                trello_card_id: tid,
                kr_id: isPrivateSection ? null : section.krId,
                kr_name: isPrivateSection ? null : section.title,
                private: isPrivateSection,
            },
        ]);
        setNewStrategyModalSection(null);
        setNewStrategyTitle('');
        setNewStrategyDesc('');
        fetch_load_task();
        refreshMovePopupRows();
    }, [newStrategyTitle, newStrategyDesc, newStrategyModalSection, krsCatalog, token, navigate, refreshMovePopupRows, usr_id]);
   

    const handleOpenModal = (task) => {
        const resolved =
            resolveTaskForMutation(task?.id, tasks, teamDetailTasks, strategyTaskPreviews) ??
            mosaicTaskToUpdateTask(task) ??
            task;
        setSelectedTask(resolved);
        setModalContent("Card_link_2task");
        setModalOpen(true);
    };

    // --- LÓGICA DE AGRUPAMIENTO POR CATEGORÍA/ESTRATEGIA ---
    const toDueDate = useCallback((d) => (d ? new Date(d) : null), []);
    const groupedTasks = useMemo(() => {
        const sortedTasks = [...filtered_tasks].sort((a, b) => {
            const da = toDueDate(a.due_date);
            const db = toDueDate(b.due_date);
            if (!da && !db) return (b.createdAt || 0) - (a.createdAt || 0);
            if (!da) return 1;
            if (!db) return -1;
            return da - db;
        });
        const grouped = sortedTasks.reduce((acc, task) => {
            const groupKey = getTaskStrategyGroupKey(task, strategyCanvasMeta);
            if (!acc[groupKey]) {
                acc[groupKey] = [];
            }
            acc[groupKey].push(task);
            return acc;
        }, {});
        Object.keys(grouped).forEach((key) => {
            grouped[key].sort((a, b) => {
                const da = toDueDate(a.due_date);
                const db = toDueDate(b.due_date);
                if (!da && !db) return 0;
                if (!da) return 1;
                if (!db) return -1;
                return da - db;
            });
        });
        return grouped;
    }, [filtered_tasks, toDueDate, strategyCanvasMeta]);

    /** Mosaico: si hay contexto «+ en tarjeta», esa estrategia sigue visible aunque el filtro por texto no coincida con ninguna tarea (cuerpo vacío). */
    const groupedTasksForDisplay = useMemo(() => {
        const g = { ...groupedTasks };
        const term = searchText.trim().toLowerCase();
        const hasSearch = term.length > 0;

        const pinKey = newTaskStrategyContext?.strategyGroupKey;
        if (pinKey != null && String(pinKey).length > 0 && g[pinKey] === undefined) {
            g[pinKey] = [];
        }
        if (strategyCanvasMeta && typeof strategyCanvasMeta === 'object') {
            for (const ck of Object.keys(strategyCanvasMeta)) {
                if (g[ck] !== undefined) continue;
                if (hasSearch) {
                    if (String(ck).toLowerCase().includes(term)) {
                        g[ck] = [];
                    }
                } else {
                    g[ck] = [];
                }
            }
        }
        for (const ph of newlyCreatedStrategyPlaceholders) {
            const gk = ph.groupKey;
            if (gk == null || String(gk).trim() === '' || g[gk] !== undefined) continue;
            if (hasSearch) {
                if (String(gk).toLowerCase().includes(term)) {
                    g[gk] = [];
                }
            } else {
                g[gk] = [];
            }
        }
        return g;
    }, [
        groupedTasks,
        newTaskStrategyContext?.strategyGroupKey,
        strategyCanvasMeta,
        newlyCreatedStrategyPlaceholders,
        searchText,
    ]);

    const sortedGroupKeys = useMemo(() => {
        let keys = Object.keys(groupedTasksForDisplay);
        if (cardStatusFilterSelected.length > 0) {
            const allowed = new Set(cardStatusFilterSelected.map(Number));
            keys = keys.filter((k) => {
                // Sin tarjeta Trello asociada al estado: no aplicar filtro de estado.
                if (k === 'Sin estrategia' || getOpportunityGroupId(k) != null) return true;
                const code = getStrategyGroupCardStatusCode(
                    k,
                    groupedTasksForDisplay,
                    newTaskStrategyContext,
                    strategyCanvasMeta
                );
                if (code === null) return false;
                return allowed.has(code);
            });
        }
        const strictMosaicList =
            onlyPendingTasks || teamAssigneeFilterIds.length > 0 || tagFilterCategoryId != null;
        if (strictMosaicList) {
            const searchTerm = String(searchText ?? '')
                .toLowerCase()
                .trim();
            const pinKey = newTaskStrategyContext?.strategyGroupKey;
            keys = keys.filter((k) => {
                const preview = resolveStrategyMosaicPreview(
                    k,
                    groupedTasksForDisplay,
                    strategyTaskPreviews,
                    teamAssigneeFilterIds,
                    onlyPendingTasks,
                    newTaskStrategyContext,
                    strategyCanvasMeta,
                    PREVIEW_TASKS_PER_STRATEGY_CARD,
                    tagFilterCategoryId,
                    usr_id,
                    searchText
                );
                if (preview.hasListableTasks) return true;
                if ((groupedTasksForDisplay[k] || []).length > 0) return true;
                if (searchTerm) {
                    if (pinKey != null && String(pinKey) === String(k)) return true;
                    return String(k).toLowerCase().includes(searchTerm);
                }
                return (
                    isCreatorOwnedCanvas(strategyCanvasMeta?.[k], usr_id) &&
                    preview.displayTotal === 0 &&
                    !isCreatorOwnedPrivateCanvas(strategyCanvasMeta?.[k], usr_id)
                );
            });
        }
        const sinEstrategia = 'Sin estrategia';
        const cmpName = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' });
        const isOpportunityKey = (k) => getOpportunityGroupId(k) != null;
        const isPrivateKey = (k) => isPrivateStrategyGroup(k, groupedTasksForDisplay, strategyCanvasMeta);
        const isSpecial = (k) => k === sinEstrategia || isOpportunityKey(k) || isPrivateKey(k);
        const krSortRank = (k) => {
            const { krId } = getKrMetaForStrategySection(k, groupedTasksForDisplay, strategyCanvasMeta);
            return krId != null && Number.isFinite(Number(krId)) ? Number(krId) : Number.POSITIVE_INFINITY;
        };
        const regular = keys.filter((k) => !isSpecial(k)).sort((a, b) => {
            const ra = krSortRank(a);
            const rb = krSortRank(b);
            if (ra !== rb) return ra - rb;
            return cmpName(a, b);
        });
        const oppKeys = keys
            .filter((k) => isOpportunityKey(k))
            .sort((a, b) => {
                const ta = getOpportunityGroupTitle(a, groupedTasksForDisplay, strategyCanvasMeta);
                const tb = getOpportunityGroupTitle(b, groupedTasksForDisplay, strategyCanvasMeta);
                const tcmp = cmpName(ta, tb);
                if (tcmp !== 0) return tcmp;
                return Number(getOpportunityGroupId(a)) - Number(getOpportunityGroupId(b));
            });
        const tail = [];
        tail.push(...oppKeys);
        if (keys.includes(sinEstrategia)) tail.push(sinEstrategia);
        return [...regular, ...tail];
    }, [
        groupedTasksForDisplay,
        cardStatusFilterSelected,
        teamAssigneeFilterIds,
        onlyPendingTasks,
        tagFilterCategoryId,
        strategyTaskPreviews,
        strategyBarStats,
        newTaskStrategyContext,
        strategyCanvasMeta,
        usr_id,
        searchText,
    ]);

    const privateSectionGroupKeys = useMemo(() => {
        const viewerId = usr_id != null ? Number(usr_id) : NaN;
        if (
            teamAssigneeFilterIds.length > 0 &&
            Number.isFinite(viewerId) &&
            !teamAssigneeFilterIds.some((x) => Number(x) === viewerId)
        ) {
            return [];
        }
        const keySet = new Set();
        for (const k of Object.keys(groupedTasksForDisplay)) {
            if (isPrivateStrategyGroup(k, groupedTasksForDisplay, strategyCanvasMeta)) {
                keySet.add(k);
            }
        }
        let keys = [...keySet];
        if (cardStatusFilterSelected.length > 0) {
            const allowed = new Set(cardStatusFilterSelected.map(Number));
            keys = keys.filter((k) => {
                const code = getStrategyGroupCardStatusCode(
                    k,
                    groupedTasksForDisplay,
                    newTaskStrategyContext,
                    strategyCanvasMeta
                );
                if (code === null) return false;
                return allowed.has(code);
            });
        }
        const strictMosaicList =
            onlyPendingTasks || teamAssigneeFilterIds.length > 0 || tagFilterCategoryId != null;
        if (strictMosaicList) {
            const searchTerm = String(searchText ?? '')
                .toLowerCase()
                .trim();
            const pinKey = newTaskStrategyContext?.strategyGroupKey;
            keys = keys.filter((k) => {
                const preview = resolveStrategyMosaicPreview(
                    k,
                    groupedTasksForDisplay,
                    strategyTaskPreviews,
                    teamAssigneeFilterIds,
                    onlyPendingTasks,
                    newTaskStrategyContext,
                    strategyCanvasMeta,
                    PREVIEW_TASKS_PER_STRATEGY_CARD,
                    tagFilterCategoryId,
                    usr_id,
                    searchText
                );
                if (preview.hasListableTasks) return true;
                if ((groupedTasksForDisplay[k] || []).length > 0) return true;
                if (searchTerm) {
                    if (pinKey != null && String(pinKey) === String(k)) return true;
                    return String(k).toLowerCase().includes(searchTerm);
                }
                return (
                    isCreatorOwnedPrivateCanvas(strategyCanvasMeta?.[k], usr_id) &&
                    preview.displayTotal === 0
                );
            });
        }
        return keys.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }, [
        groupedTasksForDisplay,
        cardStatusFilterSelected,
        strategyCanvasMeta,
        newTaskStrategyContext,
        usr_id,
        teamAssigneeFilterIds,
        onlyPendingTasks,
        tagFilterCategoryId,
        strategyTaskPreviews,
        searchText,
    ]);

    /** Acordeones por KR (título = kr_name); «Oportunidades» primero si existe grupo; «Sin estrategia» va aparte arriba. */
    const strategyKrSections = useMemo(() => {
        const sinEstrategia = 'Sin estrategia';
        const buckets = new Map();
        const opportunityGroupKeys = [];

        for (const groupKey of sortedGroupKeys) {
            if (groupKey === sinEstrategia) continue;
            if (isPrivateStrategyGroup(groupKey, groupedTasksForDisplay, strategyCanvasMeta)) continue;
            if (getOpportunityGroupId(groupKey) != null) {
                opportunityGroupKeys.push(groupKey);
                continue;
            }
            const { krId, krName } = getKrMetaForStrategySection(groupKey, groupedTasksForDisplay, strategyCanvasMeta);
            const bucketKey = krId !== null ? `kr:${krId}` : 'kr:none';
            if (!buckets.has(bucketKey)) {
                buckets.set(bucketKey, {
                    krId,
                    krName: krName || 'Sin vínculo KR',
                    groupKeys: [],
                });
            }
            buckets.get(bucketKey).groupKeys.push(groupKey);
        }

        for (const b of buckets.values()) {
            b.groupKeys.sort((a, c) => a.localeCompare(c, undefined, { sensitivity: 'base' }));
        }

        const sortedBucketKeys = [...buckets.keys()].sort((a, b) => {
            const na = a === 'kr:none' ? Number.POSITIVE_INFINITY : Number(a.replace(/^kr:/, ''));
            const nb = b === 'kr:none' ? Number.POSITIVE_INFINITY : Number(b.replace(/^kr:/, ''));
            if (na !== nb) return na - nb;
            return a.localeCompare(b);
        });

        const sections = sortedBucketKeys.map((bk) => {
            const b = buckets.get(bk);
            const groupKeys = b.groupKeys;
            const hasRunningTask = groupKeys.some((gk) =>
                (groupedTasksForDisplay[gk] || []).some((t) => t.active)
            );
            return {
                sectionId: bk,
                title: b.krName,
                krId: b.krId,
                groupKeys,
                cardCount: groupKeys.length,
                hasRunningTask,
            };
        });

        if (opportunityGroupKeys.length > 0) {
            sections.unshift({
                sectionId: 'special:oportunidades',
                title: 'Oportunidades',
                krId: null,
                groupKeys: opportunityGroupKeys,
                cardCount: opportunityGroupKeys.length,
                hasRunningTask: opportunityGroupKeys.some((gk) =>
                    (groupedTasksForDisplay[gk] || []).some((t) => t.active)
                ),
            });
        }

        if (!searchText.trim() || privateSectionGroupKeys.length > 0) {
            sections.unshift({
                sectionId: 'special:privadas',
                title: PRIVATE_SECTION_TITLE,
                krId: null,
                isPrivate: true,
                groupKeys: privateSectionGroupKeys,
                cardCount: privateSectionGroupKeys.length,
                hasRunningTask: privateSectionGroupKeys.some((gk) =>
                    getOwnTasksForStrategyGroup(gk, groupedTasksForDisplay, strategyCanvasMeta).some(
                        (t) => t.active
                    )
                ),
            });
        }

        return sections;
    }, [sortedGroupKeys, groupedTasksForDisplay, strategyCanvasMeta, privateSectionGroupKeys, usr_id, searchText]);

    const krAccordionActiveKeys = useMemo(() => {
        if (!strategyKrSections.length) return [];
        if (krAccordionOpenIds === null) {
            return strategyKrSections.map((_, i) => String(i));
        }
        const openSet = new Set(krAccordionOpenIds);
        return strategyKrSections
            .map((s, i) => ({ s, i }))
            .filter(({ s }) => openSet.has(s.sectionId))
            .map(({ i }) => String(i));
    }, [strategyKrSections, krAccordionOpenIds]);

    const handleKrAccordionSelect = useCallback(
        (newActiveKey) => {
            if (!Array.isArray(newActiveKey)) return;
            const visibleIds = strategyKrSections.map((s) => s.sectionId);
            if (visibleIds.length === 0) return;
            const openVisible = new Set(
                newActiveKey
                    .map((k) => strategyKrSections[Number(k)]?.sectionId)
                    .filter(Boolean)
            );
            setKrAccordionOpenIds((prev) => {
                // null = todos abiertos: al cerrar alguno, materializar solo los visibles abiertos.
                if (prev === null) {
                    const openList = visibleIds.filter((id) => openVisible.has(id));
                    return openList.length === visibleIds.length ? null : openList;
                }
                // Conservar IDs abiertos de secciones no visibles (filtros) para restaurarlos al volver.
                const next = new Set(prev);
                for (const id of visibleIds) {
                    if (openVisible.has(id)) next.add(id);
                    else next.delete(id);
                }
                const openList = [...next];
                const onlyVisibleAllOpen =
                    visibleIds.every((id) => openVisible.has(id)) &&
                    openList.length === visibleIds.length &&
                    openList.every((id) => visibleIds.includes(id));
                return onlyVisibleAllOpen ? null : openList;
            });
        },
        [strategyKrSections]
    );

    const [expandedStrategyKey, setExpandedStrategyKey] = useState(null);
    const [strategyModalDescExpanded, setStrategyModalDescExpanded] = useState(false);
    const [strategyModalDescEditing, setStrategyModalDescEditing] = useState(false);
    const [strategyModalDescDraft, setStrategyModalDescDraft] = useState('');
    const [strategyModalDescSaving, setStrategyModalDescSaving] = useState(false);
    const [strategyModalTitleEditing, setStrategyModalTitleEditing] = useState(false);
    const [strategyModalTitleDraft, setStrategyModalTitleDraft] = useState('');
    const [strategyModalTitleSaving, setStrategyModalTitleSaving] = useState(false);
    const [strategyCommentsOpen, setStrategyCommentsOpen] = useState(false);
    const [strategyCommentsLoading, setStrategyCommentsLoading] = useState(false);
    const [strategyCommentsRows, setStrategyCommentsRows] = useState([]);
    const [strategyCommentDraft, setStrategyCommentDraft] = useState('');
    useEffect(() => {
        if (expandedStrategyKey != null && groupedTasksForDisplay[expandedStrategyKey] === undefined) {
            setExpandedStrategyKey(null);
        }
    }, [groupedTasksForDisplay, expandedStrategyKey]);

    useEffect(() => {
        setStrategyModalDescExpanded(false);
        setStrategyModalDescEditing(false);
        setStrategyModalDescDraft('');
        setStrategyModalDescSaving(false);
        setStrategyModalTitleEditing(false);
        setStrategyModalTitleDraft('');
        setStrategyModalTitleSaving(false);
    }, [expandedStrategyKey]);

    useEffect(() => {
        if (expandedStrategyKey == null) {
            setStrategyCommentsOpen(false);
            setStrategyCommentsRows([]);
            setStrategyCommentDraft('');
            setStrategyCommentsLoading(false);
        }
    }, [expandedStrategyKey]);

    useEffect(() => {
        teamDetailTasksRef.current = teamDetailTasks;
    }, [teamDetailTasks]);

    const resolveStatsKeyForGroup = useCallback(
        (groupKey) => {
            if (!groupKey) return null;
            return getStrategyBarStatsKey(
                groupKey,
                groupedTasksForDisplay,
                newTaskStrategyContext,
                strategyCanvasMeta
            );
        },
        [groupedTasksForDisplay, newTaskStrategyContext, strategyCanvasMeta]
    );

    const patchMosaicStats = useCallback(
        (groupKey, { activeDelta = 0, completedDelta = 0, minutesDelta = 0 }) => {
            const statsKey = resolveStatsKeyForGroup(groupKey);
            if (!statsKey || statsKey === 'none') return;
            if (activeDelta !== 0 || minutesDelta !== 0) {
                setStrategyBarStats((prev) =>
                    patchStrategyBarStatsEntry(prev, statsKey, { activeDelta, minutesDelta })
                );
            }
            if (completedDelta !== 0) {
                setStrategyTaskPreviews((prev) =>
                    patchStrategyPreviewCounts(prev, statsKey, { completedDelta })
                );
            }
        },
        [resolveStatsKeyForGroup]
    );

    patchMosaicStatsRef.current = patchMosaicStats;

    const closeStrategyTeamModal = useCallback(() => {
        syncMosaicIfPending();
        setTeamDetailGroupKey(null);
        setTeamDetailQuery(null);
        setExpandedStrategyKey(null);
    }, [syncMosaicIfPending]);

    useEffect(() => {
        if (teamDetailGroupKey != null && groupedTasksForDisplay[teamDetailGroupKey] === undefined) {
            setTeamDetailGroupKey(null);
            setTeamDetailQuery(null);
            setTeamDetailTasks([]);
        }
    }, [groupedTasksForDisplay, teamDetailGroupKey]);

    useEffect(() => {
        setTeamPanelSearchText('');
        setTeamPanelUserFilterSearch('');
        if (teamDetailGroupKey == null) {
            setTeamPanelFilterUserIds([]);
            setTeamPanelFilterStatusKeys([]);
            setTeamPanelGroupBy(TEAM_PANEL_GROUP_BY.creation);
            return;
        }
        if (onlyPendingTasks) {
            setTeamPanelFilterStatusKeys(['pending', 'running']);
        } else {
            setTeamPanelFilterStatusKeys([]);
        }
        if (teamAssigneeFilterIds.length > 0) {
            const syncedIds = [...new Set(
                teamAssigneeFilterIds
                    .map((x) => Number(x))
                    .filter((x) => Number.isFinite(x) && x > 0)
            )].sort((a, b) => a - b);
            setTeamPanelFilterUserIds(syncedIds);
            return;
        }
        setTeamPanelFilterUserIds([]);
    }, [teamDetailGroupKey, teamAssigneeFilterIds, onlyPendingTasks]);

    useEffect(() => {
        if (!teamDetailGroupKey) {
            setTeamDetailQuery(null);
            return;
        }
        const params = getStrategyTeamTasksParams(
            teamDetailGroupKey,
            groupedTasksForDisplay,
            newTaskStrategyContext,
            strategyCanvasMeta
        );
        setTeamDetailQuery(params);
        setTeamDetailTasks([]);
    }, [teamDetailGroupKey]);

    useEffect(() => {
        if (!teamDetailGroupKey || !teamDetailQuery) {
            if (!teamDetailGroupKey) {
                setTeamDetailTasks([]);
            }
            setTeamDetailLoading(false);
            return;
        }
        let cancelled = false;
        const hasExistingData = teamDetailTasksRef.current.length > 0;
        if (!hasExistingData) {
            setTeamDetailLoading(true);
        }
        listStrategyTeamTasks(
            token,
            usr_id,
            teamDetailQuery.strategy_mode,
            teamDetailQuery.trello_card_id,
            teamDetailQuery.opportunity_id
        )
            .then((res) => {
                if (cancelled) return;
                if (res === 'INVALID' || !res || res.ok !== true || !Array.isArray(res.tasks)) {
                    setTeamDetailTasks([]);
                } else {
                    const syncedAt = Date.now();
                    const stamped = res.tasks.map((row) => stampDurationSync(row, syncedAt));
                    setTeamDetailTasks(stamped);
                    // Alinear preview del mosaico con el listado completo del modal (misma card / filtro).
                    if (teamDetailQuery.strategy_mode === 'trello' && teamDetailQuery.trello_card_id) {
                        const sk = `trello:${String(teamDetailQuery.trello_card_id).trim()}`;
                        const mosaicTasks = stamped
                            .map((row) => previewRowToMosaicTask(row))
                            .filter((t) => t != null);
                        const filtered =
                            teamAssigneeFilterIds.length > 0
                                ? mosaicTasks.filter((t) =>
                                      taskMatchesAssigneeFilter(t, teamAssigneeFilterIds)
                                  )
                                : mosaicTasks;
                        setStrategyTaskPreviews((prev) => ({
                            ...prev,
                            [sk]: {
                                total: filtered.length,
                                completed_total: filtered.filter((t) => isTaskFlagTrue(t.completed))
                                    .length,
                                tasks: sortMosaicPreviewTasksRunningFirst(filtered).slice(
                                    0,
                                    PREVIEW_TASKS_PER_STRATEGY_CARD
                                ),
                            },
                        }));
                    }
                }
                setTeamDetailLoading(false);
            })
            .catch(() => {
                if (!cancelled) {
                    setTeamDetailTasks([]);
                    setTeamDetailLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [
        teamDetailGroupKey,
        teamDetailQuery,
        token,
        usr_id,
        teamDetailRefreshTick,
        teamAssigneeFilterIds,
    ]);

    /** Abre o cierra el modal ampliado con el panel de tareas del equipo. */
    const openStrategyTeamModal = useCallback((groupKey) => {
        if (!groupKey) return;
        setExpandedStrategyKey((ek) => {
            const closing = ek === groupKey;
            if (closing) {
                syncMosaicIfPending();
                setTeamDetailGroupKey(null);
                setTeamDetailQuery(null);
                return null;
            }
            setTeamDetailGroupKey(groupKey);
            return groupKey;
        });
    }, [syncMosaicIfPending]);

    /**
     * Clic en título/cuerpo del mosaico: abre o cierra el modal ampliado (vista de equipo).
     */
    const openStrategyCardFullView = useCallback(
        (e, groupKey) => {
            if (e.target.closest('button, a, input, textarea, select, label, [role="button"]')) {
                return;
            }
            openStrategyTeamModal(groupKey);
        },
        [openStrategyTeamModal]
    );

    const handleCancelStrategyCardDescriptionEdit = useCallback(() => {
        setStrategyModalDescEditing(false);
        setStrategyModalDescDraft('');
    }, []);

    const handleSaveStrategyCardDescription = useCallback(async () => {
        const gk = expandedStrategyKey;
        if (gk == null) return;
        const tid = getTrelloCardIdForStrategyGroup(gk, groupedTasksForDisplay, newTaskStrategyContext, strategyCanvasMeta);
        if (!tid) return;
        setStrategyModalDescSaving(true);
        const res = await updateCardDescription(token, tid, strategyModalDescDraft);
        setStrategyModalDescSaving(false);
        if (res === 'INVALID') {
            navigate('/');
            return;
        }
        if (res == null || typeof res !== 'object' || res.ok !== true) {
            window.alert(
                res && res.error === 'card_not_found'
                    ? 'No se encontró la tarjeta de estrategia.'
                    : 'No se pudo guardar la descripción.'
            );
            return;
        }
        const nextText = strategyModalDescDraft;
        setStrategyCanvasMeta((prev) => {
            const base = prev[gk] || {
                trello_card_id: tid,
                card_status: 0,
                kr_id: null,
                kr_name: null,
                stats_key: `trello:${tid}`,
                card_descript: '',
            };
            return { ...prev, [gk]: { ...base, card_descript: nextText } };
        });
        setTasks((prev) =>
            prev.map((t) => {
                const cid = t.trello_card_id != null ? String(t.trello_card_id).trim() : '';
                if (cid === tid) return { ...t, card_descript: nextText };
                return t;
            })
        );
        setStrategyModalDescEditing(false);
        setStrategyModalDescExpanded(true);
    }, [
        expandedStrategyKey,
        groupedTasksForDisplay,
        newTaskStrategyContext,
        strategyCanvasMeta,
        strategyModalDescDraft,
        token,
        navigate,
    ]);

    const refreshStrategyComments = useCallback(async () => {
        const gk = expandedStrategyKey;
        if (gk == null) return;
        const tid = getTrelloCardIdForStrategyGroup(gk, groupedTasksForDisplay, newTaskStrategyContext, strategyCanvasMeta);
        if (!tid) {
            setStrategyCommentsRows([]);
            return;
        }
        setStrategyCommentsLoading(true);
        const res = await listCardComments(token, tid);
        setStrategyCommentsLoading(false);
        if (res === 'INVALID') {
            navigate('/');
            return;
        }
        if (res != null && typeof res === 'object' && res.ok === true && Array.isArray(res.comments)) {
            setStrategyCommentsRows(res.comments);
        } else {
            setStrategyCommentsRows([]);
        }
    }, [expandedStrategyKey, groupedTasksForDisplay, newTaskStrategyContext, strategyCanvasMeta, token, navigate]);

    useEffect(() => {
        if (!strategyCommentsOpen || expandedStrategyKey == null) return;
        refreshStrategyComments();
    }, [strategyCommentsOpen, expandedStrategyKey, refreshStrategyComments]);

    const toggleStrategyCommentsPanel = useCallback(() => {
        setStrategyCommentsOpen((v) => !v);
    }, []);

    /** Desde el pie de tarjeta en mosaico: abre el modal y el panel de comentarios (mismo resultado que el icono del popup). */
    const openStrategyModalWithComments = useCallback((groupKey) => {
        setExpandedStrategyKey(groupKey);
        setTeamDetailGroupKey(groupKey);
        setStrategyCommentsOpen(true);
    }, []);

    const handleSubmitStrategyComment = useCallback(async () => {
        const text = strategyCommentDraft.trim();
        const gk = expandedStrategyKey;
        if (text === '' || gk == null) return;
        const tid = getTrelloCardIdForStrategyGroup(gk, groupedTasksForDisplay, newTaskStrategyContext, strategyCanvasMeta);
        if (!tid) return;
        const res = await addCardComment(token, tid, text);
        if (res === 'INVALID') {
            navigate('/');
            return;
        }
        if (res == null || typeof res !== 'object' || res.ok !== true) {
            window.alert(
                res && res.error === 'empty_comment'
                    ? 'Escribe un comentario.'
                    : res && res.error === 'comment_too_long'
                      ? 'El comentario es demasiado largo (máx. 8000 caracteres).'
                      : 'No se pudo guardar el comentario.'
            );
            return;
        }
        setStrategyCommentDraft('');
        refreshStrategyComments();
    }, [
        strategyCommentDraft,
        expandedStrategyKey,
        groupedTasksForDisplay,
        newTaskStrategyContext,
        strategyCanvasMeta,
        token,
        navigate,
        refreshStrategyComments,
    ]);

    const handleCancelStrategyGroupTitleEdit = useCallback(() => {
        setStrategyModalTitleEditing(false);
        setStrategyModalTitleDraft('');
    }, []);

    const handleSaveStrategyGroupTitle = useCallback(async () => {
        const oldKey = expandedStrategyKey;
        if (oldKey == null) return;
        const newName = strategyModalTitleDraft.trim();
        if (!newName) {
            window.alert('El nombre no puede estar vacío.');
            return;
        }
        if (newName.length > STRATEGY_GROUP_TITLE_MAX) {
            window.alert(`El nombre no puede superar ${STRATEGY_GROUP_TITLE_MAX} caracteres.`);
            return;
        }
        if (newName === oldKey) {
            setStrategyModalTitleEditing(false);
            setStrategyModalTitleDraft('');
            return;
        }
        const tid = getTrelloCardIdForStrategyGroup(
            oldKey,
            groupedTasksForDisplay,
            newTaskStrategyContext,
            strategyCanvasMeta
        );
        const tidStr = tid != null ? String(tid).trim() : '';
        if (!tidStr || tidStr === '99999999') {
            window.alert('Esta agrupación no se puede renombrar desde aquí.');
            return;
        }

        setStrategyModalTitleSaving(true);
        const res = await ensure_card(token, tidStr, newName, null, null, null, { set_creator: true });
        if (res === 'INVALID' || (typeof res === 'object' && res?.error === 'INVALID')) {
            setStrategyModalTitleSaving(false);
            navigate('/');
            return;
        }
        const idNum = res != null && typeof res === 'object' ? Number(res.id) : NaN;
        if (!Number.isFinite(idNum) || idNum <= 0) {
            setStrategyModalTitleSaving(false);
            const reason = typeof res === 'object' ? res?.reason : null;
            window.alert(
                reason === 'update_failed'
                    ? 'No se pudo actualizar el nombre en la base de datos.'
                    : reason === 'empty_params'
                      ? 'Indica un nombre válido.'
                      : 'No se pudo guardar el nombre de la estrategia.'
            );
            return;
        }

        const commentBody = `Nombre de la estrategia actualizado. Nombre anterior: «${oldKey}».`;
        const resComment = await addCardComment(token, tidStr, commentBody);
        if (resComment === 'INVALID') {
            setStrategyModalTitleSaving(false);
            navigate('/');
            return;
        }
        if (resComment == null || typeof resComment !== 'object' || resComment.ok !== true) {
            window.alert(
                'El nombre se guardó, pero no se pudo registrar el comentario con el historial. Puedes añadirlo manualmente en comentarios.'
            );
        }

        setTasks((prev) =>
            prev.map((t) => {
                const c = t.trello_card_id != null ? String(t.trello_card_id).trim() : '';
                if (c === tidStr) return { ...t, strategy: newName };
                return t;
            })
        );
        setStrategyCanvasMeta((prev) => {
            if (!prev[oldKey]) return prev;
            const next = { ...prev };
            next[newName] = { ...next[oldKey] };
            delete next[oldKey];
            return next;
        });
        setNewlyCreatedStrategyPlaceholders((prev) =>
            prev.map((ph) => (ph.groupKey === oldKey ? { ...ph, groupKey: newName } : ph))
        );
        setNewTaskStrategyContext((ctx) => {
            if (ctx == null || ctx.strategyGroupKey !== oldKey) return ctx;
            return {
                ...ctx,
                strategyGroupKey: newName,
                displayLabel:
                    ctx.linkType === 'trello' || ctx.displayLabel === oldKey ? newName : ctx.displayLabel,
            };
        });
        setTeamDetailGroupKey((gk) => (gk === oldKey ? newName : gk));
        setReassignStrategyGroupKey((gk) => (gk === oldKey ? newName : gk));
        setCardStatusPicker((p) => (p && p.groupKey === oldKey ? { ...p, groupKey: newName } : p));
        setKrReassignModal((p) => (p && p.groupKey === oldKey ? { ...p, groupKey: newName } : p));

        setExpandedStrategyKey(newName);
        setStrategyModalTitleEditing(false);
        setStrategyModalTitleDraft('');
        setStrategyModalTitleSaving(false);

        if (strategyCommentsOpen) {
            setStrategyCommentsLoading(true);
            try {
                const resList = await listCardComments(token, tidStr);
                if (resList === 'INVALID') {
                    navigate('/');
                    return;
                }
                if (resList != null && typeof resList === 'object' && resList.ok === true && Array.isArray(resList.comments)) {
                    setStrategyCommentsRows(resList.comments);
                }
            } finally {
                setStrategyCommentsLoading(false);
            }
        }
    }, [
        expandedStrategyKey,
        strategyModalTitleDraft,
        groupedTasksForDisplay,
        newTaskStrategyContext,
        strategyCanvasMeta,
        token,
        navigate,
        strategyCommentsOpen,
    ]);

    const openTeamPanelReassign = useCallback(
        (row, strategyGroupKeyOverride = null) => {
            const gk = strategyGroupKeyOverride ?? teamDetailGroupKey;
            if (gk == null || row == null) return;
            setAssigneePickerMode('reassign');
            setReassignTaskId(row.id);
            setReassignStrategyGroupKey(gk);
            setAssigneeSearch('');
            setAssigneePickerOpen(true);
        },
        [teamDetailGroupKey]
    );

    const toggleTeamPanelUserFilter = useCallback((uid) => {
        const n = Number(uid);
        setTeamPanelFilterUserIds((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]));
    }, []);

    const toggleTeamPanelStatusFilter = useCallback((statusKey) => {
        setTeamPanelFilterStatusKeys((prev) =>
            prev.includes(statusKey) ? prev.filter((x) => x !== statusKey) : [...prev, statusKey]
        );
    }, []);

    const clearTeamPanelFilters = useCallback(() => {
        setTeamPanelSearchText('');
        setTeamPanelUserFilterSearch('');
        setTeamPanelFilterUserIds([]);
        setTeamPanelFilterStatusKeys([]);
        setTeamPanelGroupBy(TEAM_PANEL_GROUP_BY.creation);
    }, []);

    const openTaskStatisticsPopup = useCallback((taskId, taskUsrId) => {
        const n = Number(taskId);
        if (!Number.isFinite(n) || n <= 0) return;
        const uidRaw = taskUsrId != null && taskUsrId !== '' ? Number(taskUsrId) : usr_id != null ? Number(usr_id) : null;
        const uid = Number.isFinite(uidRaw) && uidRaw > 0 ? uidRaw : null;
        setSelectedTaskForStats({ id: n, usr_id: uid });
    }, [usr_id]);

    const closeTaskStatisticsPopup = useCallback(() => {
        setSelectedTaskForStats(null);
    }, []);

    const closeNewTaskFabPopup = useCallback(() => {
        if (isAddingTaskRef.current) return;
        setNewTaskFabPopupOpen(false);
        setNewTaskStrategyContext(null);
        if (usr_id != null) setNewTaskAssignUsrId(Number(usr_id));
    }, [usr_id]);

    const openNewTaskFabPopupForGroup = useCallback(
        (groupKey) => {
            if (usr_id != null) setNewTaskAssignUsrId(Number(usr_id));
            const isPrivate = isPrivateStrategyGroup(groupKey, groupedTasksForDisplay, strategyCanvasMeta);
            const section = strategyKrSections.find(
                (s) => Array.isArray(s.groupKeys) && s.groupKeys.includes(groupKey)
            );
            const krRow =
                !isPrivate && section?.krId != null
                    ? krsCatalog.find((k) => Number(k.id) === Number(section.krId))
                    : null;
            const krTrelloIdHint =
                krRow?.trello_id != null && String(krRow.trello_id).trim() !== ''
                    ? String(krRow.trello_id).trim()
                    : null;
            setNewTaskStrategyContext({
                ...resolveNewTaskLinkForGroup(groupKey, groupedTasksForDisplay, strategyCanvasMeta),
                strategyGroupKey: groupKey,
                krTrelloIdHint,
                isPrivate,
            });
            setNewTaskFabPopupOpen(true);
        },
        [groupedTasksForDisplay, strategyCanvasMeta, usr_id, strategyKrSections, krsCatalog]
    );

    const openNewTaskAssigneePicker = useCallback(() => {
        setAssigneePickerMode('new_task');
        setReassignTaskId(null);
        setReassignStrategyGroupKey(null);
        setAssigneeSearch('');
        setAssigneePickerOpen(true);
    }, []);

    useEffect(() => {
        if (!newTaskFabPopupOpen) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') closeNewTaskFabPopup();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [newTaskFabPopupOpen, closeNewTaskFabPopup]);

    const openCardStatusPickerForGroup = useCallback(
        (groupKey) => {
            const trelloCardId = getTrelloCardIdForStrategyGroup(
                groupKey,
                groupedTasksForDisplay,
                newTaskStrategyContext,
                strategyCanvasMeta
            );
            const cardDbId = getCardDbIdForStrategyGroup(
                groupKey,
                groupedTasksForDisplay,
                newTaskStrategyContext,
                strategyCanvasMeta
            );
            if (!trelloCardId && !cardDbId) return;
            setCardStatusPicker({ groupKey, trelloCardId: trelloCardId || '', cardDbId: cardDbId ?? null });
        },
        [groupedTasksForDisplay, newTaskStrategyContext, strategyCanvasMeta]
    );

    const handleCardStatusSelect = async (statusVal) => {
        const ctx = cardStatusPicker;
        setCardStatusPicker(null);
        if (ctx == null || (!ctx.trelloCardId && !ctx.cardDbId)) return;
        const res = await updateCardStatus(token, ctx.trelloCardId, statusVal, ctx.cardDbId);
        if (res === 'INVALID' || res == null) {
            window.alert('Sesión inválida o error de red.');
            return;
        }
        if (typeof res === 'object' && res.ok === false) {
            const msg =
                res.error === 'card_not_found'
                    ? 'No hay fila en talent_db.cards para esta tarjeta.'
                    : res.error === 'invalid_params'
                      ? 'Parámetros no válidos al guardar el estado. Vuelve a cargar la página e inténtalo de nuevo.'
                      : 'No se pudo actualizar el estado.';
            window.alert(msg);
            return;
        }
        fetch_load_task();
    };

    const openKrReassignFromFooter = useCallback(
        (groupKey) => {
            if (isPrivateStrategyGroup(groupKey, groupedTasksForDisplay, strategyCanvasMeta)) {
                return;
            }
            const tid = getTrelloCardIdForStrategyGroup(
                groupKey,
                groupedTasksForDisplay,
                newTaskStrategyContext,
                strategyCanvasMeta
            );
            if (!tid) {
                window.alert(
                    'Esta estrategia no tiene una tarjeta Trello vinculada. Vincula primero una estrategia desde la tarea o el menú «Mover».'
                );
                return;
            }
            setKrReassignModal({ groupKey, trelloCardId: tid });
        },
        [groupedTasksForDisplay, newTaskStrategyContext, strategyCanvasMeta]
    );

    useEffect(() => {
        if (!krReassignModal) return;
        if (Array.isArray(krListRows) && krListRows.length > 0) return;
        if (Array.isArray(krsCatalog) && krsCatalog.length > 0) {
            setKrListRows(krsCatalog);
            return;
        }
        if (!token) return;
        let cancelled = false;
        setKrListLoading(true);
        listKrs(token)
            .then((res) => {
                if (cancelled) return;
                if (res === 'INVALID' || res == null) {
                    window.alert('No se pudo cargar la lista de KRs.');
                    setKrReassignModal(null);
                    return;
                }
                if (res.ok === true && Array.isArray(res.krs)) {
                    setKrListRows(res.krs);
                    setKrsCatalog(res.krs);
                } else {
                    setKrListRows([]);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    window.alert('Error al cargar KRs.');
                    setKrReassignModal(null);
                }
            })
            .finally(() => {
                if (!cancelled) setKrListLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [krReassignModal, token, krListRows, krsCatalog]);

    const handleKrReassignSelect = async (krTrelloId) => {
        const ctx = krReassignModal;
        if (!ctx || !ctx.trelloCardId) return;
        const val = krTrelloId != null ? String(krTrelloId).trim() : '';
        const res = await updateCardKr(token, ctx.trelloCardId, val);
        setKrReassignModal(null);
        if (res === 'INVALID' || res == null) {
            window.alert('Sesión inválida o error de red.');
            return;
        }
        if (typeof res === 'object' && res.ok === false) {
            const msg =
                res.error === 'card_not_found'
                    ? 'No hay fila en talent_db.cards para esta tarjeta.'
                    : res.error === 'kr_not_found'
                      ? 'El KR seleccionado no existe en la base de datos.'
                      : 'No se pudo actualizar el vínculo con el KR.';
            window.alert(msg);
            return;
        }
        fetch_load_task();
        setTeamDetailRefreshTick((x) => x + 1);
    };

    const StrategyCardFooterBar = ({
        groupKey,
        onAddClick,
        onExpandClick,
        onKrSpacerClick,
        onOpenCardStatusPicker,
        onCommentsClick,
        commentsActive,
        krFooterId: krFooterIdProp = null,
        isPrivateFooter = false,
    }) => {
        const statsKey = getStrategyBarStatsKey(
            groupKey,
            groupedTasksForDisplay,
            newTaskStrategyContext,
            strategyCanvasMeta
        );
        const krFooterIdResolved =
            krFooterIdProp !== null && krFooterIdProp !== undefined
                ? krFooterIdProp
                : getKrFooterIdForStrategyGroup(groupKey, groupedTasksForDisplay, strategyCanvasMeta);
        const krColorSuffix = isPrivateFooter
            ? getStrategyKrColorSuffix(null, true)
            : getStrategyKrColorSuffix(krFooterIdResolved);
        const krFooterClass = krColorSuffix ? ` strategy-card-footer-bar--${krColorSuffix}` : '';
        const canvasMeta = strategyCanvasMeta && strategyCanvasMeta[groupKey];
        const footerTid =
            (statsKey && String(statsKey).startsWith('trello:')
                ? String(statsKey).slice('trello:'.length).trim()
                : '') ||
            (canvasMeta?.trello_card_id != null ? String(canvasMeta.trello_card_id).trim() : '') ||
            '';
        const footerCid =
            canvasMeta?.card_db_id != null && Number(canvasMeta.card_db_id) > 0
                ? Number(canvasMeta.card_db_id)
                : null;
        const relatedStatsKeys =
            String(statsKey || '').startsWith('trello:') && footerTid
                ? collectTeamPreviewForTrelloCard(
                      statsKey,
                      footerTid,
                      footerCid,
                      strategyTaskPreviews
                  ).relatedStatsKeys
                : [statsKey];
        let totalActiveMinutes = 0;
        for (const sk of relatedStatsKeys.length > 0 ? relatedStatsKeys : [statsKey]) {
            const st = strategyBarStats[sk];
            totalActiveMinutes += st != null ? Number(st.total_active_minutes) || 0 : 0;
        }
        const totalHoursLabel = formatoDuracion(totalActiveMinutes);
        const cardStatusBadge = getStrategyGroupCardStatusBadge(
            groupKey,
            groupedTasksForDisplay,
            newTaskStrategyContext,
            strategyCanvasMeta
        );
        const handleAdd = (e) => {
            e?.stopPropagation?.();
            if (onAddClick) onAddClick();
            else openNewTaskFabPopupForGroup(groupKey);
        };
        const metricsContent = (
            <>
                <div
                    className="strategy-card-footer-metric"
                    title={
                        teamAssigneeFilterIds.length > 0
                            ? 'Tiempo activo acumulado en las tareas de esta estrategia (miembros filtrados)'
                            : 'Tiempo activo total acumulado en todas las tareas de esta estrategia (equipo)'
                    }
                >
                    <Clock size={15} className="flex-shrink-0" strokeWidth={2.2} color="currentColor" aria-hidden />
                    <span className="strategy-card-footer-metric-duration-text">{totalHoursLabel}</span>
                </div>
            </>
        );
        return (
            <div className={`strategy-card-footer-bar${krFooterClass}`}>
                <div className="strategy-card-footer-metrics" onClick={(e) => e.stopPropagation()}>
                    {metricsContent}
                </div>
                {cardStatusBadge ? (
                    <button
                        type="button"
                        className={`strategy-card-footer-cstatus strategy-card-footer-cstatus--${cardStatusBadge.variant}`}
                        title={cardStatusBadge.title}
                        aria-label={cardStatusBadge.title}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onOpenCardStatusPicker) onOpenCardStatusPicker(groupKey);
                        }}
                    >
                        {cardStatusBadge.label}
                    </button>
                ) : null}
                {onCommentsClick ? (
                    <button
                        type="button"
                        className={`strategy-card-footer-comments${commentsActive ? ' strategy-card-footer-comments--active' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onCommentsClick();
                        }}
                        title="Comentarios de la estrategia"
                        aria-label="Comentarios de la estrategia"
                        aria-pressed={!!commentsActive}
                    >
                        <MessageSquare size={17} strokeWidth={2.1} className="strategy-card-footer-comments-icon" />
                    </button>
                ) : null}
                {onKrSpacerClick || onExpandClick ? (
                    <button
                        type="button"
                        className="strategy-card-footer-spacer"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onKrSpacerClick) onKrSpacerClick();
                            else if (onExpandClick) onExpandClick();
                        }}
                        aria-label={
                            onKrSpacerClick
                                ? 'Elegir KR (resultado clave) para esta estrategia'
                                : 'Ver todas las tareas de esta estrategia'
                        }
                    >
                        
                    </button>
                ) : (
                    <div className="strategy-card-footer-spacer strategy-card-footer-spacer--inert" aria-hidden />
                )}
                <button type="button" className="strategy-card-add-fab" onClick={handleAdd} aria-label="Agregar tarea">
                    <span className="strategy-card-add-fab-plus" aria-hidden>
                        +
                    </span>
                </button>
            </div>
        );
    };

    const renderStrategyMosaicCard = (groupKey, sectionKrId) => {
        const isOpportunityGroup = getOpportunityGroupId(groupKey) != null;
        const isPrivateGroup = isPrivateStrategyGroup(groupKey, groupedTasksForDisplay, strategyCanvasMeta);
        const groupTitle = getOpportunityGroupTitle(groupKey, groupedTasksForDisplay, strategyCanvasMeta);
        const mosaicPreview = resolveStrategyMosaicPreview(
            groupKey,
            groupedTasksForDisplay,
            strategyTaskPreviews,
            teamAssigneeFilterIds,
            onlyPendingTasks,
            newTaskStrategyContext,
            strategyCanvasMeta,
            PREVIEW_TASKS_PER_STRATEGY_CARD,
            tagFilterCategoryId,
            usr_id,
            searchText
        );
        const { previewTasks, displayTotal, displayCompleted, moreCount, hasListableTasks } = mosaicPreview;
        const strictMosaicList =
            onlyPendingTasks || teamAssigneeFilterIds.length > 0 || tagFilterCategoryId != null;
        const searchTerm = String(searchText ?? '')
            .toLowerCase()
            .trim();
        const pinKey = newTaskStrategyContext?.strategyGroupKey;
        const keepEmptyShellWithSearch =
            searchTerm.length > 0 &&
            (String(groupKey).toLowerCase().includes(searchTerm) ||
                (pinKey != null && String(pinKey) === String(groupKey)) ||
                (groupedTasksForDisplay[groupKey] || []).length > 0);
        const isCreatorOwnedEmptyShell =
            !hasListableTasks &&
            (searchTerm
                ? keepEmptyShellWithSearch
                : isCreatorOwnedCanvas(strategyCanvasMeta?.[groupKey], usr_id));
        if (strictMosaicList && !hasListableTasks && !isCreatorOwnedEmptyShell) {
            return null;
        }
        /** Sin tareas en la lista: descripción compacta si no hay filtros estrictos o es estrategia vacía del creador. */
        const showDescriptionOnly =
            !hasListableTasks && (!strictMosaicList || isCreatorOwnedEmptyShell);
        const ownTasksInGroup = getOwnTasksForStrategyGroup(
            groupKey,
            groupedTasksForDisplay,
            strategyCanvasMeta
        );
        const allTasksCompleted = displayTotal > 0 && displayCompleted === displayTotal;
        const hasActiveTask =
            previewTasks.some((t) => t.active) || ownTasksInGroup.some((t) => t.active);
        const ownerUsrId = usr_id != null ? Number(usr_id) : NaN;
        const cardDescription = getStrategyCardDescript(groupKey, groupedTasksForDisplay, strategyCanvasMeta).trim();
        return (
            <div
                key={groupKey}
                className={`strategy-card${groupKey === 'Sin estrategia' ? ' strategy-card--sin-estrategia' : ''}${isPrivateGroup ? ' strategy-card--private' : ''}${allTasksCompleted ? ' strategy-card--completed' : ''}${showDescriptionOnly ? ' strategy-card--team-compact' : ''}`}
            >
                <div className="strategy-card-main">
                    <div
                        className="strategy-card-expand-hit"
                        onClick={(e) => openStrategyCardFullView(e, groupKey)}
                    >
                        <div className="strategy-card-title-row">
                            <div className={`strategy-card-title ${hasActiveTask ? 'text-danger' : ''}`}>
                                {groupTitle}
                                <span
                                    className={`fw-normal small ms-2 ${displayCompleted === displayTotal && displayTotal > 0 ? 'text-success' : 'text-muted'}`}
                                >
                                    ({displayCompleted}/{displayTotal})
                                </span>
                            </div>
                            <button
                                type="button"
                                className="strategy-card-expand-btn"
                                title="Ampliar"
                                aria-label="Ampliar"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openStrategyTeamModal(groupKey);
                                }}
                            >
                                <Maximize2 size={18} strokeWidth={2} aria-hidden />
                            </button>
                        </div>
                        <div className={`strategy-card-body${showDescriptionOnly ? ' strategy-card-body--team-compact' : ''}`}>
                            {showDescriptionOnly ? (
                                <p className="strategy-card-team-description">
                                    {cardDescription !== '' ? cardDescription : 'Sin descripción.'}
                                </p>
                            ) : (
                                <>
                                    {previewTasks.map((task) => {
                                        const taskUsrId =
                                            task.usr_id != null && task.usr_id !== ''
                                                ? Number(task.usr_id)
                                                : null;
                                        const isOwnTask =
                                            !Number.isFinite(ownerUsrId) ||
                                            ownerUsrId <= 0 ||
                                            taskUsrId == null ||
                                            Number.isNaN(taskUsrId) ||
                                            taskUsrId === ownerUsrId;
                                        const assigneeOpt =
                                            taskUsrId != null
                                                ? assigneeOptions.find((o) => Number(o.usr_id) === taskUsrId)
                                                : null;
                                        const assigneeLabel =
                                            assigneeOpt?.displayName ||
                                            (task.email_id && String(task.email_id).trim()) ||
                                            (taskUsrId != null ? `Usuario ${taskUsrId}` : '');
                                        return (
                                            <StrategyCardTaskRow
                                                key={task.id}
                                                task={task}
                                                nowMs={nowMs}
                                                readOnly={!isOwnTask}
                                                assigneeUsrId={taskUsrId}
                                                assigneeLabel={assigneeLabel}
                                                isMosaicEditing={isOwnTask && mosaicEditTaskId === task.id}
                                                mosaicEditText={mosaicEditText}
                                                onMosaicEditTextChange={setMosaicEditText}
                                                setMosaicEditTaskId={setMosaicEditTaskId}
                                                maxChars={MAX_CHARS}
                                                mosaicNavigateTimeoutRef={mosaicNavigateTimeoutRef}
                                                onOpenStatistics={openTaskStatisticsPopup}
                                                cardsLength={cards.length}
                                                truncateCategoryLabel={truncateCategoryLabel}
                                                onUpdateTaskText={handleTextTask}
                                                onToggleTaskComplete={handleToggleTask}
                                                onOpenTaskModal={handleOpenModal}
                                                onOpenCategoryModal={openTaskCategoryModal}
                                                onOpenDueDateModal={openDueDateModal}
                                                onDeleteTask={handleDeleteTask}
                                                onTogglePriority={handleTogglePriority}
                                                onStartMosaicTitleEdit={() => {
                                                    if (mosaicNavigateTimeoutRef.current) {
                                                        clearTimeout(mosaicNavigateTimeoutRef.current);
                                                        mosaicNavigateTimeoutRef.current = null;
                                                    }
                                                    setMosaicEditTaskId(task.id);
                                                    setMosaicEditText(task.text);
                                                }}
                                            />
                                        );
                                    })}
                                    {moreCount > 0 && (
                                        <button
                                            type="button"
                                            className="strategy-card-more-link w-100 text-start"
                                            onClick={() => openStrategyTeamModal(groupKey)}
                                        >
                                            {moreCount} tarea{moreCount !== 1 ? 's' : ''} más
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <StrategyCardFooterBar
                    groupKey={groupKey}
                    krFooterId={sectionKrId}
                    isPrivateFooter={isPrivateGroup}
                    onKrSpacerClick={
                        isOpportunityGroup || isPrivateGroup
                            ? undefined
                            : () => openKrReassignFromFooter(groupKey)
                    }
                    onOpenCardStatusPicker={openCardStatusPickerForGroup}
                    onCommentsClick={
                        getTrelloCardIdForStrategyGroup(
                            groupKey,
                            groupedTasksForDisplay,
                            newTaskStrategyContext,
                            strategyCanvasMeta
                        )
                            ? () => openStrategyModalWithComments(groupKey)
                            : undefined
                    }
                    commentsActive={expandedStrategyKey === groupKey && strategyCommentsOpen}
                />
            </div>
        );
    };

    const strategyCommentCreatorLabel = useCallback(
        (row) => {
            const n =
                row.creator_name != null && String(row.creator_name).trim() !== ''
                    ? String(row.creator_name).trim()
                    : '';
            if (n) return n;
            const uid = row.creator_usr_id != null ? Number(row.creator_usr_id) : null;
            if (uid != null && !Number.isNaN(uid)) {
                const o = assigneeOptions.find((x) => Number(x.usr_id) === uid);
                if (o?.displayName) return o.displayName;
                return `Usuario ${uid}`;
            }
            return 'Usuario';
        },
        [assigneeOptions]
    );

    // --- RENDERIZADO PRINCIPAL ---
    const strategyModalHeaderExtra = useMemo(() => {
        if (expandedStrategyKey == null) return null;
        const tid = getTrelloCardIdForStrategyGroup(
            expandedStrategyKey,
            groupedTasksForDisplay,
            newTaskStrategyContext,
            strategyCanvasMeta
        );
        if (!tid) return null;
        const full = getStrategyCardDescript(expandedStrategyKey, groupedTasksForDisplay, strategyCanvasMeta);
        const preview = full.length > 50 ? `${full.slice(0, 50)}...` : full;
        const hasDesc = full.trim().length > 0;

        if (!hasDesc && !strategyModalDescEditing) {
            return (
                <div className="strategy-modal-card-desc" onClick={(e) => e.stopPropagation()}>
                    <button
                        type="button"
                        className="strategy-modal-card-desc-snippet text-muted fst-italic"
                        onClick={() => {
                            setStrategyModalDescDraft('');
                            setStrategyModalDescEditing(true);
                            setStrategyModalDescExpanded(true);
                        }}
                    >
                        Descripción...
                    </button>
                </div>
            );
        }

        return (
            <div className="strategy-modal-card-desc" onClick={(e) => e.stopPropagation()}>
                {strategyModalDescEditing ? (
                    <>
                        <textarea
                            className="form-control form-control-sm strategy-modal-card-desc-textarea"
                            rows={5}
                            value={strategyModalDescDraft}
                            onChange={(e) => setStrategyModalDescDraft(e.target.value)}
                            disabled={strategyModalDescSaving}
                            maxLength={8000}
                            aria-label="Editar descripción de la estrategia"
                        />
                        <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                disabled={strategyModalDescSaving}
                                onClick={() => handleSaveStrategyCardDescription()}
                            >
                                Guardar
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                disabled={strategyModalDescSaving}
                                onClick={() => handleCancelStrategyCardDescriptionEdit()}
                            >
                                Cancelar
                            </button>
                        </div>
                    </>
                ) : strategyModalDescExpanded ? (
                    <>
                        <div className="strategy-modal-card-desc-scroll">
                            <p className="strategy-modal-card-desc-full">{linkifyHttpsInPlainText(full)}</p>
                        </div>
                        <div className="strategy-modal-card-desc-actions">
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-decoration-underline"
                                style={{ color: '#0060b9' }}
                                onClick={() => {
                                    setStrategyModalDescExpanded(false);
                                    setStrategyModalDescEditing(false);
                                }}
                            >
                                ver menos
                            </button>
                            <button
                                type="button"
                                className="strategy-modal-card-desc-edit-btn"
                                title="Editar descripción"
                                aria-label="Editar descripción"
                                onClick={() => {
                                    setStrategyModalDescDraft(full);
                                    setStrategyModalDescEditing(true);
                                    setStrategyModalDescExpanded(true);
                                }}
                            >
                                <Pencil size={17} />
                            </button>
                        </div>
                    </>
                ) : (
                    <button
                        type="button"
                        className="strategy-modal-card-desc-snippet"
                        onClick={() => setStrategyModalDescExpanded(true)}
                    >
                        {preview}
                    </button>
                )}
            </div>
        );
    }, [
        expandedStrategyKey,
        groupedTasksForDisplay,
        newTaskStrategyContext,
        strategyCanvasMeta,
        strategyModalDescExpanded,
        strategyModalDescEditing,
        strategyModalDescDraft,
        strategyModalDescSaving,
        handleSaveStrategyCardDescription,
        handleCancelStrategyCardDescriptionEdit,
    ]);

    const reassignTargetTeamRow = useMemo(() => {
        if (reassignTaskId == null) return null;
        return teamDetailTasks.find((t) => Number(t.id) === Number(reassignTaskId)) ?? null;
    }, [reassignTaskId, teamDetailTasks]);

    const assigneePickerHighlightId =
        assigneePickerMode === 'reassign' && reassignTargetTeamRow != null && reassignTargetTeamRow.usr_id != null
            ? Number(reassignTargetTeamRow.usr_id)
            : newTaskAssignUsrId;

    const teamFilteredTasks = useMemo(() => {
        let list = teamDetailTasks;
        const q = teamPanelSearchText.trim().toLowerCase();
        if (q) {
            list = list.filter((t) => (t.task_name && String(t.task_name).toLowerCase().includes(q)));
        }
        if (teamPanelFilterUserIds.length > 0) {
            const allowed = new Set(teamPanelFilterUserIds.map(Number));
            list = list.filter((t) => t.usr_id != null && allowed.has(Number(t.usr_id)));
        }
        if (teamPanelFilterStatusKeys.length > 0) {
            const allowedSt = new Set(teamPanelFilterStatusKeys);
            list = list.filter((t) => allowedSt.has(getTeamPanelTaskStatusKey(t)));
        }
        if (teamPanelGroupBy === TEAM_PANEL_GROUP_BY.completion) {
            list = list.filter((t) => !!t.completed);
        }
        if (tagFilterCategoryId != null) {
            list = list.filter((t) => {
                const catId = resolveTaskCategoryId(t, tasks, strategyTaskPreviews);
                return String(catId ?? '') === String(tagFilterCategoryId);
            });
        }
        return list;
    }, [
        teamDetailTasks,
        teamPanelSearchText,
        teamPanelFilterUserIds,
        teamPanelFilterStatusKeys,
        teamPanelGroupBy,
        tagFilterCategoryId,
        tasks,
        strategyTaskPreviews,
    ]);

    const expandedModalTaskCounts = useMemo(() => {
        if (expandedStrategyKey == null || teamDetailGroupKey !== expandedStrategyKey) {
            return { done: 0, total: 0 };
        }
        const done = teamFilteredTasks.filter((t) => t.completed).length;
        return { done, total: teamFilteredTasks.length };
    }, [expandedStrategyKey, teamDetailGroupKey, teamFilteredTasks]);

    const expandedStrategyModalTitle = useMemo(() => {
        if (expandedStrategyKey == null) return '';
        const expandedGroupTitle = getOpportunityGroupTitle(
            expandedStrategyKey,
            groupedTasksForDisplay,
            strategyCanvasMeta
        );
        const { done, total } = expandedModalTaskCounts;
        const tid = getTrelloCardIdForStrategyGroup(
            expandedStrategyKey,
            groupedTasksForDisplay,
            newTaskStrategyContext,
            strategyCanvasMeta
        );
        const tidStr = tid != null ? String(tid).trim() : '';
        const canRename = tidStr !== '' && tidStr !== '99999999';

        if (strategyModalTitleEditing) {
            return (
                <span className="d-inline-flex flex-wrap align-items-center gap-2" style={{ minWidth: 0, maxWidth: '100%' }}>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ minWidth: '12rem', maxWidth: 'min(100%, 28rem)' }}
                        value={strategyModalTitleDraft}
                        onChange={(e) => setStrategyModalTitleDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSaveStrategyGroupTitle();
                            } else if (e.key === 'Escape') {
                                e.preventDefault();
                                handleCancelStrategyGroupTitleEdit();
                            }
                        }}
                        maxLength={STRATEGY_GROUP_TITLE_MAX}
                        disabled={strategyModalTitleSaving}
                        aria-label="Nombre de la estrategia"
                    />
                    <span className="text-muted fw-normal small">{`(${done}/${total})`}</span>
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={strategyModalTitleSaving}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSaveStrategyGroupTitle()}
                    >
                        Guardar
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={strategyModalTitleSaving}
                        onClick={() => handleCancelStrategyGroupTitleEdit()}
                    >
                        Cancelar
                    </button>
                </span>
            );
        }

        return (
            <span className="d-inline-flex flex-wrap align-items-center gap-2" style={{ minWidth: 0, maxWidth: '100%' }}>
                <span className="text-break" style={{ fontWeight: 600 }}>
                    {expandedGroupTitle}
                </span>
                <span className="text-muted fw-normal">{`(${done}/${total})`}</span>
                {canRename ? (
                    <button
                        type="button"
                        className="btn btn-link p-0 d-inline-flex align-items-center"
                        title="Editar nombre de la estrategia"
                        aria-label="Editar nombre de la estrategia"
                        onClick={() => {
                            setStrategyModalTitleDraft(expandedStrategyKey);
                            setStrategyModalTitleEditing(true);
                        }}
                    >
                        <Pencil size={18} className="text-secondary" />
                    </button>
                ) : null}
            </span>
        );
    }, [
        expandedStrategyKey,
        expandedModalTaskCounts,
        groupedTasksForDisplay,
        newTaskStrategyContext,
        strategyCanvasMeta,
        strategyModalTitleEditing,
        strategyModalTitleDraft,
        strategyModalTitleSaving,
        handleSaveStrategyGroupTitle,
        handleCancelStrategyGroupTitleEdit,
    ]);

    const teamPanelUserOptions = useMemo(() => {
        const map = new Map();
        for (const t of teamDetailTasks) {
            if (t.usr_id == null) continue;
            const uid = Number(t.usr_id);
            if (map.has(uid)) continue;
            const fromAssignee = assigneeOptions.find((o) => Number(o.usr_id) === uid);
            const label = fromAssignee?.displayName || t.email_id || `Usuario ${uid}`;
            map.set(uid, { usr_id: uid, label });
        }
        const selfNum = usr_id != null ? Number(usr_id) : null;
        return [...map.values()].sort((a, b) => {
            if (selfNum != null && !Number.isNaN(selfNum)) {
                if (a.usr_id === selfNum) return -1;
                if (b.usr_id === selfNum) return 1;
            }
            return a.label.localeCompare(b.label, 'es', { sensitivity: 'base' });
        });
    }, [teamDetailTasks, assigneeOptions, usr_id]);

    const filteredTeamPanelUserOptions = useMemo(() => {
        const q = teamPanelUserFilterSearch.trim().toLowerCase();
        if (!q) return teamPanelUserOptions;
        return teamPanelUserOptions.filter((u) => String(u.label).toLowerCase().includes(q));
    }, [teamPanelUserOptions, teamPanelUserFilterSearch]);

    const teamPanelGroupedWeeks = useMemo(
        () => buildTeamPanelGroupedWeeksFromRows(teamFilteredTasks, { groupBy: teamPanelGroupBy }),
        [teamFilteredTasks, teamPanelGroupBy]
    );

    const renderTeamPanelTaskRow = (row, reassignStrategyGroupKeyOverride = null) => {
        const panelTask = teamPanelRowToTask(row);
        const ownerUsrId = usr_id != null ? Number(usr_id) : null;
        const rowUsrId = row.usr_id != null && row.usr_id !== '' ? Number(row.usr_id) : null;
        const isOwnTask =
            !Number.isFinite(ownerUsrId) ||
            ownerUsrId <= 0 ||
            rowUsrId == null ||
            Number.isNaN(rowUsrId) ||
            rowUsrId === ownerUsrId;
        return (
            <TeamPanelTaskRow
                key={row.id}
                row={row}
                panelTask={panelTask}
                isOwnTask={isOwnTask}
                cardsLength={cards.length}
                maxChars={MAX_CHARS}
                nowMs={nowMs}
                truncateCategoryLabel={truncateCategoryLabel}
                onToggleTask={handleToggleTask}
                onSaveName={handleTextTask}
                onOpenStatistics={(taskId) => openTaskStatisticsPopup(taskId, row.usr_id)}
                onOpenTaskModal={handleOpenModal}
                onOpenCategoryModal={openTaskCategoryModal}
                onOpenDueDateModal={openDueDateModal}
                onDeleteTask={handleDeleteTask}
                onTogglePriority={handleTogglePriority}
                onReassign={(r) => openTeamPanelReassign(r, reassignStrategyGroupKeyOverride)}
            />
        );
    };

    const strategyTeamPanelMarkup = (
        <div
            className="strategy-team-panel"
            onClick={(e) => e.stopPropagation()}
            role="region"
            aria-label="Tareas del equipo en esta estrategia"
        >
            {teamDetailLoading ? (
                <p className="small text-white text-center mb-0 py-2">Cargando…</p>
            ) : teamDetailTasks.length === 0 ? (
                <p className="small text-center mb-0 py-2" style={{ color: 'rgba(255,255,255,0.75)' }}>
                    No hay tareas en esta estrategia.
                </p>
            ) : (
                <>
                    <div
                        className="strategy-team-panel-filters mb-2"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        role="search"
                    >
                        <div className="strategy-team-panel-filters-search-row">
                            <div className="input-group input-group-sm strategy-team-panel-search-field">
                                <span className="input-group-text bg-white border-end-0">
                                    <Search size={14} className="text-muted" aria-hidden />
                                </span>
                                <input
                                    type="search"
                                    className="form-control border-start-0"
                                    placeholder="Buscar por nombre de tarea…"
                                    value={teamPanelSearchText}
                                    onChange={(e) => setTeamPanelSearchText(e.target.value)}
                                    aria-label="Buscar tareas en el panel del equipo"
                                />
                            </div>
                            {teamPanelUserOptions.length > 0 ? (
                                <Dropdown autoClose="outside" drop="down">
                                    <Dropdown.Toggle
                                        variant="light"
                                        size="sm"
                                        id="team-panel-users-filter"
                                        className="d-inline-flex align-items-center gap-1 text-dark border"
                                    >
                                        <Users size={14} aria-hidden />
                                        <span>Usuarios</span>
                                        {teamPanelFilterUserIds.length > 0 ? (
                                            <span className="badge bg-primary rounded-pill">{teamPanelFilterUserIds.length}</span>
                                        ) : null}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="strategy-team-panel-user-dropdown shadow py-1">
                                        <div className="dropdown-header small py-2 px-3 text-muted mb-0 border-bottom border-light">
                                            Mostrar solo tareas de…
                                        </div>
                                        <div
                                            className="strategy-team-panel-user-search"
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => e.stopPropagation()}
                                        >
                                            <div className="input-group input-group-sm">
                                                <span className="input-group-text bg-white border-end-0 py-1">
                                                    <Search size={14} className="text-muted" aria-hidden />
                                                </span>
                                                <input
                                                    type="search"
                                                    className="form-control border-start-0 py-1"
                                                    placeholder="Buscar miembro…"
                                                    value={teamPanelUserFilterSearch}
                                                    onChange={(e) => setTeamPanelUserFilterSearch(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    aria-label="Buscar miembro en el filtro de usuarios"
                                                />
                                            </div>
                                        </div>
                                        <div className="strategy-team-panel-user-list">
                                            {filteredTeamPanelUserOptions.length === 0 ? (
                                                <p className="small text-muted text-center mb-0 py-2 px-2">
                                                    No hay miembros que coincidan.
                                                </p>
                                            ) : (
                                                filteredTeamPanelUserOptions.map((u) => {
                                                    const uid = Number(u.usr_id);
                                                    const checked = teamPanelFilterUserIds.some(
                                                        (id) => Number(id) === uid
                                                    );
                                                    return (
                                                        <button
                                                            key={`team-filter-user-${uid}`}
                                                            type="button"
                                                            className="dropdown-item d-flex align-items-center gap-2 py-2 px-2 strategy-team-panel-user-item"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                toggleTeamPanelUserFilter(uid);
                                                            }}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input flex-shrink-0 mt-0"
                                                                checked={checked}
                                                                readOnly
                                                                tabIndex={-1}
                                                                style={{ pointerEvents: 'none' }}
                                                                aria-hidden
                                                            />
                                                            <div className="strategy-team-avatar-wrap flex-shrink-0">
                                                                <UserAvatar usr_id={uid} usr_name={u.label} />
                                                            </div>
                                                            <span className="small text-truncate text-start">
                                                                {u.label}
                                                            </span>
                                                        </button>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </Dropdown.Menu>
                                </Dropdown>
                            ) : null}
                            <Dropdown autoClose="outside" drop="down">
                                <Dropdown.Toggle
                                    variant="light"
                                    size="sm"
                                    id="team-panel-status-filter"
                                    className="d-inline-flex align-items-center gap-1 text-dark border"
                                >
                                    <ListFilter size={14} aria-hidden />
                                    <span>Estado</span>
                                    {teamPanelFilterStatusKeys.length > 0 ? (
                                        <span className="badge bg-primary rounded-pill">{teamPanelFilterStatusKeys.length}</span>
                                    ) : null}
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="strategy-team-panel-user-dropdown shadow py-1">
                                    <div className="dropdown-header small py-2 px-3 text-muted mb-0 border-bottom border-light">
                                        Mostrar tareas con estado…
                                    </div>
                                    {TEAM_PANEL_STATUS_FILTER_OPTIONS.map((opt) => {
                                        const checked = teamPanelFilterStatusKeys.includes(opt.key);
                                        return (
                                            <button
                                                key={`team-filter-status-${opt.key}`}
                                                type="button"
                                                className="dropdown-item d-flex align-items-center gap-2 py-2 px-2 strategy-team-panel-user-item"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    toggleTeamPanelStatusFilter(opt.key);
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input flex-shrink-0 mt-0"
                                                    checked={checked}
                                                    readOnly
                                                    tabIndex={-1}
                                                    style={{ pointerEvents: 'none' }}
                                                    aria-hidden
                                                />
                                                <span className="small text-start">{opt.label}</span>
                                            </button>
                                        );
                                    })}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                        <div className="strategy-team-panel-filters-toolbar">
                            <div className="strategy-team-panel-filter-group">
                                <label htmlFor="team-panel-group-by" className="text-nowrap">
                                    Agrupar
                                </label>
                                <Form.Select
                                    id="team-panel-group-by"
                                    size="sm"
                                    className="border text-dark"
                                    style={{ width: 'auto', minWidth: '11rem' }}
                                    value={teamPanelGroupBy}
                                    onChange={(e) => setTeamPanelGroupBy(e.target.value)}
                                    aria-label="Agrupar tareas por"
                                >
                                    <option value={TEAM_PANEL_GROUP_BY.creation}>Creación</option>
                                    <option value={TEAM_PANEL_GROUP_BY.due}>Vencimiento</option>
                                    <option value={TEAM_PANEL_GROUP_BY.completion}>Terminación</option>
                                </Form.Select>
                            </div>
                            {(teamPanelSearchText.trim() !== '' ||
                                teamPanelFilterUserIds.length > 0 ||
                                teamPanelFilterStatusKeys.length > 0 ||
                                teamPanelGroupBy !== TEAM_PANEL_GROUP_BY.creation) && (
                                <button
                                    type="button"
                                    className="btn btn-link btn-sm text-white text-decoration-underline p-0 py-1"
                                    onClick={clearTeamPanelFilters}
                                >
                                    Limpiar filtros
                                </button>
                            )}
                        </div>
                    </div>
                    {teamFilteredTasks.length === 0 ? (
                        <p className="small text-center mb-0 py-2" style={{ color: 'rgba(255,255,255,0.75)' }}>
                            Ninguna tarea coincide con los filtros.
                        </p>
                    ) : (
                        <Accordion flush defaultActiveKey="0" className="strategy-team-accordion">
                            {teamPanelGroupedWeeks.map((wk, i) => {
                                const weekTotal = wk.tasks.length;
                                const weekDone = wk.tasks.filter((r) => r.completed).length;
                                const weekAllComplete = weekTotal > 0 && weekDone === weekTotal;
                                return (
                                <Accordion.Item
                                    eventKey={String(i)}
                                    key={`${wk.weekStart}-${i}`}
                                    className={weekAllComplete ? 'strategy-team-accordion-item--week-complete' : ''}
                                >
                                    <Accordion.Header className="strategy-team-accordion-header">
                                        <span className="me-1">{wk.label}</span>
                                        <span
                                            className={`fw-normal small ms-1 ${weekAllComplete ? 'text-success' : ''}`}
                                            style={{ opacity: 0.9 }}
                                        >
                                            ({weekDone}/{weekTotal})
                                            {weekAllComplete ? ' ✅' : ''}
                                        </span>
                                    </Accordion.Header>
                                    <Accordion.Body className="p-0">
                                        {wk.tasks.map((row) => renderTeamPanelTaskRow(row))}
                                    </Accordion.Body>
                                </Accordion.Item>
                                );
                            })}
                        </Accordion>
                    )}
                </>
            )}
        </div>
    );

    return (
        <div className="container-fluid px-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            {/* Inyectamos estilos personalizados */}
            <style>{customStyles}</style>

            <div className="row justify-content-center">
                <div className="col-12">
                    {/* Área para agregar nueva tarea (TOP) */}
                    <div className="d-flex flex-column p-2 mb-2 bg-white rounded-4 shadow-sm border">
                        <div className="tactic-new-task-bar">
                            <div className="tactic-new-task-input-wrap">
                                <textarea
                                    ref={newTaskInputRef}
                                    rows={1}
                                    placeholder="ie: Crear pieza gráfica para campaña"
                                    value={newTaskText}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setNewTaskText(v);
                                        setTaskFilterText(v);
                                    }}
                                    maxLength={MAX_CHARS}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                        }
                                    }}
                                    disabled={isAddingTask}
                                    className="form-control form-control-lg border-0 tactic-new-task-textarea"
                                />
                                <button
                                    type="button"
                                    className="btn btn-light rounded-circle p-0 d-inline-flex align-items-center justify-content-center border tactic-new-task-clear-btn"
                                    aria-label="Limpiar texto del filtro"
                                    title="Limpiar texto"
                                    onClick={() => {
                                        setNewTaskText('');
                                        setTaskFilterText('');
                                        if (newTaskInputRef.current) newTaskInputRef.current.focus();
                                    }}
                                    disabled={isAddingTask || (newTaskText.length === 0 && taskFilterText.length === 0)}
                                >
                                    <X size={16} />
                                </button>
                            </div>
                            <div className="tactic-new-task-actions">
                                <span className="tactic-new-task-counter" aria-live="polite">
                                    {newTaskText.length}/{MAX_CHARS}
                                </span>
                                <button
                                    type="button"
                                    className="new-task-assignee-btn"
                                    title={newTaskAssigneeLabel ? `Asignar a: ${newTaskAssigneeLabel}` : 'Elegir asignatario'}
                                    aria-label="Elegir usuario asignado a la tarea"
                                    onClick={openNewTaskAssigneePicker}
                                    disabled={isAddingTask}
                                >
                                    <UserAvatar usr_id={newTaskAssignUsrId} usr_name={newTaskAssigneeLabel || undefined} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleAddTask()}
                                    disabled={isAddingTask || newTaskText.length === 0 || newTaskText.length > MAX_CHARS}
                                    className="btn btn-success shadow-sm flex-shrink-0 tactic-new-task-add-btn"
                                    aria-busy={isAddingTask}
                                    aria-label={isAddingTask ? 'Creando tarea…' : 'Agregar tarea'}
                                >
                                    {isAddingTask ? (
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                    ) : (
                                        <Plus size={22} strokeWidth={2.5} aria-hidden />
                                    )}
                                </button>
                            </div>
                        </div>
                        {isAddingTask ? (
                            <p className="small text-muted mb-0 ps-1 pt-1">Procesando…</p>
                        ) : null}
                        {newTaskStrategyContext && (
                            <div className="d-flex align-items-start justify-content-between gap-2 ps-1 pt-2 mt-1 border-top border-light">
                                <p className="mb-0 small text-muted" style={{ fontSize: '0.8125rem', lineHeight: 1.35 }}>
                                    {' '}
                                    <span className="text-secondary fw-semibold">{newTaskStrategyContext.displayLabel}</span>
                                </p>
                                <button
                                    type="button"
                                    className="btn btn-link btn-sm text-nowrap p-0 flex-shrink-0 align-self-start"
                                    onClick={() => setNewTaskStrategyContext(null)}
                                    aria-label="Quitar estrategia de contexto"
                                >
                                    Quitar
                                </button>
                            </div>
                        )}
                    </div>
                    {personalRunningTasks.length > 0 ? (
                        <p className="small text-muted mt-0 mb-2 pe-1" style={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                            <span className="text-secondary">En ejecución: </span>
                            {personalRunningTasks.map((t, i) => (
                                <span key={t.key}>
                                    {i > 0 ? ', ' : ''}
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        className="text-primary text-decoration-underline"
                                        style={{ cursor: 'pointer' }}
                                        title={`Filtrar por: ${t.title}`}
                                        onClick={() => {
                                            setNewTaskText(t.title);
                                            setTaskFilterText(t.title);
                                            if (newTaskInputRef.current) newTaskInputRef.current.focus();
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setNewTaskText(t.title);
                                                setTaskFilterText(t.title);
                                                if (newTaskInputRef.current) newTaskInputRef.current.focus();
                                            }
                                        }}
                                    >
                                        {t.title}
                                    </span>
                                </span>
                            ))}
                        </p>
                    ) : null}

                    {/* Categorías / etiquetas (filtros) */}
                    <div className="mt-3 mb-4">
                        <div className="d-flex flex-wrap align-items-center gap-1">
                            {displayCategories.map((cat) => {
                                const filterOn =
                                    tagFilterCategoryId != null && String(tagFilterCategoryId) === String(cat.id);
                                return (
                                    <div
                                        key={cat.id}
                                        className={`category-pill-wrap${filterOn ? ' category-pill-wrap--filter-active' : ''}`}
                                        style={{
                                            backgroundColor: cat.color || '#e5e7eb',
                                            color: '#1f2937',
                                            fontSize: '0.8rem',
                                            minHeight: 28,
                                        }}
                                    >
                                        <button
                                            type="button"
                                            className="category-pill-filter-part"
                                            onClick={() => toggleTagFilter(cat.id)}
                                            title="Mostrar solo tareas con esta etiqueta (clic de nuevo para quitar el filtro)"
                                            aria-pressed={filterOn}
                                            aria-label={
                                                filterOn
                                                    ? `Quitar filtro por ${cat.category_name}`
                                                    : `Filtrar por etiqueta ${cat.category_name}`
                                            }
                                        >
                                            <span className="fw-normal text-truncate">
                                                {truncateCategoryLabel(cat.category_name)}
                                            </span>
                                        </button>
                                        <button
                                            type="button"
                                            className="category-pill-edit-btn"
                                            onClick={() => openCategoryModal(cat)}
                                            title="Editar etiqueta"
                                            aria-label={`Editar categoría ${cat.category_name}`}
                                        >
                                            <Pencil size={12} strokeWidth={2.25} aria-hidden />
                                        </button>
                                    </div>
                                );
                            })}
                            <button
                                type="button"
                                className="btn btn-primary rounded d-flex align-items-center justify-content-center category-add-btn p-0"
                                style={{ width: 28, height: 28, minWidth: 28, minHeight: 28 }}
                                onClick={() => openCategoryModal(null)}
                                aria-label="Agregar categoría"
                            >
                                <Plus size={18} color="white" strokeWidth={2.5} />
                            </button>
                            <div
                                className="tactic-filters-toolbar-period ms-1 ps-2 border-start"
                                style={{ borderColor: 'rgba(0,0,0,0.12)' }}
                            >
                                <div
                                    className="tactic-filter-inline-field tactic-filter-due-date-field position-relative"
                                    ref={dueDateFilterRef}
                                >
                                    <InputGroup
                                        className="input-group-sm tactic-filter-due-date-input-group"
                                        onClick={() => setIsDueDatePickerOpen(true)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <Form.Control
                                            readOnly
                                            value={dueDateFilterDisplayText}
                                            aria-labelledby="tactic-filter-due-date-label"
                                            aria-label="Filtrar tareas por rango de fecha de vencimiento"
                                            style={{
                                                cursor: 'pointer',
                                                width: dueDateFilterInputWidth,
                                                maxWidth: '100%',
                                                borderTopRightRadius:
                                                    dueDateFilterMode === 'none' ? '0.25rem' : 0,
                                                borderBottomRightRadius:
                                                    dueDateFilterMode === 'none' ? '0.25rem' : 0,
                                            }}
                                        />
                                        {dueDateFilterMode !== 'none' ? (
                                            <Button
                                                variant="light"
                                                onClick={clearDueDateFilter}
                                                className="d-flex align-items-center border"
                                                aria-label="Quitar filtro de vencimiento"
                                            >
                                                <X size={14} />
                                            </Button>
                                        ) : null}
                                    </InputGroup>
                                    {isDueDatePickerOpen ? (
                                        <div className="tactic-filter-due-date-popover shadow-lg">
                                            <TacticDueDateRangePicker
                                                range={[dueDateFilterRange]}
                                                handleDateChange={handleDueDateFilterRangeChange}
                                                onSelectNoDueDate={handleDueDateFilterNoDate}
                                                setIsDatePickerOpen={setIsDueDatePickerOpen}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                                <div className="tactic-filter-inline-field tactic-filter-estado-row">
                                    <Select
                                        inputId="tactic-card-status-dropdown"
                                        className="tactic-filter-estado-select"
                                        classNamePrefix="react-select"
                                        options={CARD_STATUS_FILTER_OPTIONS}
                                        isMulti
                                        isClearable
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        placeholder="Estado..."
                                        noOptionsMessage={() => 'Sin opciones'}
                                        value={selectedCardStatusOptions}
                                        onChange={handleCardStatusFilterChange}
                                        styles={{
                                            container: (provided) => ({
                                                ...provided,
                                                width: '100%',
                                                minWidth: 0,
                                            }),
                                            control: (provided) => ({
                                                ...provided,
                                                minHeight: '31px',
                                                minWidth: 0,
                                            }),
                                            valueContainer: (provided) => ({
                                                ...provided,
                                                paddingTop: 0,
                                                paddingBottom: 0,
                                                minWidth: 0,
                                                flexWrap: 'nowrap',
                                                overflow: 'hidden',
                                            }),
                                            multiValue: (provided) => ({
                                                ...provided,
                                                alignItems: 'center',
                                                minWidth: 0,
                                                maxWidth: '100%',
                                                minHeight: '20px',
                                            }),
                                            multiValueLabel: (provided) => ({
                                                ...provided,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }),
                                            input: (provided) => ({
                                                ...provided,
                                                margin: 0,
                                                padding: 0,
                                                minWidth: '0 !important',
                                            }),
                                        }}
                                        aria-labelledby="tactic-card-status-filter-label"
                                    />
                                </div>
                                <div className="tactic-filter-inline-field tactic-filter-estado-row">
                                    <span className="visually-hidden" id="tactic-team-assignee-filter-label">
                                        Filtrar estrategias por usuarios con tareas asignadas
                                    </span>
                                    <Select
                                        inputId="tactic-team-assignee-select"
                                        className="tactic-filter-estado-select"
                                        classNamePrefix="react-select"
                                        options={teamAssigneeFilterOptions}
                                        isMulti
                                        isClearable
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        placeholder="Miembros..."
                                        noOptionsMessage={() => 'No hay miembros que coincidan.'}
                                        value={selectedTeamAssigneeFilterOptions}
                                        onChange={handleTeamAssigneeFilterChange}
                                        components={{
                                            Option: TeamAssigneeOption,
                                            MultiValueLabel: TeamAssigneeMultiValueLabel,
                                        }}
                                        styles={{
                                            container: (provided) => ({
                                                ...provided,
                                                width: '100%',
                                                minWidth: 0,
                                            }),
                                            control: (provided) => ({
                                                ...provided,
                                                minHeight: '31px',
                                                minWidth: 0,
                                            }),
                                            valueContainer: (provided) => ({
                                                ...provided,
                                                paddingTop: 0,
                                                paddingBottom: 0,
                                                minWidth: 0,
                                            }),
                                            multiValue: (provided) => ({
                                                ...provided,
                                                alignItems: 'center',
                                                minHeight: '20px',
                                            }),
                                            multiValueLabel: (provided) => ({
                                                ...provided,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start',
                                                padding: 0,
                                            }),
                                            menu: (provided) => ({
                                                ...provided,
                                                zIndex: 9999,
                                            }),
                                            menuPortal: (base) => ({
                                                ...base,
                                                zIndex: 9999,
                                            }),
                                        }}
                                        aria-labelledby="tactic-team-assignee-filter-label"
                                    />
                                </div>
                                <div className="tactic-filter-inline-field tactic-filter-pending-check">
                                    <Form.Check
                                        type="checkbox"
                                        id="tactic-only-pending-tasks"
                                        className="m-0 small user-select-none"
                                        label="Pendientes"
                                        checked={onlyPendingTasks}
                                        onChange={(e) => setOnlyPendingTasks(e.target.checked)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* LISTA DE TAREAS AGRUPADAS POR ESTRATEGIA */}
                    <>
                            {sortedGroupKeys.includes('Sin estrategia') ? (
                                <div className="strategy-sin-estrategia-standalone mt-4">
                                    <div className="strategy-sin-estrategia-standalone-title">Sin estrategia</div>
                                    <div
                                        className="strategy-mosaic"
                                        ref={listTopRef}
                                    >
                                        {renderStrategyMosaicCard('Sin estrategia', null)}
                                    </div>
                                </div>
                            ) : null}
                            <Accordion
                                alwaysOpen
                                activeKey={krAccordionActiveKeys}
                                onSelect={handleKrAccordionSelect}
                                className={`strategy-kr-accordion ${sortedGroupKeys.includes('Sin estrategia') ? 'mt-3' : 'mt-4'}`}
                            >
                                {strategyKrSections.map((section, sIdx) => {
                                    const isPrivateSection = section.sectionId === 'special:privadas';
                                    const krAccordionColorClass = isPrivateSection
                                        ? getStrategyKrColorSuffix(null, true)
                                        : getStrategyKrColorSuffix(section.krId);
                                    return (
                                    <Accordion.Item
                                        eventKey={String(sIdx)}
                                        key={section.sectionId}
                                        className={
                                            krAccordionColorClass
                                                ? `strategy-kr-accordion-item--${krAccordionColorClass}`
                                                : ''
                                        }
                                    >
                                        <Accordion.Header>
                                            <div className="strategy-kr-accordion-header-inner">
                                                <span className="strategy-kr-accordion-header-text d-inline-flex align-items-center flex-wrap gap-1">
                                                    <span>{section.title}</span>
                                                    <span
                                                        className={
                                                            section.hasRunningTask && !krAccordionColorClass
                                                                ? 'fw-semibold text-danger'
                                                                : 'strategy-kr-accordion-header-count'
                                                        }
                                                    >
                                                        ({section.cardCount})
                                                    </span>
                                                </span>
                                                {isPrivateSection ? (
                                                    <button
                                                        type="button"
                                                        className="strategy-kr-new-strategy-btn"
                                                        title="Nueva estrategia privada"
                                                        aria-label="Nueva estrategia privada"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setNewStrategyModalSection({
                                                                sectionId: 'special:privadas',
                                                                title: PRIVATE_SECTION_TITLE,
                                                                krId: null,
                                                                isPrivate: true,
                                                            });
                                                            setNewStrategyTitle('');
                                                            setNewStrategyDesc('');
                                                        }}
                                                    >
                                                        <Plus size={16} strokeWidth={2.25} />
                                                    </button>
                                                ) : section.krId != null &&
                                                  section.sectionId !== 'special:oportunidades' ? (
                                                    <button
                                                        type="button"
                                                        className="strategy-kr-new-strategy-btn"
                                                        title="Nueva estrategia en este KR"
                                                        aria-label="Nueva estrategia en este KR"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setNewStrategyModalSection(section);
                                                            setNewStrategyTitle('');
                                                            setNewStrategyDesc('');
                                                        }}
                                                    >
                                                        <Plus size={16} strokeWidth={2.25} />
                                                    </button>
                                                ) : null}
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            {isPrivateSection && section.groupKeys.length === 0 ? (
                                                <p className="small text-muted mb-0">
                                                    Aún no tienes estrategias privadas. Usa + para crear una.
                                                </p>
                                            ) : (
                                                <div
                                                    className="strategy-mosaic"
                                                    ref={
                                                        !sortedGroupKeys.includes('Sin estrategia') &&
                                                        sIdx === 0
                                                            ? listTopRef
                                                            : undefined
                                                    }
                                                >
                                                    {section.groupKeys.map((groupKey) =>
                                                        renderStrategyMosaicCard(groupKey, section.krId)
                                                    )}
                                                </div>
                                            )}
                                        </Accordion.Body>
                                    </Accordion.Item>
                                    );
                                })}
                            </Accordion>
                    </>

                    <div className="text-center mt-5 mb-4">
                        <button
                            type="button"
                            className="btn btn-link btn-sm p-0 border-0"
                            onClick={() => navigate('/task_progress')}
                            style={{ color: '#0060b9' }}
                        >
                            Ver todo el equipo
                        </button>
                    </div>
                    </div>
            </div>

            {newTaskFabPopupOpen && (
                <NewTaskFabPopup
                    maxChars={MAX_CHARS}
                    strategyContext={newTaskStrategyContext}
                    onClose={closeNewTaskFabPopup}
                    onAdd={handleAddTask}
                    isAdding={isAddingTask}
                    assigneeUsrId={newTaskAssignUsrId}
                    assigneeLabel={newTaskAssigneeLabel}
                    onOpenAssigneePicker={openNewTaskAssigneePicker}
                    hideAssigneePicker={
                        newTaskStrategyContext?.isPrivate === true ||
                        (newTaskStrategyContext?.strategyGroupKey != null &&
                            isPrivateStrategyGroup(
                                newTaskStrategyContext.strategyGroupKey,
                                groupedTasksForDisplay,
                                strategyCanvasMeta
                            ))
                    }
                />
            )}

            <Modal_proyectos
                isOpen={expandedStrategyKey != null}
                onClose={closeStrategyTeamModal}
                title={expandedStrategyModalTitle}
                headerExtra={strategyModalHeaderExtra}
                contentClassName="modal-content2--full-viewport-height"
            >
                <div className="strategy-modal-expanded-layout">
                    <div className="strategy-modal-expanded-main">
                        {expandedStrategyKey != null ? strategyTeamPanelMarkup : null}
                    </div>
                    {expandedStrategyKey != null && strategyCommentsOpen && (
                        <div className="strategy-modal-comments-panel" onClick={(e) => e.stopPropagation()}>
                            <div className="strategy-modal-comments-composer">
                                <textarea
                                    className="strategy-modal-comments-input"
                                    placeholder="Escribe un comentario…"
                                    value={strategyCommentDraft}
                                    onChange={(e) => setStrategyCommentDraft(e.target.value)}
                                    maxLength={8000}
                                    rows={2}
                                    aria-label="Nuevo comentario"
                                />
                                <button
                                    type="button"
                                    className="strategy-modal-comments-send"
                                    title="Enviar comentario"
                                    aria-label="Enviar comentario"
                                    disabled={strategyCommentDraft.trim() === ''}
                                    onClick={() => handleSubmitStrategyComment()}
                                >
                                    <Send size={18} strokeWidth={2.2} />
                                </button>
                            </div>
                            <div className="strategy-modal-comments-list">
                                {strategyCommentsLoading ? (
                                    <p className="small text-muted mb-0 py-2 px-1">Cargando comentarios…</p>
                                ) : strategyCommentsRows.length === 0 ? (
                                    <p className="small text-muted mb-0 py-2 px-1">Aún no hay comentarios.</p>
                                ) : (
                                    strategyCommentsRows.map((row) => (
                                        <div key={row.id} className="strategy-modal-comments-item">
                                            <div className="strategy-modal-comments-item-text">
                                                {linkifyHttpsInPlainText(
                                                    row.comments,
                                                    `c-${row.id}`,
                                                    'strategy-modal-card-desc-link'
                                                )}
                                            </div>
                                            <div className="strategy-modal-comments-item-aside">
                                                <UserAvatar
                                                    usr_id={row.creator_usr_id}
                                                    usr_name={strategyCommentCreatorLabel(row)}
                                                />
                                                <span className="strategy-modal-comments-date">
                                                    {formatStrategyCommentDate(row.date)}
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                    {expandedStrategyKey != null && (
                        <div className="strategy-modal-fab-footer">
                            <StrategyCardFooterBar
                                groupKey={expandedStrategyKey}
                                isPrivateFooter={isPrivateStrategyGroup(
                                    expandedStrategyKey,
                                    groupedTasksForDisplay,
                                    strategyCanvasMeta
                                )}
                                onAddClick={() => {
                                    if (expandedStrategyKey) {
                                        openNewTaskFabPopupForGroup(expandedStrategyKey);
                                    }
                                }}
                                onKrSpacerClick={
                                    expandedStrategyKey &&
                                    getOpportunityGroupId(expandedStrategyKey) == null &&
                                    !isPrivateStrategyGroup(
                                        expandedStrategyKey,
                                        groupedTasksForDisplay,
                                        strategyCanvasMeta
                                    )
                                        ? () => openKrReassignFromFooter(expandedStrategyKey)
                                        : undefined
                                }
                                onOpenCardStatusPicker={openCardStatusPickerForGroup}
                                onCommentsClick={
                                    getTrelloCardIdForStrategyGroup(
                                        expandedStrategyKey,
                                        groupedTasksForDisplay,
                                        newTaskStrategyContext,
                                        strategyCanvasMeta
                                    )
                                        ? toggleStrategyCommentsPanel
                                        : undefined
                                }
                                commentsActive={strategyCommentsOpen}
                            />
                        </div>
                    )}
                </div>
            </Modal_proyectos>

            <Modal_proyectos
                isOpen={assigneePickerOpen}
                onClose={() => {
                    setAssigneePickerOpen(false);
                    setAssigneePickerMode('new_task');
                    setReassignTaskId(null);
                    setReassignStrategyGroupKey(null);
                }}
                title={assigneePickerMode === 'reassign' ? 'Reasignar tarea' : 'Asignar tarea a'}
                overlayClassName={newTaskFabPopupOpen ? 'modal-overlay--elevated' : undefined}
            >
                <div className="modal-inner">
                    <div className="mb-3">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text bg-white border-end-0">
                                <Search size={16} className="text-muted" />
                            </span>
                            <input
                                type="search"
                                className="form-control border-start-0"
                                placeholder="Buscar por nombre o correo…"
                                value={assigneeSearch}
                                onChange={(e) => setAssigneeSearch(e.target.value)}
                                autoFocus
                            />
                        </div>
                    </div>
                    <div className="list-group list-group-flush" style={{ maxHeight: 'min(55vh, 360px)', overflowY: 'auto' }}>
                        {filteredAssigneeOptions.length === 0 ? (
                            <p className="small text-muted mb-0 py-3 text-center">No hay miembros que coincidan.</p>
                        ) : (
                            filteredAssigneeOptions.map((opt) => (
                                <button
                                    key={opt.usr_id}
                                    type="button"
                                    className={`list-group-item list-group-item-action d-flex align-items-center gap-3 py-2 px-2${
                                        opt.usr_id === assigneePickerHighlightId ? ' active' : ''
                                    }`}
                                    onClick={async () => {
                                        if (
                                            assigneePickerMode === 'reassign' &&
                                            reassignTaskId != null &&
                                            reassignStrategyGroupKey
                                        ) {
                                            const { strategy_mode, trello_card_id, opportunity_id } = getStrategyTeamTasksParams(
                                                reassignStrategyGroupKey,
                                                groupedTasksForDisplay,
                                                newTaskStrategyContext,
                                                strategyCanvasMeta
                                            );
                                            const res = await reassignTaskAssignee(
                                                token,
                                                reassignTaskId,
                                                opt.usr_id,
                                                strategy_mode,
                                                trello_card_id,
                                                opportunity_id
                                            );
                                            if (res === 'INVALID' || res == null) {
                                                navigate('/');
                                                return;
                                            }
                                            if (typeof res === 'object' && res.ok !== true) {
                                                window.alert(
                                                    res.message === 'strategy_mismatch'
                                                        ? 'La tarea no coincide con esta estrategia.'
                                                        : 'No se pudo reasignar la tarea.'
                                                );
                                                return;
                                            }
                                            setAssigneePickerOpen(false);
                                            setAssigneePickerMode('new_task');
                                            setReassignTaskId(null);
                                            setReassignStrategyGroupKey(null);
                                            setTeamDetailRefreshTick((x) => x + 1);
                                            fetch_load_task();
                                            return;
                                        }
                                        setNewTaskAssignUsrId(opt.usr_id);
                                        setAssigneePickerOpen(false);
                                    }}
                                >
                                    <UserAvatar usr_id={opt.usr_id} usr_name={opt.displayName} />
                                    <span className="text-start text-truncate flex-grow-1">{opt.displayName}</span>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            </Modal_proyectos>

            <Modal_proyectos
                isOpen={krReassignModal != null}
                onClose={() => setKrReassignModal(null)}
                title="Vincular estrategia a un KR"
            >
                <div className="modal-inner">
                    <p className="small text-muted mb-3">
                        Elige el resultado clave (KR) vinculado a esta estrategia. «Sin KR» quita el vínculo.
                    </p>
                    {krListLoading ? (
                        <p className="small text-muted mb-0">Cargando KRs…</p>
                    ) : (
                        <div className="list-group list-group-flush" style={{ maxHeight: 'min(50vh, 360px)', overflowY: 'auto' }}>
                            <button
                                type="button"
                                className="list-group-item list-group-item-action text-start"
                                onClick={() => handleKrReassignSelect('')}
                            >
                                <span className="fw-semibold">Sin KR</span>
                            </button>
                            {krListRows.map((kr) => (
                                <button
                                    key={kr.id != null ? `kr-${kr.id}` : `kr-t-${kr.trello_id}`}
                                    type="button"
                                    className="list-group-item list-group-item-action text-start"
                                    onClick={() => handleKrReassignSelect(kr.trello_id)}
                                >
                                    <span className="fw-semibold">{kr.kr_name || `KR #${kr.id}`}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </Modal_proyectos>

            <Modal_proyectos
                isOpen={newStrategyModalSection != null}
                onClose={() => {
                    if (!newStrategySaving) {
                        setNewStrategyModalSection(null);
                        setNewStrategyTitle('');
                        setNewStrategyDesc('');
                    }
                }}
                title={
                    newStrategyModalSection
                        ? `Nueva estrategia · ${newStrategyModalSection.title}`
                        : 'Nueva estrategia'
                }
            >
                <div className="modal-inner">
                    <p className="small text-muted mb-3">
                        {newStrategyModalSection?.sectionId === 'special:privadas' ||
                        newStrategyModalSection?.isPrivate === true
                            ? 'La tarjeta será privada: solo tú podrás verla y sus tareas. No se vinculará a ningún KR.'
                            : 'La tarjeta se guardará vinculada a este KR. Luego puedes crear tareas en ella desde el mosaico.'}
                    </p>
                    <div className="mb-3">
                        <label className="form-label small mb-1" htmlFor="new-strategy-title">
                            Título
                        </label>
                        <input
                            id="new-strategy-title"
                            type="text"
                            className="form-control"
                            value={newStrategyTitle}
                            onChange={(e) => setNewStrategyTitle(e.target.value)}
                            disabled={newStrategySaving}
                            maxLength={500}
                            placeholder="Nombre de la estrategia"
                            autoFocus
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label small mb-1" htmlFor="new-strategy-desc">
                            Descripción (opcional)
                        </label>
                        <textarea
                            id="new-strategy-desc"
                            className="form-control"
                            rows={4}
                            value={newStrategyDesc}
                            onChange={(e) => setNewStrategyDesc(e.target.value)}
                            disabled={newStrategySaving}
                            maxLength={8000}
                            placeholder="Detalle o contexto"
                        />
                    </div>
                    <div className="d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            disabled={newStrategySaving}
                            onClick={() => {
                                setNewStrategyModalSection(null);
                                setNewStrategyTitle('');
                                setNewStrategyDesc('');
                            }}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={newStrategySaving || newStrategyTitle.trim() === ''}
                            onClick={() => handleSaveNewStrategyFromKr()}
                        >
                            {newStrategySaving ? 'Guardando…' : 'Crear estrategia'}
                        </button>
                    </div>
                </div>
            </Modal_proyectos>

            <Modal_proyectos
                isOpen={cardStatusPicker != null}
                onClose={() => setCardStatusPicker(null)}
                title="Estado de la tarjeta"
            >
                <div className="modal-inner">
                    <p className="small text-muted mb-3">
                        Selecciona el estado en la base de datos para la tarjeta vinculada a esta estrategia.
                    </p>
                    <div className="d-grid gap-2">
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => handleCardStatusSelect(0)}>
                            Ninguno
                        </button>
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => handleCardStatusSelect(1)}>
                            Alineación
                        </button>
                        <button type="button" className="btn btn-warning btn-sm" onClick={() => handleCardStatusSelect(2)}>
                            Ejecución
                        </button>
                        <button type="button" className="btn btn-success btn-sm" onClick={() => handleCardStatusSelect(3)}>
                            Terminado
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleCardStatusSelect(4)}>
                            Archivado
                        </button>
                    </div>
                </div>
            </Modal_proyectos>

            {/* Renderizar Modal */}
            {selectedTask && (
                
                  <Modal_proyectos isOpen={isModalOpen} onClose={() => setModalOpen(false)}
                               title={
                                modal_content === 'Card_link_2task' ? 'Vincular a tarjeta u oportunidad' :
                                ''
                            } >
                           
                                {modal_content === 'Card_link_2task'  &&  <Card_link_2task
                                onSet={(payload) => handleLinkStrategy(payload)}
                                onClose={() => setModalOpen(false)}
                                initialRows={movePopupRows}
                                member_trello_id={member_trelloid}
                                selectedStrategyID={selectedTask.trello_card_id}
                                selectedOpportunityID={selectedTask.opportunity_id}
                                crmPermission={crm}
                                token={token}
                                strategyLoading={cardsLoading}
                            />}
                           
                        </Modal_proyectos>
            )}

            {/* Modal de categoría (crear / editar) */}
            <Modal_proyectos
                isOpen={categoryModalOpen}
                onClose={closeCategoryModal}
                title={categoryEditing ? 'Editar categoría' : 'Nueva categoría'}
            >
                <div className="modal-inner">
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Título</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Nombre de la categoría"
                            value={categoryFormName}
                            onChange={(e) => setCategoryFormName(e.target.value)}
                            maxLength={60}
                        />
                    </div>
                    <div className="mb-4">
                        <label className="form-label fw-semibold">Color</label>
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                            {CATEGORY_COLORS.map((hex) => (
                                <button
                                    key={hex}
                                    type="button"
                                    className="rounded-circle border p-0 category-color-dot"
                                    style={{
                                        width: 32,
                                        height: 32,
                                        backgroundColor: hex,
                                        borderWidth: categoryFormColor === hex ? 3 : 1,
                                        borderColor: categoryFormColor === hex ? '#111' : '#dee2e6'
                                    }}
                                    onClick={() => setCategoryFormColor(hex)}
                                    aria-label={`Color ${hex}`}
                                />
                            ))}
                            <input
                                type="color"
                                className="form-control form-control-color align-middle"
                                value={categoryFormColor}
                                onChange={(e) => setCategoryFormColor(e.target.value)}
                                title="Elegir color"
                                style={{ width: 36, height: 36, padding: 2 }}
                            />
                        </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2 justify-content-between">
                        <div>
                            {categoryEditing && !categoryEditing.isSystem && (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    onClick={handleDeleteCategory}
                                >
                                    Eliminar
                                </button>
                            )}
                        </div>
                        <div className="d-flex gap-2">
                            <button type="button" className="btn btn-secondary" onClick={closeCategoryModal}>
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSaveCategory}
                                disabled={!categoryFormName.trim()}
                            >
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            </Modal_proyectos>

            {/* Modal para elegir categoría de la tarea */}
            <Modal_proyectos
                isOpen={!!taskForCategoryModal}
                onClose={closeTaskCategoryModal}
                title="Seleccionar categoría"
            >
                <div className="modal-inner">
                    {taskForCategoryModal && (
                        <>
                            <p className="text-muted small mb-3">
                                Tarea: <strong>{taskForCategoryModal.text}</strong>
                            </p>
                            <div className="d-flex flex-column gap-2">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary text-start"
                                    onClick={() => handleSelectTaskCategory(taskForCategoryModal, null)}
                                >
                                    Sin categoría
                                </button>
                                {(categories || []).map((cat) => (
                                    <button
                                        key={cat.id}
                                        type="button"
                                        className="btn btn-outline-primary text-start d-flex align-items-center gap-2"
                                        style={{ borderLeftColor: cat.color || '#6b7280', borderLeftWidth: 4 }}
                                        onClick={() => handleSelectTaskCategory(taskForCategoryModal, cat)}
                                    >
                                        <span
                                            className="rounded-circle flex-shrink-0"
                                            style={{ width: 12, height: 12, backgroundColor: cat.color || '#6b7280' }}
                                            aria-hidden
                                        />
                                        {cat.category_name}
                                    </button>
                                ))}
                            </div>
                            {(!categories || categories.length === 0) && (
                                <p className="text-muted small mt-2">No hay categorías creadas. Crea una desde el menú de categorías.</p>
                            )}
                        </>
                    )}
                </div>
            </Modal_proyectos>

            {/* Modal fecha de vencimiento (calendario personalizado) */}
            <Modal_proyectos
                isOpen={!!taskForDueDateModal}
                onClose={closeDueDateModal}
                title="Fecha de vencimiento"
            >
                <div className="modal-inner" style={{ minWidth: 280 }}>
                    {taskForDueDateModal && (
                        <>
                            <p className="text-muted small mb-2">
                                Tarea: <strong>{taskForDueDateModal.text}</strong>
                            </p>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary p-1"
                                    onClick={() => setDueDateCalendarView(prev => {
                                        const d = new Date(prev.year, prev.month - 1, 1);
                                        return { month: d.getMonth(), year: d.getFullYear() };
                                    })}
                                    aria-label="Mes anterior"
                                >
                                    <ChevronLeft size={20} />
                                </button>
                                <span className="fw-semibold">
                                    {MONTH_NAMES[typeof dueDateCalendarView.month === 'number' && !isNaN(dueDateCalendarView.month) ? dueDateCalendarView.month : new Date().getMonth()]} {typeof dueDateCalendarView.year === 'number' && !isNaN(dueDateCalendarView.year) ? dueDateCalendarView.year : new Date().getFullYear()}
                                </span>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary p-1"
                                    onClick={() => setDueDateCalendarView(prev => {
                                        const d = new Date(prev.year, prev.month + 1, 1);
                                        return { month: d.getMonth(), year: d.getFullYear() };
                                    })}
                                    aria-label="Mes siguiente"
                                >
                                    <ChevronRight size={20} />
                                </button>
                            </div>
                            <div className="d-grid mb-3" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, fontSize: '0.875rem' }}>
                                {DAY_HEADERS.map(h => (
                                    <div key={h} className="text-center text-muted fw-semibold py-1">{h}</div>
                                ))}
                                {dueDateCalendarGrid.map((cell, idx) => {
                                    if (cell.type === 'empty') {
                                        return <div key={idx} className="py-1" />;
                                    }
                                    const isSelected = dueDateCalendarSelected === cell.date;
                                    const isToday = cell.date === dueDateCalendarTodayStr;
                                    return (
                                        <button
                                            key={idx}
                                            type="button"
                                            className="btn btn-sm py-1 border"
                                            title={isToday ? 'Hoy' : undefined}
                                            aria-current={isToday ? 'date' : undefined}
                                            style={{
                                                backgroundColor: isSelected
                                                    ? 'var(--bs-primary)'
                                                    : isToday
                                                        ? 'rgba(13, 110, 253, 0.12)'
                                                        : 'transparent',
                                                color: isSelected ? '#fff' : isToday ? 'var(--bs-primary)' : 'inherit',
                                                borderColor: isToday ? 'var(--bs-primary)' : undefined,
                                                fontWeight: isToday || isSelected ? 600 : undefined,
                                            }}
                                            onClick={() => setDueDateCalendarSelected(cell.date)}
                                        >
                                            {cell.day}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="d-flex flex-wrap gap-2 justify-content-between">
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setDueDateCalendarSelected(null)}>
                                    Limpiar
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => {
                                        const t = new Date();
                                        setDueDateCalendarSelected(`${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`);
                                    }}
                                >
                                    Hoy
                                </button>
                                <button type="button" className="btn btn-primary btn-sm" onClick={handleApplyDueDate}>
                                    Aplicar
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </Modal_proyectos>

            <Modal
                show={Boolean(selectedTaskForStats)}
                onHide={closeTaskStatisticsPopup}
                size="xl"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Detalle de tarea</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                    {selectedTaskForStats ? (
                        <Statistics
                            usr_id={selectedTaskForStats.usr_id ?? usr_id}
                            token={token}
                            cards={cards}
                            taskId={selectedTaskForStats.id}
                            isEmbedded={true}
                            onCloseEmbedded={closeTaskStatisticsPopup}
                            onTaskStateChange={(detail) => {
                                mosaicSyncPendingRef.current = true;
                                fetch_load_task();
                                if (
                                    detail &&
                                    typeof detail === 'object' &&
                                    typeof onMyRunningTaskPatch === 'function'
                                ) {
                                    const ownerId = Number(selectedTaskForStats.usr_id ?? usr_id);
                                    const selfId = Number(usr_id);
                                    if (Number.isFinite(ownerId) && Number.isFinite(selfId) && ownerId === selfId) {
                                        onMyRunningTaskPatch(detail);
                                    }
                                }
                            }}
                        />
                    ) : null}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default TacticFollowup ;
